import {
  type CSSProperties,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DEV_SERVER } from './dev-dashboard/constants';
import { type RepoTarget, gitAction, repoUrl } from './dev-dashboard/hooks/useDevServer';
import FeatureFlagsSection from './dev-dashboard/sections/FeatureFlagsSection';
import { MonitoringSection } from './dev-dashboard/sections/MonitoringSection';

// ─── Keyframes (injected once) ───────────────────────────────────────────────

if (typeof document !== 'undefined' && !document.getElementById('deploy-keyframes')) {
  const style = document.createElement('style');
  style.id = 'deploy-keyframes';
  style.textContent = '@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }';
  document.head.appendChild(style);
}

// ─── Shared Styles ───────────────────────────────────────────────────────────

const card: CSSProperties = {
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border-opaque)',
  borderRadius: 12,
  padding: 20,
};

const sectionTitle: CSSProperties = {
  fontSize: 'var(--font-size-title3)',
  lineHeight: 'var(--line-height-title3)',
  letterSpacing: 'var(--letter-spacing-title3)',
  fontWeight: 600,
  color: 'var(--color-label-primary)',
  marginBottom: 16,
};

const labelSecondary: CSSProperties = {
  color: 'var(--color-label-secondary)',
  fontSize: 'var(--font-size-body)',
  lineHeight: 'var(--line-height-body)',
};

const badge = (bg: string, fg: string): CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 'var(--radius-full)',
  fontSize: 'var(--font-size-caption)',
  lineHeight: 'var(--line-height-caption)',
  fontWeight: 600,
  background: bg,
  color: fg,
});

const metricCard: CSSProperties = {
  ...card,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  padding: '16px 12px',
};

const metricValue: CSSProperties = {
  fontSize: 'var(--font-size-title2)',
  fontWeight: 700,
  color: 'var(--color-label-primary)',
};

const metricLabel: CSSProperties = {
  fontSize: 'var(--font-size-caption)',
  color: 'var(--color-label-tertiary)',
  textAlign: 'center',
};

// ─── Data ────────────────────────────────────────────────────────────────────

const QUICK_LINKS = [
  {
    emoji: '🔬',
    title: 'GraphiQL',
    url: 'https://zoobook.fly.dev/graphiql',
    desc: 'PostGraphile v5 GraphQL Explorer',
  },
  {
    emoji: '📚',
    title: 'Storybook',
    url: 'http://localhost:6006',
    desc: 'Design System & Token Documentation',
  },
  {
    emoji: '🐙',
    title: 'GitHub',
    url: 'https://github.com/artusculik/zoobook',
    desc: 'Source Code Repository (private)',
  },
  {
    emoji: '🗄️',
    title: 'Supabase Zoo Dev',
    url: 'https://supabase.com/dashboard/project/hoquwjhxocpejqppszwy',
    desc: 'Zoo Database (45+ tables)',
  },
  {
    emoji: '🌐',
    title: 'Supabase Platform',
    url: 'https://supabase.com/dashboard/project/ysfzeedmtlymvcyesyln',
    desc: 'Platform Database (12 tables)',
  },
  {
    emoji: '🐛',
    title: 'Sentry Web',
    url: 'https://zoobook-am.sentry.io/projects/zoobook-web/',
    desc: 'Error Monitoring (React)',
  },
  {
    emoji: '🔧',
    title: 'Sentry PostGraphile',
    url: 'https://zoobook-am.sentry.io/projects/zoobook-postgraphile/',
    desc: 'Error Monitoring (Node)',
  },
  {
    emoji: '📱',
    title: 'Sentry Mobile',
    url: 'https://zoobook-am.sentry.io/projects/zoobook-mobile/',
    desc: 'Error Monitoring (React Native)',
  },
  {
    emoji: '🚀',
    title: 'Fly.io',
    url: 'https://fly.io/apps/zoobook',
    desc: 'PostGraphile Deployment',
  },
  {
    emoji: '🎨',
    title: 'Figma',
    url: 'https://www.figma.com/file/ehj3PzAPvh57loPmPpWZPk',
    desc: 'Design Tokens Source',
  },
  {
    emoji: '📖',
    title: 'Docs',
    url: 'https://github.com/artusculik/zoobook/tree/main/docs',
    desc: 'Architecture, Setup, Database, Deploy',
  },
];

// Infrastructure — deployed services (production + staging)
// healthKey maps to the key from /api/health-check for live status
const INFRA_STATUS: {
  service: string;
  url?: string;
  details: string;
  healthKey?: string;
  env?: 'production' | 'staging' | 'shared';
}[] = [
  // Production
  {
    service: 'PostGraphile v5',
    url: 'https://zoobook.fly.dev/graphiql',
    details: 'Fly.io Frankfurt, 512 MB, rate limit 100/min/user',
    healthKey: 'postgraphile',
    env: 'production',
  },
  {
    service: 'Zoo DB',
    url: 'https://supabase.com/dashboard/project/hoquwjhxocpejqppszwy',
    details: '51 tables, 6 schemas, RLS on all',
    healthKey: 'supabase_zoo',
    env: 'production',
  },
  {
    service: 'Web App',
    url: 'https://app.zoobook.online',
    details: 'Cloudflare Pages, main branch',
    healthKey: 'web_prod',
    env: 'production',
  },
  // Staging
  {
    service: 'PostGraphile Staging',
    url: 'https://zoobook-staging.fly.dev/graphiql',
    details: 'Fly.io Frankfurt, 512 MB, cold start OK',
    healthKey: 'postgraphile_stg',
    env: 'staging',
  },
  {
    service: 'Staging DB',
    url: 'https://supabase.com/dashboard/project/wuanjbemryupwovbwnoo',
    details: 'Staging Zoo DB, schema synced with production',
    healthKey: 'supabase_staging',
    env: 'staging',
  },
  {
    service: 'Staging Platform DB',
    url: 'https://supabase.com/dashboard/project/nruzxbxrgxpnwxezjgrm',
    details: 'Staging Platform DB, shared data synced from production',
    healthKey: 'platform_staging',
    env: 'staging',
  },
  {
    service: 'Staging Web',
    url: 'https://develop.zoobook-web.pages.dev',
    details: 'Cloudflare Pages, develop branch',
    healthKey: 'web_staging',
    env: 'staging',
  },
  // Shared
  {
    service: 'Platform DB',
    url: 'https://supabase.com/dashboard/project/ysfzeedmtlymvcyesyln',
    details: '14 tables, shared taxons/institutions/feeds',
    healthKey: 'supabase_platform',
    env: 'shared',
  },
  {
    service: 'Edge Functions (8)',
    details:
      'resolve, invite-user, send-email, rfid-login, webauthn-register, webauthn-authenticate, sync-taxons, sync-institutions',
    healthKey: 'edge_functions',
    env: 'shared',
  },
  {
    service: 'Email (Resend)',
    details: 'noreply@zoobook.online, DKIM+SPF, 4 templates',
    healthKey: 'resend',
    env: 'shared',
  },
  {
    service: 'DNS (Cloudflare)',
    details: 'zoobook.online — app/api/platform subdomains',
    healthKey: 'dns',
    env: 'shared',
  },
  {
    service: 'Storage (6 buckets)',
    details:
      'animals-photos, animal-documents, journal-attachments, user-avatars, reports, documents',
    healthKey: 'storage',
    env: 'shared',
  },
  {
    service: 'Sentry (3 projects)',
    url: 'https://zoobook-am.sentry.io',
    details: 'zoobook-web, zoobook-postgraphile, zoobook-mobile (DE region)',
    healthKey: 'sentry',
    env: 'shared',
  },
  {
    service: 'CI/CD (4 workflows)',
    details: 'ci, deploy-postgraphile, deploy-edge-functions, build-desktop',
    healthKey: 'github',
    env: 'shared',
  },
  {
    service: 'GitHub',
    url: 'https://github.com/artusculik/zoobook',
    details: 'Private repo, conventional commits',
    healthKey: 'github',
    env: 'shared',
  },
];

const ZOO_TABLES: Record<string, string[]> = {
  core: [
    'organizations_cache',
    'org_units',
    'profiles',
    'organization_members',
    'user_preferences',
    'org_settings',
    'special_roles',
    'special_role_permissions',
    'org_level_permissions',
    'user_special_roles',
    'user_allowed_sectors',
    'substitute_assignments',
    'sectors',
    'sector_segments',
    'sector_segment_members',
    'attachments',
    'comments',
    'notifications',
    'search_index',
    'feature_flags',
    'feature_flag_overrides',
    'release_notes',
    'translations',
    'translation_configs',
    'czech_stems',
    'event_types',
    'event_type_variants',
    'event_type_links',
    'journal_entry_types',
    'request_types',
    'integrations',
    'integration_logs',
    'org_usage_metrics',
    'org_unit_capabilities',
    'tags',
  ],
  core_private: ['audit_log'],
  animals: [
    'animals',
    'animal_events',
    'animal_identifiers',
    'animal_parents',
    'animal_groups',
    'animal_links',
    'animal_reminders',
  ],
  journals: ['journal_books', 'journal_pages', 'journal_entries', 'journal_entry_revisions'],
  management: ['requests', 'request_participants', 'request_timeline', 'request_views'],
};

const PLATFORM_TABLES = [
  'platform.organizations',
  'platform.platform_users',
  'platform.platform_modules',
  'platform.org_module_licenses',
  'platform.shared_taxons',
  'platform.shared_institutions',
  'platform.shared_feeds',
  'platform.animal_transfers',
  'platform.shared_items',
  'platform.shared_item_access',
  'platform.breeding_programs',
  'platform.breeding_program_members',
  'platform.join_codes',
  'platform.user_directory',
];

const MODULE_CATEGORIES = [
  { name: 'Core', count: 3, status: 'active', color: 'var(--color-tint-green)' },
  { name: 'Essentials', count: 5, status: 'next', color: 'var(--color-tint-orange)' },
  { name: 'Cloud', count: 5, status: 'active', color: 'var(--color-tint-green)' },
  { name: 'Zoology', count: 6, status: 'planned', color: 'var(--color-tint-blue)' },
  { name: 'Management', count: 4, status: 'planned', color: 'var(--color-tint-blue)' },
  { name: 'Assets', count: 3, status: 'planned', color: 'var(--color-tint-blue)' },
  { name: 'Files', count: 5, status: 'planned', color: 'var(--color-tint-blue)' },
  { name: 'IoT', count: 4, status: 'planned', color: 'var(--color-tint-blue)' },
  { name: 'Integrations', count: 1, status: 'planned', color: 'var(--color-tint-blue)' },
];

const SPRINTS = [
  {
    name: 'F.0 Monorepo & Tooling',
    status: 'done',
    items: [
      'pnpm monorepo (shared/web/desktop/mobile)',
      'RSBuild + React Router v7 + TSS-react',
      'Biome + ESLint + Vitest + Playwright',
      'CI/CD (5 GitHub Actions workflows)',
    ],
  },
  {
    name: 'F.1 DB & Backend Foundation',
    status: 'done',
    items: [
      'Supabase Zoo + Platform projekty',
      'Multi-schema (core/animals/journals/management)',
      'RLS (current_org_id, current_user_can, scoped access)',
      'Czech fulltext (czech_stems + to_czech_tsvector)',
      '24 migrací, 51+ tables, 6 schemas',
      'Generics (attachments, comments, audit_log, notifications, search_index)',
      'AI tables, feature flags, translations, usage metrics',
    ],
  },
  {
    name: 'F.2 PostGraphile & GraphQL',
    status: 'done',
    items: [
      'PostGraphile v5 Docker (Fly.io Frankfurt)',
      'Graphile Worker (7 tasks + crontab)',
      'Rate limiting (100/min/user, 1000/min/zoo)',
      'gql.tada (typed GraphQL bez codegen)',
      'Adapter pattern (AuthAdapter, StorageAdapter)',
      '8 Edge Functions (resolve, webauthn, rfid, email, invite)',
      'JWT → pgSettings',
    ],
  },
  {
    name: 'F.3 Auth & Routing',
    status: 'done',
    items: [
      'Supabase Auth (handle_new_user + custom_access_token_hook)',
      'Auth flow (onboarding, login, magic link, WebAuthn, RFID, MFA)',
      'Biometric unlock (cached refresh token)',
      'React Router v7 auth guards',
      'urql Client + subscriptions',
      '14 authenticated routes',
      'Sentry (web + PostGraphile + mobile)',
    ],
  },
  {
    name: 'F.4 Cross-cutting Systems',
    status: 'active',
    items: [
      '\u2705 i18n (react-intl + Tolgee)',
      '\u2705 Offline (Dexie + Workbox)',
      '\u2705 Feature flags + Module system + AI system',
      '\u2705 Email (Resend, 4 templates, DKIM+SPF)',
      '\u2705 Dev Tools Server + Dashboard',
      '\u2705 Mobile scaffold (RN Expo SDK 55, 4 form factors)',
      'Realtime subscriptions (PgPubSub)',
      'Notifications UI',
      'Dark mode',
      'Search & Spotlight UI',
      'Dashboard framework (widgets, DnD)',
      'PWA offline (full)',
      'Attachments + Comments UI',
      'Reporting engine',
    ],
  },
  {
    name: 'F.DS Design System',
    status: 'active',
    items: [
      '\u2705 Tokens export (136 colors, 56 text, 5 effects)',
      '\u2705 TSS theme + Storybook',
      'globals.css custom properties',
      'UI primitiva (Button, Input, Card, Badge, Modal, ...)',
      'Layout shell (Sidebar, TopBar, MobileBottomBar)',
      'Storybook stories pro v\u0161echny komponenty',
    ],
  },
  {
    name: 'F.M Modules',
    status: 'next',
    items: [
      'Animals (D.1)',
      'Journal + STT (D.2)',
      'Welfare AWAG/BIAZA (D.3)',
      'Sections (D.7)',
      'Nutrition (D.4)',
      'Management (D.5)',
      'Projects (D.6)',
      'Assets (D.8)',
      'Analytics (D.9)',
      'Dashboards (D.10)',
      'Users + Settings (D.11-12)',
      'Reporting (D.18)',
    ],
  },
  {
    name: 'F.E Desktop (Electron)',
    status: 'active',
    items: [
      '\u2705 Electron setup + system tray + auto-updater',
      '\u2705 Deep linking (zoobook://)',
      '\u2705 Window state + app menu',
      'RFID USB HID reader',
      'Direct print',
      'App signing + distribution',
    ],
  },
  {
    name: 'F.MO Mobile (React Native Expo)',
    status: 'planned',
    items: [
      '\u2705 Expo setup (SDK 55, New Architecture)',
      '\u2705 urql + gql.tada + Zod (shared)',
      '\u2705 Auth + Sentry',
      'Adaptive layout (phone/tablet)',
      'Core features (Animals, Journal, Welfare, Tasks)',
      'Offline (expo-sqlite + SyncService)',
      'Push + WidgetKit + Siri Shortcuts',
    ],
  },
  {
    name: 'F.FUT Future',
    status: 'planned',
    items: [
      'Chat/Messaging',
      'DMS/eSSL',
      'ZIMS integrace',
      'Asociace (EAZA/BIAZA)',
      'Breeding programy (EEP/SSP)',
      'K8s migration',
    ],
  },
];

const TECH_STACK = [
  {
    label: 'Web',
    items:
      'React 19, TypeScript 5.7, RSBuild, React Router 7, TSS-react, Radix UI, urql, gql.tada, Zod',
  },
  {
    label: 'Desktop',
    items: 'Electron 33, electron-builder, electron-updater, contextIsolation + sandbox',
  },
  {
    label: 'Backend',
    items: 'PostGraphile v5, Graphile Worker, Supabase Postgres 17, Edge Functions (Deno)',
  },
  {
    label: 'Infra',
    items: 'Fly.io (Frankfurt), GitHub Actions (4 workflows), Sentry (DE), Supabase Storage',
  },
  {
    label: 'Design',
    items: 'Figma tokens.json > web.css > TSS theme, Storybook 8, Inter Variable font',
  },
  { label: 'i18n', items: 'react-intl + Tolgee Cloud, FormatICU, cs/en/sk/de/pl/hu' },
  { label: 'Offline', items: 'Dexie.js (IndexedDB), Workbox (SW), Background Sync' },
  { label: 'Future', items: 'K8s migration, self-hosted PostgreSQL' },
];

const DESKTOP_FEATURES = [
  { feature: 'Security', detail: 'contextIsolation, sandbox, CSP headers, navigation blocking' },
  {
    feature: 'Deep linking',
    detail: 'zoobook:// protocol — join, animal, journal routes (macOS/Win/Linux)',
  },
  {
    feature: 'Auto-update',
    detail: 'electron-updater via GitHub Releases, differential updates (blockmap)',
  },
  {
    feature: 'App menu',
    detail: 'Edit (undo/copy/paste), View (reload/devtools/zoom), File, Window',
  },
  { feature: 'System tray', detail: 'Minimize to tray, restore on double-click, context menu' },
  {
    feature: 'Window state',
    detail: 'Position, size, maximize state persisted, first-launch maximize',
  },
  { feature: 'NFC/RFID', detail: 'IPC placeholder for nfc-pcsc USB reader (badge auth)' },
  {
    feature: 'Preload API',
    detail: 'window.zoobook: deepLink, openPath, openExternal, updater, NFC',
  },
  { feature: 'Notarize', detail: 'Apple notarization enabled (requires CSC_LINK in CI)' },
  { feature: 'Build targets', detail: 'macOS DMG (x64+arm64), Windows NSIS, Linux AppImage+deb' },
  {
    feature: 'CI pipeline',
    detail: 'build-desktop.yml: 4 jobs (web, macOS, Windows, publish release)',
  },
];

const BUILD_METRICS = {
  webBundle: '298 KB',
  webBundleRaw: '967 KB',
  tables: 65, // 51 zoo + 14 platform (join_codes + user_directory added)
  schemas: 6,
  modules: 36,
  edgeFunctions: 8, // Zoo: resolve, invite-user, send-email, rfid-login, webauthn-register, webauthn-authenticate | Platform: sync-taxons, sync-institutions
  storageBuckets: 6,
  ciWorkflows: 4,
  designTokens: 197,
  storybookStories: 5,
  czechStems: 66,
  securityWarnings: 0,
};

// ─── Sidebar Navigation Data ─────────────────────────────────────────────────

interface SidebarCategory {
  label: string;
  items: { id: string; label: string }[];
}

const SIDEBAR_CATEGORIES: SidebarCategory[] = [
  {
    label: 'Infrastructure',
    items: [{ id: 'section-monitoring', label: 'Monitoring' }],
  },
  {
    label: 'Operations',
    items: [
      { id: 'section-deploy', label: 'Deploy' },
      { id: 'section-releases', label: 'Releases' },
      { id: 'section-staging-import', label: 'Staging Import' },
      { id: 'section-feature-flags', label: 'Feature Flags' },
      { id: 'section-servers-tools', label: 'Servers & Tools' },
      { id: 'section-simulators', label: 'Simulators' },
    ],
  },
  {
    label: 'Monitoring',
    items: [
      { id: 'section-sentry', label: 'Sentry' },
      { id: 'section-git', label: 'Git' },
      { id: 'section-deps', label: 'Dependencies' },
      { id: 'section-ops', label: 'Ops Health' },
    ],
  },
  {
    label: 'Project',
    items: [
      { id: 'section-key-metrics', label: 'Key Metrics' },
      { id: 'section-infrastructure', label: 'Infrastructure' },
      { id: 'section-database', label: 'Database' },
      { id: 'section-modules', label: 'Modules' },
      { id: 'section-desktop', label: 'Desktop' },
      { id: 'section-tech-stack', label: 'Tech Stack' },
      { id: 'section-pending', label: 'Pending Setup' },
    ],
  },
  {
    label: 'Reference',
    items: [
      { id: 'section-quick-links', label: 'Quick Links' },
      { id: 'section-graphql-test', label: 'GraphQL Test' },
      { id: 'section-sprints', label: 'Sprints' },
      { id: 'section-commands', label: 'Commands' },
    ],
  },
];

// ─── Pending Setup Data ──────────────────────────────────────────────────────

const PENDING_SETUP_ITEMS: {
  id: string;
  title: string;
  priority: 'critical' | 'high' | 'medium';
  description: string;
  why: string;
  actionItems: string[];
  links?: { label: string; url: string }[];
}[] = [
  {
    id: 'macos-signing',
    title: 'macOS Code Signing',
    priority: 'high',
    description: 'Electron Desktop macOS build requires a Developer ID Application certificate.',
    why: 'Without signing + notarization, macOS Gatekeeper blocks the app for end users. CI notarization step is already configured (requires CSC_LINK secret).',
    actionItems: [
      'Export Developer ID Application cert from Keychain as .p12',
      'Add CSC_LINK (base64 .p12) + CSC_KEY_PASSWORD to GitHub Actions secrets',
      'Test: eas build or pnpm --filter @zoobook/desktop dist:mac in CI',
    ],
    links: [{ label: 'electron-builder docs', url: 'https://www.electron.build/code-signing' }],
  },
  {
    id: 'windows-signing',
    title: 'Windows Code Signing (Azure)',
    priority: 'high',
    description:
      'Windows MSIX/NSIS installer needs a code signing certificate to avoid SmartScreen warnings.',
    why: 'Without a trusted certificate, Windows SmartScreen shows "Unknown publisher" warning on install. Azure Code Signing (formerly Trusted Signing) is the recommended cloud-based solution.',
    actionItems: [
      'Create Azure Trusted Signing account + certificate profile',
      'Add AZURE_TENANT_ID, AZURE_CLIENT_ID, AZURE_CLIENT_SECRET to GitHub Actions secrets',
      'Configure electron-builder to use Azure Trusted Signing',
    ],
    links: [
      {
        label: 'Azure Trusted Signing',
        url: 'https://azure.microsoft.com/en-us/products/trusted-signing',
      },
    ],
  },
  {
    id: 'google-play',
    title: 'Google Play Console',
    priority: 'medium',
    description:
      'Android app publishing requires a Google Play Developer account ($25 one-time fee).',
    why: 'Currently Android builds are disabled in CI (no Google Play Console access). iOS is on TestFlight/App Store via Expo EAS. Android needs a Play Console account to publish.',
    actionItems: [
      'Register Google Play Developer account ($25 one-time)',
      'Create new app in Play Console: zoobook',
      'Generate upload keystore + add to GitHub Actions secrets (ANDROID_KEYSTORE etc.)',
      'Enable Android in CI: uncomment eas build --platform android',
    ],
    links: [{ label: 'Google Play Console', url: 'https://play.google.com/console/' }],
  },
  {
    id: 'dashboard-refactor',
    title: 'Refactor DevDashboardPage into sections',
    priority: 'medium',
    description:
      'DevDashboardPage.tsx is 5000+ lines with all sections inline. Extracted section files exist in dev-dashboard/sections/ but are not imported.',
    why: 'Duplicate code — sections/DeploySection.tsx etc. exist but DevDashboardPage uses inline versions. Changes get applied to wrong file (happened with local/online toggles). Need to wire imports and delete inline copies.',
    actionItems: [
      'Import each *Section component from dev-dashboard/sections/',
      'Delete inline section functions from DevDashboardPage.tsx',
      'Sync any missing changes from inline to extracted files',
      'Verify all sections render correctly after refactor',
    ],
  },
  {
    id: 'gitlab-migration',
    title: 'GitLab CI/CD Migration',
    priority: 'medium',
    description:
      'Migrate CI/CD from GitHub Actions to GitLab CI — no OS multiplier, free self-hosted runners, billing API.',
    why: 'GitHub Free: 2,000 min/month with 10x macOS multiplier — one desktop build costs ~175 billable min (11 builds = exhausted). GitHub charges $0.002/min even for self-hosted runners on private repos (since 03/2026). GitLab: no OS multiplier, self-hosted runners free, billing API available, better K8s integration.',
    actionItems: [
      'Rewrite 7 GitHub Actions workflows to .gitlab-ci.yml',
      'Switch electron-builder publish to generic provider (Cloudflare R2)',
      'Update deploy-pipeline.js to use GitLab API instead of GitHub API',
      'Migrate secrets to GitLab CI/CD Variables',
      'Update dev-server monitoring to GitLab CI API',
      'Test all pipelines: web, desktop, mobile, postgraphile, edge functions',
    ],
    links: [
      { label: 'GitLab CI/CD docs', url: 'https://docs.gitlab.com/ee/ci/' },
      { label: 'GitLab pricing', url: 'https://about.gitlab.com/pricing/' },
    ],
  },
  {
    id: 'auto-update-signing',
    title: 'Auto-Update Signing & Activation',
    priority: 'critical',
    description:
      'Server-controlled auto-update infrastructure is ready (R2, Worker, updater, ForceUpdateModal). Needs code signing to activate.',
    why: 'Without signed builds: macOS Gatekeeper blocks app, Windows MSIX requires cert, AppInstaller needs signed manifest. Infrastructure: Cloudflare R2 bucket + Worker at updates.zoobook.online, electron-updater generic provider, two-tier check (public startup + post-login org-specific), per-zoo channels via org_settings.',
    actionItems: [
      'macOS: Get Developer ID Application cert, upload signed .zip + .blockmap to R2 stable channel',
      'macOS: Enable notarization (CSC_LINK + CSC_KEY_PASSWORD in CI)',
      'Windows: Azure Trusted Signing cert for .appx',
      'Windows: Sign AppInstaller manifest (SignTool)',
      'Upload first signed release to R2 + set metadata.json',
      'E2E test: login → updater:configure IPC → download → install → version check',
      'Add update_channel UI to Zoo Settings page (zoo_overlord only)',
    ],
    links: [
      { label: 'electron-builder code signing', url: 'https://www.electron.build/code-signing' },
      {
        label: 'Azure Trusted Signing',
        url: 'https://azure.microsoft.com/en-us/products/trusted-signing',
      },
    ],
  },
  {
    id: 'rfid-reader',
    title: 'RFID/NFC USB Reader',
    priority: 'medium',
    description:
      'Electron preload has IPC placeholder for nfc-pcsc USB reader (badge authentication).',
    why: 'The NFC IPC channel (window.zoobook.onNFC) is wired but nfc-pcsc npm package is not integrated. Needed for kiosk-mode badge login in zoo offices.',
    actionItems: [
      'Install nfc-pcsc in packages/desktop',
      'Implement NFC reader in main/nfc.ts + connect IPC channel',
      'Test with ACR122U or similar reader',
    ],
  },
];

// ─── Pending Setup Section ───────────────────────────────────────────────────

function PendingSetupSection() {
  const priorityColor = {
    critical: 'var(--color-tint-red)',
    high: 'var(--color-tint-orange)',
    medium: 'var(--color-tint-blue)',
  };
  const priorityBg = {
    critical: 'rgba(255,59,48,0.08)',
    high: 'rgba(255,149,0,0.08)',
    medium: 'rgba(0,122,255,0.08)',
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))',
        gap: 16,
      }}
    >
      {PENDING_SETUP_ITEMS.map((item) => (
        <div
          key={item.id}
          style={{
            ...card,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            borderLeft: `3px solid ${priorityColor[item.priority]}`,
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '1px 7px',
                borderRadius: 'var(--radius-full)',
                fontSize: 11,
                fontWeight: 700,
                background: priorityBg[item.priority],
                color: priorityColor[item.priority],
                textTransform: 'uppercase' as const,
                letterSpacing: '0.4px',
                flexShrink: 0,
              }}
            >
              {item.priority}
            </span>
            <span
              style={{
                fontWeight: 600,
                fontSize: 'var(--font-size-body)',
                color: 'var(--color-label-primary)',
              }}
            >
              {item.title}
            </span>
          </div>

          {/* Description */}
          <div
            style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-secondary)' }}
          >
            {item.description}
          </div>

          {/* Why */}
          <div
            style={{
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-label-tertiary)',
              fontStyle: 'italic' as const,
            }}
          >
            {item.why}
          </div>

          {/* Action items */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {item.actionItems.map((action, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: action items have no stable unique ID
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  fontSize: 'var(--font-size-caption)',
                  color: 'var(--color-label-secondary)',
                }}
              >
                <span
                  style={{ color: 'var(--color-label-quaternary)', flexShrink: 0, marginTop: 1 }}
                >
                  {i + 1}.
                </span>
                <span>{action}</span>
              </div>
            ))}
          </div>

          {/* Links */}
          {item.links && item.links.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
              {item.links.map((link) => (
                <a
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-tint-blue)',
                    textDecoration: 'none',
                  }}
                >
                  {link.label} →
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Components ──────────────────────────────────────────────────────────────

function Section({
  title,
  id,
  children,
}: {
  title: string;
  id?: string;
  children: React.ReactNode;
  defaultOpen?: boolean; // reserved for future collapsible behaviour — not yet implemented
}) {
  return (
    <section id={id} style={{ marginBottom: 32, overflow: 'hidden', minWidth: 0 }}>
      <h2 style={sectionTitle}>{title}</h2>
      {children}
    </section>
  );
}

function SidebarNav({ activeSection }: { activeSection: string }) {
  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    const container = document.getElementById('dev-dashboard-scroll');
    if (el && container) {
      container.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
    }
  };

  return (
    <nav
      style={{
        position: 'sticky',
        top: 32,
        alignSelf: 'start',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        fontSize: 'var(--font-size-caption)',
        minWidth: 0,
      }}
    >
      {SIDEBAR_CATEGORIES.map((cat) => (
        <div key={cat.label}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-label-tertiary)',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.5px',
              marginBottom: 6,
            }}
          >
            {cat.label}
          </div>
          {cat.items.map((item) => {
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleClick(item.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                  padding: '4px 10px',
                  marginLeft: 0,
                  border: 'none',
                  borderLeft: isActive
                    ? '2px solid var(--color-tint-blue)'
                    : '2px solid transparent',
                  color: isActive ? 'var(--color-tint-blue)' : 'var(--color-label-secondary)',
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap' as const,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: 'inherit',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = 'var(--color-label-primary)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = 'var(--color-label-secondary)';
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

function KeyMetrics() {
  const metrics = [
    { value: BUILD_METRICS.webBundle, label: 'Bundle (gzip)' },
    { value: `${BUILD_METRICS.tables}`, label: 'DB Tables' },
    { value: `${BUILD_METRICS.schemas}`, label: 'Schemas' },
    { value: `${BUILD_METRICS.modules}`, label: 'Modules' },
    { value: `${BUILD_METRICS.czechStems}`, label: 'Czech Stems' },
    { value: `${BUILD_METRICS.securityWarnings}`, label: 'Security Warnings' },
    { value: `${BUILD_METRICS.designTokens}`, label: 'Design Tokens' },
    { value: `${BUILD_METRICS.ciWorkflows}`, label: 'CI Workflows' },
    { value: `${BUILD_METRICS.edgeFunctions}`, label: 'Edge Functions' },
    { value: `${BUILD_METRICS.storageBuckets}`, label: 'Storage Buckets' },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: 10,
      }}
    >
      {metrics.map((m) => (
        <div key={m.label} style={metricCard}>
          <span style={metricValue}>{m.value}</span>
          <span style={metricLabel}>{m.label}</span>
        </div>
      ))}
    </div>
  );
}

function QuickLinks() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 12,
      }}
    >
      {QUICK_LINKS.map((link) => (
        <a
          key={link.title}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            ...card,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            textDecoration: 'none',
            color: 'inherit',
            transition: 'border-color 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-tint-blue)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border-opaque)';
          }}
        >
          <span style={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>{link.emoji}</span>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--color-label-primary)', marginBottom: 2 }}>
              {link.title}
            </div>
            <div style={{ ...labelSecondary, fontSize: 'var(--font-size-footnote)' }}>
              {link.desc}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

function InfraStatus() {
  const health = usePolling<{
    success?: boolean;
    checks?: Record<string, { status: string; latencyMs: number }>;
  }>(`${DEV_SERVER}/api/health-check`, 15000, {});
  const checks = health.checks ?? {};

  const statusDotColor = (s: string) =>
    s === 'online'
      ? 'var(--color-tint-green)'
      : s === 'degraded'
        ? 'var(--color-tint-orange)'
        : 'var(--color-tint-red)';

  const envGroups: { label: string; env: string; color: string }[] = [
    { label: 'Production', env: 'production', color: 'var(--color-tint-green)' },
    { label: 'Staging', env: 'staging', color: 'var(--color-tint-orange)' },
    { label: 'Shared', env: 'shared', color: 'var(--color-label-tertiary)' },
  ];

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: '35%' }} />
          <col style={{ width: '65%' }} />
        </colgroup>
        <tbody>
          {envGroups.map((group) => {
            const rows = INFRA_STATUS.filter((r) => (r.env || 'shared') === group.env);
            if (!rows.length) return null;
            return [
              <tr key={`header-${group.env}`}>
                <td
                  colSpan={2}
                  style={{
                    padding: '8px 16px',
                    background: 'var(--color-fill-quaternary)',
                    fontWeight: 600,
                    fontSize: 'var(--font-size-footnote)',
                    color: group.color,
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid var(--color-border-opaque)',
                  }}
                >
                  {group.label}
                </td>
              </tr>,
              ...rows.map((row) => {
                const check = row.healthKey ? checks[row.healthKey] : null;
                return (
                  <tr
                    key={row.service}
                    style={{ borderBottom: '1px solid var(--color-border-transparent)' }}
                  >
                    <td
                      style={{
                        padding: '10px 16px',
                        fontWeight: 500,
                        whiteSpace: 'nowrap' as const,
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: check
                              ? statusDotColor(check.status)
                              : 'var(--color-fill-quaternary)',
                            flexShrink: 0,
                          }}
                        />
                        {row.url ? (
                          <a
                            href={row.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--color-tint-blue)', textDecoration: 'none' }}
                          >
                            {row.service}
                          </a>
                        ) : (
                          row.service
                        )}
                        {check && (
                          <span
                            style={{
                              fontSize: 'var(--font-size-caption)',
                              color: 'var(--color-label-quaternary)',
                              fontWeight: 400,
                            }}
                          >
                            {check.latencyMs}ms
                          </span>
                        )}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '10px 16px',
                        ...labelSecondary,
                        fontSize: 'var(--font-size-footnote)',
                      }}
                    >
                      {row.details}
                    </td>
                  </tr>
                );
              }),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

function DatabaseOverview() {
  const totalZoo = Object.values(ZOO_TABLES).flat().length;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 16,
      }}
    >
      <div style={card}>
        <h3 style={{ fontWeight: 600, marginBottom: 12 }}>Zoo Dev ({totalZoo} tables)</h3>
        {Object.entries(ZOO_TABLES).map(([schema, tables]) => (
          <div key={schema} style={{ marginBottom: 12 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 'var(--font-size-footnote)',
                color: 'var(--color-tint-blue)',
                marginBottom: 4,
              }}
            >
              {schema}.* ({tables.length})
            </div>
            <div
              style={{
                ...labelSecondary,
                fontSize: 'var(--font-size-caption)',
                lineHeight: '18px',
                wordBreak: 'break-word' as const,
              }}
            >
              {tables.join(', ')}
            </div>
          </div>
        ))}
      </div>
      <div style={card}>
        <h3 style={{ fontWeight: 600, marginBottom: 12 }}>
          Platform ({PLATFORM_TABLES.length} tables)
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {PLATFORM_TABLES.map((t) => (
            <code
              key={t}
              style={{
                fontSize: 'var(--font-size-footnote)',
                background: 'var(--color-fill-quaternary)',
                padding: '3px 8px',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-label-primary)',
              }}
            >
              {t}
            </code>
          ))}
        </div>
        <div
          style={{
            marginTop: 16,
            padding: '10px 12px',
            background: 'var(--color-fill-quaternary)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <div style={{ fontSize: 'var(--font-size-footnote)', fontWeight: 600, marginBottom: 4 }}>
            Seed Data
          </div>
          <div
            style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-secondary)' }}
          >
            36 modules, 5 taxons, 3 institutions, 10 feeds, 1 zoo (Liberec), email_domains
            registered
          </div>
        </div>
      </div>
    </div>
  );
}

function ModuleCategories() {
  const statusLabel = (s: string) => {
    if (s === 'active')
      return { text: 'Active', bg: 'var(--color-tint-light-green)', fg: 'var(--color-tint-green)' };
    if (s === 'next')
      return {
        text: 'Next sprint',
        bg: 'var(--color-tint-light-orange)',
        fg: 'var(--color-tint-orange)',
      };
    return { text: 'Planned', bg: 'var(--color-tint-light-blue)', fg: 'var(--color-tint-blue)' };
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: 12,
      }}
    >
      {MODULE_CATEGORIES.map((cat) => {
        const sl = statusLabel(cat.status);
        return (
          <div key={cat.name} style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontWeight: 600 }}>{cat.name}</div>
            <div style={{ ...labelSecondary, fontSize: 'var(--font-size-footnote)' }}>
              {cat.count} modules
            </div>
            <span style={badge(sl.bg, sl.fg)}>{sl.text}</span>
          </div>
        );
      })}
    </div>
  );
}

function DesktopApp() {
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--color-border-opaque)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <span style={{ fontSize: 20 }}>🖥️</span>
        <div>
          <div style={{ fontWeight: 600 }}>Electron Desktop Wrapper</div>
          <div
            style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-tertiary)' }}
          >
            packages/desktop/ — Electron 33 + electron-builder
          </div>
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {DESKTOP_FEATURES.map((row) => (
            <tr
              key={row.feature}
              style={{ borderBottom: '1px solid var(--color-border-transparent)' }}
            >
              <td
                style={{
                  padding: '8px 20px',
                  fontWeight: 500,
                  fontSize: 'var(--font-size-footnote)',
                  width: 140,
                }}
              >
                {row.feature}
              </td>
              <td
                style={{
                  padding: '8px 20px',
                  ...labelSecondary,
                  fontSize: 'var(--font-size-footnote)',
                }}
              >
                {row.detail}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TechStack() {
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {TECH_STACK.map((row) => (
        <div key={row.label} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: 'var(--font-size-footnote)',
              color: 'var(--color-tint-blue)',
              minWidth: 64,
              flexShrink: 0,
            }}
          >
            {row.label}
          </span>
          <span style={{ ...labelSecondary, fontSize: 'var(--font-size-footnote)' }}>
            {row.items}
          </span>
        </div>
      ))}
    </div>
  );
}

function GraphQLTest() {
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = '{ allOrgUnits { nodes { name level } } }';

  const runQuery = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch('https://zoobook.fly.dev/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
      const json = await res.json();
      setResult(JSON.stringify(json, null, 2));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <code
          style={{
            flex: 1,
            padding: '8px 12px',
            background: 'var(--color-fill-quaternary)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-footnote)',
            color: 'var(--color-label-primary)',
            overflow: 'auto',
          }}
        >
          {query}
        </code>
        <button
          type="button"
          onClick={runQuery}
          disabled={loading}
          style={{
            padding: '8px 16px',
            background: 'var(--color-tint-blue)',
            color: 'var(--color-static-white)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-body)',
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
            minHeight: 36,
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? 'Running...' : 'Test Query'}
        </button>
      </div>
      {error && (
        <div
          style={{
            padding: '10px 12px',
            background: 'var(--color-tint-light-red)',
            color: 'var(--color-tint-red)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-footnote)',
          }}
        >
          {error}
        </div>
      )}
      {result && (
        <pre
          style={{
            padding: 12,
            background: 'var(--color-bg-tertiary)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-caption)',
            lineHeight: '18px',
            overflow: 'auto',
            maxHeight: 300,
            color: 'var(--color-label-primary)',
            margin: 0,
          }}
        >
          {result}
        </pre>
      )}
    </div>
  );
}

// ─── Clipboard helper (with textarea fallback) ──────────────────────────────

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for unfocused documents or older browsers
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      document.body.removeChild(ta);
      return false;
    }
  }
}

// ─── Dev Tools ───────────────────────────────────────────────────────────────

// RepoTarget, repoUrl, gitAction imported from ./dev-dashboard/hooks/useDevServer

interface ToolStatus {
  label: string;
  status: string;
  pid: number | null;
  startedAt: string | null;
  url?: string;
  port?: number;
}

const DEV_TOOLS_CONFIG: {
  name: string;
  label: string;
  icon: string;
  port?: number;
  url?: string;
}[] = [
  {
    name: 'web-dev',
    label: 'Web Dev Server',
    icon: 'globe',
    port: 3000,
    url: 'http://localhost:3000',
  },
  { name: 'storybook', label: 'Storybook', icon: 'book', port: 6006, url: 'http://localhost:6006' },
  {
    name: 'mobile-expo',
    label: 'Mobile (Expo)',
    icon: 'phone',
    port: 8081,
    url: 'http://localhost:8081',
  },
];

const toolIconMap: Record<string, string> = {
  globe: 'var(--color-tint-blue)',
  book: 'var(--color-tint-purple)',
  monitor: 'var(--color-tint-green)',
  phone: 'var(--color-tint-orange)',
  tablet: 'var(--color-tint-indigo)',
};

const toolIconSymbol: Record<string, string> = {
  globe: '\u{1F310}', // globe
  book: '\u{1F4DA}', // books
  monitor: '\u{1F5A5}', // desktop
  phone: '\u{1F4F1}', // mobile
  tablet: '\u{1F4BB}', // tablet/laptop
};

function DevTools() {
  const [serverOnline, setServerOnline] = useState<boolean | null>(null);
  const [tools, setTools] = useState<Record<string, ToolStatus>>({});
  const [launching, setLaunching] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [openLogs, setOpenLogs] = useState<string | null>(null);
  const [launchingDevServer, setLaunchingDevServer] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${DEV_SERVER}/api/status`);
      const data = await res.json();
      if (data.success) {
        setTools(data.tools);
        setServerOnline(true);
      }
    } catch {
      setServerOnline(false);
      setTools({});
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 3000);

    const handleVisibility = () => {
      if (document.hidden) {
        if (intervalRef.current) clearInterval(intervalRef.current);
      } else {
        fetchStatus();
        intervalRef.current = setInterval(fetchStatus, 3000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchStatus]);

  const handleLaunch = useCallback(
    async (name: string) => {
      setLaunching((prev) => new Set(prev).add(name));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      try {
        const resp = await fetch(`${DEV_SERVER}/api/launch/${name}`, { method: 'POST' });
        const data = await resp.json();
        if (!data.success) {
          setErrors((prev) => ({ ...prev, [name]: data.error || 'Launch failed' }));
        }
        setTimeout(() => fetchStatus(), 500);
      } catch {
        setErrors((prev) => ({ ...prev, [name]: 'Dev Tools Server offline' }));
      } finally {
        setLaunching((prev) => {
          const next = new Set(prev);
          next.delete(name);
          return next;
        });
      }
    },
    [fetchStatus],
  );

  const handleStop = useCallback(
    async (name: string) => {
      try {
        await fetch(`${DEV_SERVER}/api/stop/${name}`, { method: 'POST' });
        setTimeout(() => fetchStatus(), 500);
      } catch {
        // noop
      }
    },
    [fetchStatus],
  );

  const handleRestart = useCallback(
    async (name: string) => {
      try {
        await fetch(`${DEV_SERVER}/api/stop/${name}`, { method: 'POST' });
        // Wait for process to fully stop, then relaunch
        await new Promise((r) => setTimeout(r, 1500));
        await fetch(`${DEV_SERVER}/api/launch/${name}`, { method: 'POST' });
        setTimeout(() => fetchStatus(), 1000);
      } catch {
        // noop
      }
    },
    [fetchStatus],
  );

  if (serverOnline === false) {
    return (
      <div
        style={{
          ...card,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderColor: 'var(--color-tint-orange)',
          borderStyle: 'dashed',
        }}
      >
        <span style={{ fontSize: 20, opacity: 0.6 }}>{'\u{26A0}\u{FE0F}'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, color: 'var(--color-label-primary)', marginBottom: 2 }}>
            Dev Server offline
          </div>
          <div style={{ ...labelSecondary, fontSize: 'var(--font-size-footnote)' }}>
            Run:{' '}
            <code
              style={{
                padding: '2px 6px',
                background: 'var(--color-fill-quaternary)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-caption)',
                fontFamily: 'monospace',
              }}
            >
              node tools/dev-server/index.js
            </code>{' '}
            or{' '}
            <code
              style={{
                padding: '2px 6px',
                background: 'var(--color-fill-quaternary)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-caption)',
                fontFamily: 'monospace',
              }}
            >
              pnpm dev:tools
            </code>
          </div>
        </div>
        <button
          type="button"
          disabled={launchingDevServer}
          onClick={async () => {
            setLaunchingDevServer(true);
            try {
              await fetch('/api/dev-tools/launch', { method: 'POST' });
              setTimeout(() => fetchStatus(), 2000);
              setTimeout(() => fetchStatus(), 4000);
            } catch {
              /* noop */
            }
            setTimeout(() => setLaunchingDevServer(false), 5000);
          }}
          style={{
            padding: '8px 20px',
            background: 'var(--color-tint-green)',
            color: 'var(--color-static-white)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-footnote)',
            cursor: launchingDevServer ? 'wait' : 'pointer',
            opacity: launchingDevServer ? 0.6 : 1,
            minHeight: 36,
            whiteSpace: 'nowrap' as const,
            flexShrink: 0,
          }}
        >
          {launchingDevServer ? 'Starting...' : 'Launch'}
        </button>
      </div>
    );
  }

  if (serverOnline === null) {
    return (
      <div style={{ ...card, textAlign: 'center', color: 'var(--color-label-tertiary)' }}>
        Connecting to dev server...
      </div>
    );
  }

  const statusDot = (status: string): CSSProperties => ({
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor:
      status === 'running'
        ? 'var(--color-tint-green)'
        : status === 'starting'
          ? 'var(--color-tint-orange)'
          : status === 'crashed'
            ? 'var(--color-tint-red)'
            : status === 'external'
              ? 'var(--color-tint-blue)'
              : 'var(--color-fill-primary)',
  });

  const statusText = (status: string): string => {
    if (status === 'running') return 'Running';
    if (status === 'stopping') return 'Stopping...';
    if (status === 'crashed') return 'Crashed';
    if (status === 'remote') return 'Remote (Fly.io)';
    if (status === 'external') return 'External';
    return 'Stopped';
  };

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 12,
        }}
      >
        {/* Deployed apps — same card pattern as servers */}
        {[
          {
            label: 'Production',
            url: 'https://app.zoobook.online',
            sub: 'app.zoobook.online',
            dotColor: 'var(--color-tint-green)',
            btnColor: 'var(--color-tint-green)',
          },
          {
            label: 'Staging',
            url: 'https://develop.zoobook-web.pages.dev',
            sub: 'develop.zoobook-web.pages.dev',
            dotColor: 'var(--color-tint-orange)',
            btnColor: 'var(--color-tint-orange)',
          },
        ].map((app) => (
          <div
            key={app.label}
            style={{
              ...card,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              borderColor: app.dotColor,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>{'\uD83C\uDF10'}</span>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-label-primary)',
                  }}
                >
                  {app.label}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: app.dotColor,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 'var(--font-size-caption)',
                      color: 'var(--color-label-tertiary)',
                    }}
                  >
                    {app.sub}
                  </span>
                </div>
              </div>
              <a
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '4px 10px',
                  background: 'var(--color-tint-blue)',
                  color: 'var(--color-static-white)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-caption)',
                  fontWeight: 600,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap' as const,
                  lineHeight: '20px',
                }}
              >
                Open
              </a>
            </div>
          </div>
        ))}

        {/* Dev Tools Server — launch/stop via RSBuild middleware */}
        {(() => {
          const isRunning = serverOnline === true;
          const isStarting = launching.has('dev-tools-server');
          return (
            <div
              style={{
                ...card,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                borderColor: isRunning ? 'var(--color-tint-green)' : 'var(--color-border-opaque)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>{'\u2699\uFE0F'}</span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-label-primary)',
                    }}
                  >
                    Dev Tools Server
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={statusDot(isRunning ? 'running' : 'stopped')} />
                    <span
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        color: 'var(--color-label-tertiary)',
                      }}
                    >
                      {isRunning ? 'Running' : 'Offline'} :4000
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!isRunning && !isStarting && (
                  <button
                    type="button"
                    onClick={async () => {
                      setLaunching((prev) => new Set(prev).add('dev-tools-server'));
                      try {
                        await fetch('/api/dev-tools/launch', { method: 'POST' });
                        setTimeout(() => fetchStatus(), 2000);
                      } catch {
                        /* noop */
                      }
                      setTimeout(
                        () =>
                          setLaunching((prev) => {
                            const n = new Set(prev);
                            n.delete('dev-tools-server');
                            return n;
                          }),
                        3000,
                      );
                    }}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      background: 'var(--color-tint-green)',
                      color: 'var(--color-static-white)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      fontWeight: 600,
                      fontSize: 'var(--font-size-footnote)',
                      cursor: 'pointer',
                      minHeight: 32,
                    }}
                  >
                    Launch
                  </button>
                )}
                {isStarting && (
                  <button
                    type="button"
                    disabled
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      background: 'var(--color-fill-secondary)',
                      color: 'var(--color-label-tertiary)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      fontWeight: 600,
                      fontSize: 'var(--font-size-footnote)',
                      cursor: 'wait',
                      minHeight: 32,
                    }}
                  >
                    Starting...
                  </button>
                )}
                {isRunning && (
                  <>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await fetch('/api/dev-tools/stop', { method: 'POST' });
                          // Force immediate page reload so all polling hooks reset
                          setTimeout(() => window.location.reload(), 500);
                        } catch {
                          /* noop */
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '6px 12px',
                        background: 'var(--color-tint-red)',
                        color: 'var(--color-static-white)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: 600,
                        fontSize: 'var(--font-size-footnote)',
                        cursor: 'pointer',
                        minHeight: 32,
                      }}
                    >
                      Stop
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setOpenLogs(openLogs === 'dev-tools-server' ? null : 'dev-tools-server')
                      }
                      style={{
                        padding: '6px 12px',
                        background: 'var(--color-fill-secondary)',
                        color: 'var(--color-label-secondary)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: 600,
                        fontSize: 'var(--font-size-footnote)',
                        cursor: 'pointer',
                        minHeight: 32,
                      }}
                    >
                      Logs
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {DEV_TOOLS_CONFIG.map(({ name, label, icon, port: cfgPort, url: cfgUrl }) => {
          const tool = tools[name];
          const isRunning = tool?.status === 'running';
          const isExternal = tool?.status === 'external';
          const isLaunching = launching.has(name);
          const isStopping = tool?.status === 'stopping';
          const isActive = isRunning || isExternal;
          const port = cfgPort ?? tool?.port;
          const openUrl = cfgUrl;

          return (
            <div
              key={name}
              style={{
                ...card,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                borderColor: isRunning
                  ? 'var(--color-tint-green)'
                  : isExternal
                    ? 'var(--color-tint-blue)'
                    : 'var(--color-border-opaque)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>{toolIconSymbol[icon]}</span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-label-primary)',
                    }}
                  >
                    {label}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={statusDot(tool?.status ?? 'stopped')} />
                    <span
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        color: 'var(--color-label-tertiary)',
                      }}
                    >
                      {isLaunching ? 'Starting...' : statusText(tool?.status ?? 'stopped')}
                    </span>
                    {port && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-caption)',
                          color: 'var(--color-label-quaternary)',
                        }}
                      >
                        :{port}
                      </span>
                    )}
                  </div>
                </div>
                {/* Open button — clickable link to running service */}
                {isActive && openUrl && (
                  <a
                    href={openUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '4px 10px',
                      background: 'var(--color-tint-blue)',
                      color: 'var(--color-static-white)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-caption)',
                      fontWeight: 600,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap' as const,
                      lineHeight: '20px',
                    }}
                  >
                    Open
                  </a>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                {isExternal ? (
                  /* External process: Restart (kill + relaunch) + Logs */
                  <>
                    <button
                      type="button"
                      onClick={() => handleRestart(name)}
                      style={{
                        flex: 1,
                        padding: '6px 12px',
                        background: 'var(--color-tint-blue)',
                        color: 'var(--color-static-white)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: 600,
                        fontSize: 'var(--font-size-footnote)',
                        cursor: 'pointer',
                        minHeight: 32,
                      }}
                    >
                      Restart
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpenLogs(openLogs === name ? null : name)}
                      style={{
                        padding: '6px 12px',
                        background: 'var(--color-fill-secondary)',
                        color: 'var(--color-label-secondary)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: 600,
                        fontSize: 'var(--font-size-footnote)',
                        cursor: 'pointer',
                        minHeight: 32,
                      }}
                    >
                      Logs
                    </button>
                  </>
                ) : (
                  <>
                    {!isRunning && !isLaunching && (
                      <button
                        type="button"
                        onClick={() => handleLaunch(name)}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          background: toolIconMap[icon],
                          color: 'var(--color-static-white)',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          fontWeight: 600,
                          fontSize: 'var(--font-size-footnote)',
                          cursor: 'pointer',
                          minHeight: 32,
                        }}
                      >
                        Launch
                      </button>
                    )}
                    {isLaunching && (
                      <button
                        type="button"
                        disabled
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          background: 'var(--color-fill-secondary)',
                          color: 'var(--color-label-tertiary)',
                          border: 'none',
                          borderRadius: 'var(--radius-md)',
                          fontWeight: 600,
                          fontSize: 'var(--font-size-footnote)',
                          cursor: 'wait',
                          minHeight: 32,
                        }}
                      >
                        Starting...
                      </button>
                    )}
                    {isRunning && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleRestart(name)}
                          disabled={isStopping}
                          style={{
                            flex: 1,
                            padding: '6px 12px',
                            background: 'var(--color-tint-blue)',
                            color: 'var(--color-static-white)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            fontWeight: 600,
                            fontSize: 'var(--font-size-footnote)',
                            cursor: isStopping ? 'wait' : 'pointer',
                            opacity: isStopping ? 0.7 : 1,
                            minHeight: 32,
                          }}
                        >
                          {isStopping ? 'Restarting...' : 'Restart'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setOpenLogs(openLogs === name ? null : name)}
                          style={{
                            padding: '6px 12px',
                            background: 'var(--color-fill-secondary)',
                            color: 'var(--color-label-secondary)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            fontWeight: 600,
                            fontSize: 'var(--font-size-footnote)',
                            cursor: 'pointer',
                            minHeight: 32,
                          }}
                        >
                          Logs
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Error message */}
              {errors[name] && (
                <div
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-tint-red)',
                    padding: '4px 0',
                  }}
                >
                  {errors[name]}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Process Logs Panel */}
      {openLogs && <ProcessLogs toolName={openLogs} onClose={() => setOpenLogs(null)} />}
    </>
  );
}

// ─── Live Panels (poll Dev Tools Server) ──────────────────────────────────────

function usePolling<T>(url: string, intervalMs: number, fallback: T): T {
  const [data, setData] = useState<T>(fallback);
  const visibleRef = useRef(true);
  const failCountRef = useRef(0);

  useEffect(() => {
    const onVis = () => {
      visibleRef.current = document.visibilityState === 'visible';
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    let active = true;
    async function poll() {
      if (!visibleRef.current) return;
      try {
        const resp = await fetch(url, { signal: AbortSignal.timeout(5000) });
        if (resp.ok && active) {
          setData(await resp.json());
          failCountRef.current = 0;
        } else if (active) {
          failCountRef.current++;
          if (failCountRef.current >= 2) setData(fallback);
        }
      } catch {
        if (active) {
          failCountRef.current++;
          if (failCountRef.current >= 2) setData(fallback);
        }
      }
    }
    poll();
    const id = setInterval(poll, intervalMs);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [url, intervalMs, fallback]);

  return data;
}

function SimulatorLauncher() {
  const data = usePolling<{
    success?: boolean;
    simulators?: {
      ios: Array<{ name: string; udid?: string; state: string }>;
      android: Array<{ name: string; state: string }>;
    };
  }>(`${DEV_SERVER}/api/simulators/status`, 5000, {});
  const toolsData = usePolling<{ success?: boolean; tools?: Record<string, ToolStatus> }>(
    `${DEV_SERVER}/api/status`,
    5000,
    {},
  );
  const [loading, setLoading] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const sims = data.simulators;
  if (!sims)
    return (
      <div style={labelSecondary}>Start Dev Tools Server (port 4000) to manage simulators</div>
    );

  const electronStatus = toolsData.tools?.electron;
  const electronRunning =
    electronStatus?.status === 'running' || electronStatus?.status === 'external';

  const PRIMARY_NAMES = [
    'iPhone 16',
    'iPad Air 11-inch (M2)',
    'Medium_Phone_API_36.1',
    'Medium_Tablet_API_36.1',
  ];
  const EXCLUDE_PRIMARY = ['iPhone 16 Pro', 'iPhone 16 Plus'];

  const allDevices = [
    {
      name: 'Electron Desktop',
      type: 'electron',
      platform: 'Desktop',
      state: electronRunning ? 'Booted' : 'Shutdown',
    },
    ...sims.ios.map((d, i) => ({
      ...d,
      type: `ios-${d.name.replace(/\s+/g, '-').toLowerCase()}-${i}`,
      platform: 'iOS',
    })),
    ...sims.android.map((d, i) => ({
      ...d,
      type: `android-${d.name.replace(/\s+/g, '-').toLowerCase()}-${i}`,
      platform: 'Android',
    })),
  ];

  const isPrimary = (d: (typeof allDevices)[0]) => {
    if (d.type === 'electron') return true;
    if (EXCLUDE_PRIMARY.some((ex) => d.name.includes(ex))) return false;
    return PRIMARY_NAMES.some((p) => d.name === p);
  };
  const primaryDevices = allDevices.filter(isPrimary);
  const secondaryDevices = allDevices.filter((d) => !isPrimary(d));
  const visibleDevices = showAll ? allDevices : primaryDevices;

  async function handleToggle(deviceName: string, isRunning: boolean) {
    setLoading(deviceName);
    try {
      if (deviceName === 'Electron Desktop') {
        const action = isRunning ? 'stop' : 'launch';
        await fetch(`${DEV_SERVER}/api/${action}/electron`, { method: 'POST' });
      } else {
        const action = isRunning ? 'shutdown' : 'boot';
        await fetch(`${DEV_SERVER}/api/simulators/${action}/${encodeURIComponent(deviceName)}`, {
          method: 'POST',
        });
      }
    } catch {
      /* ignore */
    }
    setTimeout(() => setLoading(null), 3000);
  }

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 12,
        }}
      >
        {visibleDevices.map((device) => {
          const isRunning = device.state === 'Booted' || device.state === 'running';
          const isLoading = loading === device.name;
          return (
            <div
              key={`${device.platform}-${device.name}`}
              style={{
                ...card,
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                borderColor: isRunning ? 'var(--color-tint-green)' : 'var(--color-border-opaque)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>
                  {device.platform === 'Desktop'
                    ? '\u{1F5A5}'
                    : device.platform === 'iOS'
                      ? '\u{1F4F1}'
                      : '\u{1F916}'}
                </span>
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--font-size-footnote)',
                      color: 'var(--color-label-primary)',
                    }}
                  >
                    {device.name}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-caption)',
                      color: 'var(--color-label-tertiary)',
                    }}
                  >
                    {device.platform}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: isRunning
                      ? 'var(--color-tint-green)'
                      : 'var(--color-label-quaternary)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-label-secondary)',
                  }}
                >
                  {isRunning ? 'Running' : 'Stopped'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleToggle(device.name, isRunning)}
                disabled={isLoading}
                style={{
                  padding: '6px 12px',
                  background: isRunning ? 'var(--color-tint-red)' : 'var(--color-tint-green)',
                  color: 'var(--color-static-white)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: 'var(--font-size-footnote)',
                  cursor: isLoading ? 'wait' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                  minHeight: 32,
                }}
              >
                {isLoading ? '...' : isRunning ? 'Shutdown' : 'Boot'}
              </button>
            </div>
          );
        })}
      </div>
      {secondaryDevices.length > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          style={{
            marginTop: 12,
            padding: '8px 16px',
            background: 'none',
            border: '1px solid var(--color-border-opaque)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-label-secondary)',
            fontSize: 'var(--font-size-footnote)',
            cursor: 'pointer',
            width: '100%',
          }}
        >
          {showAll ? 'Show less' : `Show ${secondaryDevices.length} more devices`}
        </button>
      )}
    </>
  );
}

function SentryWidget() {
  const data = usePolling<{
    success?: boolean;
    projects?: Record<
      string,
      {
        issues: Array<{
          id: string;
          title: string;
          shortId: string;
          level: string;
          count: number;
          lastSeen: string;
          permalink: string;
        }>;
        total?: number;
        error?: string;
      }
    >;
    error?: string;
  }>(`${DEV_SERVER}/api/sentry/issues`, 30000, {});
  const [resolving, setResolving] = useState<Set<string>>(new Set());
  const [resolved, setResolved] = useState<Set<string>>(new Set());
  const [fixing, setFixing] = useState<Set<string>>(new Set());
  const [fixed, setFixed] = useState<Set<string>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const ISSUES_PREVIEW = 5;

  const handleFix = useCallback(async (issueId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFixing((prev) => new Set(prev).add(issueId));
    try {
      const resp = await fetch(`${DEV_SERVER}/api/sentry/detail/${issueId}`);
      const result = await resp.json();
      if (result.markdown) {
        await copyToClipboard(`@sentry-fixer\n\n${result.markdown}`);
        setFixed((prev) => new Set(prev).add(issueId));
        setTimeout(
          () =>
            setFixed((prev) => {
              const n = new Set(prev);
              n.delete(issueId);
              return n;
            }),
          3000,
        );
      }
    } catch (_) {
      /* ignore */
    }
    setFixing((prev) => {
      const n = new Set(prev);
      n.delete(issueId);
      return n;
    });
  }, []);

  const [resolveWarnings, setResolveWarnings] = useState<
    Record<string, Array<{ type: string; message: string; errors?: string }>>
  >({});

  const handleResolve = useCallback(async (issueId: string, e: React.MouseEvent, force = false) => {
    e.preventDefault();
    e.stopPropagation();
    setResolving((prev) => new Set(prev).add(issueId));

    if (force) {
      // Force resolve — skip verification
      try {
        const resp = await fetch(`${DEV_SERVER}/api/sentry/resolve/${issueId}`, { method: 'POST' });
        const result = await resp.json();
        if (result.success) {
          setResolved((prev) => new Set(prev).add(issueId));
          setResolveWarnings((prev) => {
            const n = { ...prev };
            delete n[issueId];
            return n;
          });
        }
      } catch (_) {
        /* ignore */
      }
    } else {
      // Verify first
      try {
        const resp = await fetch(`${DEV_SERVER}/api/sentry/verify-and-resolve/${issueId}`, {
          method: 'POST',
        });
        const result = await resp.json();
        if (result.resolved) {
          // All checks passed, auto-resolved
          setResolved((prev) => new Set(prev).add(issueId));
          setResolveWarnings((prev) => {
            const n = { ...prev };
            delete n[issueId];
            return n;
          });
        } else if (result.warnings) {
          // Show warnings — user decides
          setResolveWarnings((prev) => ({ ...prev, [issueId]: result.warnings }));
        }
      } catch (_) {
        /* ignore */
      }
    }

    setResolving((prev) => {
      const n = new Set(prev);
      n.delete(issueId);
      return n;
    });
  }, []);

  if (data.error) return <div style={labelSecondary}>{data.error}</div>;
  if (!data.projects)
    return (
      <div style={labelSecondary}>
        Start Dev Tools Server (port 4000) to see Sentry issues. Set SENTRY_AUTH_TOKEN env var.
      </div>
    );

  const levelBadge = (level: string) => {
    const colors: Record<string, [string, string]> = {
      error: ['var(--color-tint-red)', 'var(--color-static-white)'],
      warning: ['var(--color-tint-orange)', 'var(--color-static-white)'],
      info: ['var(--color-tint-blue)', 'var(--color-static-white)'],
    };
    const [bg, fg] = colors[level] ?? [
      'var(--color-fill-secondary)',
      'var(--color-label-secondary)',
    ];
    return badge(bg, fg);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {Object.entries(data.projects).map(([project, info]) => (
        <div key={project} style={{ ...card, padding: 14 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontWeight: 600,
                fontSize: 'var(--font-size-body)',
                color: 'var(--color-label-primary)',
              }}
            >
              {project}
            </span>
            {info.total != null && (
              <span style={badge('var(--color-fill-tertiary)', 'var(--color-label-secondary)')}>
                {info.total} unresolved
              </span>
            )}
          </div>
          {info.error && (
            <div style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-tint-red)' }}>
              {info.error}
            </div>
          )}
          {info.issues.length === 0 && !info.error && (
            <div style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-tint-green)' }}>
              No unresolved issues
            </div>
          )}
          {(() => {
            const isExpanded = expandedProjects.has(project);
            const visibleIssues = isExpanded ? info.issues : info.issues.slice(0, ISSUES_PREVIEW);
            const hiddenCount = info.issues.length - ISSUES_PREVIEW;
            return (
              <>
                {visibleIssues.map((issue) => {
                  const isResolved = resolved.has(issue.id);
                  const isResolving = resolving.has(issue.id);
                  if (isResolved) return null;
                  return (
                    <Fragment key={issue.id}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 0',
                          borderBottom: '1px solid var(--color-border-transparent)',
                        }}
                      >
                        <span style={levelBadge(issue.level)}>{issue.level}</span>
                        <a
                          href={issue.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            flex: 1,
                            fontSize: 'var(--font-size-caption)',
                            color: 'var(--color-label-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap' as const,
                            textDecoration: 'none',
                          }}
                        >
                          {issue.title}
                        </a>
                        <span
                          style={{
                            fontSize: 'var(--font-size-caption)',
                            color: 'var(--color-label-tertiary)',
                            whiteSpace: 'nowrap' as const,
                          }}
                        >
                          {issue.count}x
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleFix(issue.id, e)}
                          disabled={fixing.has(issue.id)}
                          title="Copy issue for Claude Code sentry-fixer agent — paste to auto-fix"
                          style={{
                            padding: '2px 8px',
                            background: fixed.has(issue.id)
                              ? 'var(--color-tint-light-purple)'
                              : 'var(--color-fill-secondary)',
                            color: fixed.has(issue.id)
                              ? 'var(--color-tint-purple)'
                              : 'var(--color-label-secondary)',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-caption)',
                            fontWeight: 600,
                            cursor: fixing.has(issue.id) ? 'wait' : 'pointer',
                            whiteSpace: 'nowrap' as const,
                            minHeight: 24,
                            flexShrink: 0,
                          }}
                        >
                          {fixing.has(issue.id) ? '...' : fixed.has(issue.id) ? 'Copied' : 'Copy'}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleResolve(issue.id, e)}
                          disabled={isResolving}
                          style={{
                            padding: '2px 8px',
                            background: isResolving
                              ? 'var(--color-fill-secondary)'
                              : 'var(--color-tint-green)',
                            color: isResolving
                              ? 'var(--color-label-tertiary)'
                              : 'var(--color-static-white)',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-caption)',
                            fontWeight: 600,
                            cursor: isResolving ? 'wait' : 'pointer',
                            whiteSpace: 'nowrap' as const,
                            minHeight: 24,
                            flexShrink: 0,
                          }}
                        >
                          {isResolving ? 'Verifying...' : 'Resolve'}
                        </button>
                      </div>
                      {/* Verify warnings */}
                      {resolveWarnings[issue.id] && (
                        <div
                          style={{
                            padding: '8px 12px',
                            background: 'var(--color-tint-light-orange)',
                            borderRadius: 'var(--radius-sm)',
                            marginTop: 4,
                            marginBottom: 4,
                          }}
                        >
                          {resolveWarnings[issue.id].map((w, i) => (
                            <div
                              // biome-ignore lint/suspicious/noArrayIndexKey: warnings have no stable ID
                              key={i}
                              style={{
                                fontSize: 'var(--font-size-caption)',
                                color: 'var(--color-tint-orange)',
                                marginBottom: w.errors ? 4 : 0,
                                fontWeight: 500,
                              }}
                            >
                              {w.type === 'recent'
                                ? '\u26A0 '
                                : w.type === 'build'
                                  ? '\u274C '
                                  : ''}
                              {w.message}
                              {w.errors && (
                                <pre
                                  style={{
                                    fontSize: 10,
                                    color: 'var(--color-label-tertiary)',
                                    marginTop: 4,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    maxHeight: 100,
                                    overflow: 'auto',
                                  }}
                                >
                                  {w.errors}
                                </pre>
                              )}
                            </div>
                          ))}
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button
                              type="button"
                              onClick={(e) => handleResolve(issue.id, e, true)}
                              style={{
                                padding: '2px 10px',
                                background: 'var(--color-tint-orange)',
                                color: 'var(--color-static-white)',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-caption)',
                                fontWeight: 600,
                                cursor: 'pointer',
                                minHeight: 24,
                              }}
                            >
                              Resolve Anyway
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setResolveWarnings((prev) => {
                                  const n = { ...prev };
                                  delete n[issue.id];
                                  return n;
                                })
                              }
                              style={{
                                padding: '2px 10px',
                                background: 'var(--color-fill-secondary)',
                                color: 'var(--color-label-secondary)',
                                border: 'none',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: 'var(--font-size-caption)',
                                fontWeight: 600,
                                cursor: 'pointer',
                                minHeight: 24,
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </Fragment>
                  );
                })}
                {!isExpanded && hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpandedProjects((prev) => new Set(prev).add(project))}
                    style={{
                      marginTop: 8,
                      padding: '4px 0',
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-tint-blue)',
                      fontSize: 'var(--font-size-caption)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      textAlign: 'left' as const,
                    }}
                  >
                    Show {hiddenCount} more
                  </button>
                )}
                {isExpanded && info.issues.length > ISSUES_PREVIEW && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedProjects((prev) => {
                        const n = new Set(prev);
                        n.delete(project);
                        return n;
                      })
                    }
                    style={{
                      marginTop: 8,
                      padding: '4px 0',
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-label-tertiary)',
                      fontSize: 'var(--font-size-caption)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      textAlign: 'left' as const,
                    }}
                  >
                    Show less
                  </button>
                )}
              </>
            );
          })()}
        </div>
      ))}
    </div>
  );
}

// ─── Git Panel (Dual-Mode) ────────────────────────────────────────────────

interface GitCommit {
  hash: string;
  message: string;
  time: string;
}

interface GitFile {
  index: string;
  worktree: string;
  path: string;
  renamedFrom?: string | null;
}

interface GitBranch {
  name: string;
  isCurrent: boolean;
  upstream: string | null;
  trackingStatus: string;
}

interface GitStashEntry {
  ref: string;
  message: string;
  time: string;
}

interface GitStatusData {
  success?: boolean;
  branch?: string | null;
  isClean?: boolean;
  changedFiles?: number;
  staged?: number;
  unstaged?: number;
  behind?: number;
  ahead?: number;
  commits?: GitCommit[];
  files?: GitFile[];
  error?: string;
}

// gitAction imported from ./dev-dashboard/hooks/useDevServer

const gitBtnBase: CSSProperties = {
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-caption)',
  fontWeight: 600,
  cursor: 'pointer',
  padding: '4px 12px',
  minHeight: 28,
  whiteSpace: 'nowrap' as const,
  transition: 'opacity 0.15s',
};

const gitBtnPrimary: CSSProperties = {
  ...gitBtnBase,
  background: 'var(--color-primary-default, var(--color-tint-blue))',
  color: 'var(--color-static-white)',
};

const gitBtnGhost: CSSProperties = {
  ...gitBtnBase,
  background: 'var(--color-fill-secondary)',
  color: 'var(--color-label-primary)',
};

const gitBtnDanger: CSSProperties = {
  ...gitBtnBase,
  background: 'var(--color-tint-red)',
  color: 'var(--color-static-white)',
};

function fileStatusLabel(f: GitFile): { text: string; color: string } {
  const s = f.index !== ' ' && f.index !== '?' ? f.index : f.worktree;
  switch (s) {
    case 'M':
      return { text: 'Upraveno', color: 'var(--color-tint-blue)' };
    case 'A':
      return { text: 'Novy', color: 'var(--color-tint-green)' };
    case 'D':
      return { text: 'Smazano', color: 'var(--color-tint-red)' };
    case 'R':
      return { text: 'Prejmenovano', color: 'var(--color-tint-purple, var(--color-tint-blue))' };
    case '?':
      return { text: 'Novy', color: 'var(--color-tint-green)' };
    default:
      return { text: s || '?', color: 'var(--color-label-tertiary)' };
  }
}

function fileStatusCode(f: GitFile): { code: string; color: string } {
  const s = f.index !== ' ' && f.index !== '?' ? f.index : f.worktree;
  switch (s) {
    case 'M':
      return { code: 'M', color: 'var(--color-tint-blue)' };
    case 'A':
      return { code: 'A', color: 'var(--color-tint-green)' };
    case 'D':
      return { code: 'D', color: 'var(--color-tint-red)' };
    case 'R':
      return { code: 'R', color: 'var(--color-tint-purple, var(--color-tint-blue))' };
    case '?':
      return { code: '?', color: 'var(--color-label-tertiary)' };
    default:
      return { code: s || '?', color: 'var(--color-label-tertiary)' };
  }
}

function isFileStaged(f: GitFile): boolean {
  return f.index !== ' ' && f.index !== '?';
}

function isFileUnstaged(f: GitFile): boolean {
  return f.worktree !== ' ' || f.index === '?';
}

function shortPath(p: string): string {
  const parts = p.split('/');
  return parts.length > 2 ? `.../${parts.slice(-2).join('/')}` : p;
}

// ─── Confirm Dialog ───

function GitConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  destructive = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={onCancel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--color-bg-primary, #fff)',
          borderRadius: 12,
          padding: 24,
          minWidth: 320,
          maxWidth: 420,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          border: '1px solid var(--color-border-opaque)',
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: 'var(--font-size-body)',
            marginBottom: 8,
            color: 'var(--color-label-primary)',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-footnote)',
            color: 'var(--color-label-secondary)',
            marginBottom: 20,
            lineHeight: 1.5,
          }}
        >
          {message}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} style={gitBtnGhost}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={destructive ? gitBtnDanger : gitBtnPrimary}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline Toast ───

function useToast() {
  const [msg, setMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((text: string, type: 'success' | 'error' = 'success') => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setMsg({ text, type });
    timerRef.current = setTimeout(() => setMsg(null), 4000);
  }, []);

  const ToastEl = msg ? (
    <div
      style={{
        padding: '6px 12px',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-caption)',
        fontWeight: 600,
        background:
          msg.type === 'success' ? 'var(--color-tint-light-green)' : 'var(--color-tint-light-red)',
        color: msg.type === 'success' ? 'var(--color-tint-green)' : 'var(--color-tint-red)',
        marginTop: 8,
      }}
    >
      {msg.text}
    </div>
  ) : null;

  return { show, ToastEl };
}

// ─── Simplified Mode ───

function GitPanelSimplified({
  git,
  onRefresh,
  repo,
  refreshCount = 0,
}: { git: GitStatusData; onRefresh: () => void; repo: RepoTarget; refreshCount?: number }) {
  const [commitLoading, setCommitLoading] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [pullLoading, setPullLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [editingMessage, setEditingMessage] = useState(false);
  const { show, ToastEl } = useToast();
  const aiRequestedRef = useRef(false);

  const isDirty = !git.isClean && (git.changedFiles ?? 0) > 0;
  const isAhead = (git.ahead ?? 0) > 0;
  const isBehind = (git.behind ?? 0) > 0;

  // Fetch AI commit message ONCE when first dirty — then only on manual refresh
  const fetchAiMessage = useCallback(async () => {
    setAiLoading(true);
    try {
      const resp = await fetch(repoUrl('/api/git/ai-message', repo), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(20000),
      });
      const data = await resp.json();
      if (data.success && data.message) {
        setAiMessage(data.message);
        setAiSummary(data.summary || '');
      } else {
        setAiMessage(data.fallback || 'chore: update files');
        setAiSummary('');
      }
    } catch {
      setAiMessage('chore: update files');
      setAiSummary('');
    }
    setAiLoading(false);
  }, [repo]);

  // Reset + re-fetch AI message when repo switches or manual refresh is triggered
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: re-fetch on repo/refreshCount change
  useEffect(() => {
    setAiMessage('');
    setAiSummary('');
    setEditingMessage(false);
    // Skip initial mount (refreshCount starts at 0, repo from localStorage)
    if (isDirty) {
      fetchAiMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, refreshCount]);

  // Auto-fetch AI message once when transitioning to dirty (initial load)
  useEffect(() => {
    if (isDirty && !aiRequestedRef.current) {
      aiRequestedRef.current = true;
      fetchAiMessage();
    }
    if (!isDirty) {
      aiRequestedRef.current = false;
    }
  }, [isDirty, fetchAiMessage]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: fetchAiMessage is stable ref
  const handleCommit = useCallback(async () => {
    if (!aiMessage.trim()) return;
    const result = await gitAction(
      '/api/git/commit',
      { message: aiMessage.trim(), all: true },
      setCommitLoading,
      repo,
    );
    if (result.success) {
      const hash = typeof result.hash === 'string' ? result.hash.slice(0, 7) : '';
      show(`Commit ${hash} ulozen`, 'success');
      setAiMessage('');
      setAiSummary('');
      setEditingMessage(false);
      aiRequestedRef.current = false;
      onRefresh();
    } else {
      show(String(result.error || 'Commit failed'), 'error');
    }
  }, [aiMessage, show, onRefresh]);

  const handlePush = useCallback(async () => {
    const result = await gitAction('/api/git/push', undefined, setPushLoading, repo);
    if (result.success) {
      show('Push dokoncen', 'success');
      onRefresh();
    } else show(String(result.error || 'Push failed'), 'error');
  }, [show, onRefresh, repo]);

  const handleFetch = useCallback(async () => {
    const result = await gitAction('/api/git/fetch', undefined, setFetchLoading, repo);
    if (result.success) {
      show('Fetch dokoncen', 'success');
      onRefresh();
    } else show(String(result.error || 'Fetch failed'), 'error');
  }, [show, onRefresh, repo]);

  const handlePull = useCallback(async () => {
    setPullLoading(true);
    await gitAction('/api/git/fetch', undefined, undefined, repo);
    const result = await gitAction('/api/git/pull', undefined, undefined, repo);
    setPullLoading(false);
    if (result.success) {
      show('Pull dokoncen', 'success');
      onRefresh();
    } else show(String(result.error || 'Pull failed'), 'error');
  }, [show, onRefresh, repo]);

  const [revertLoading, setRevertLoading] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);

  const handleRevert = useCallback(async () => {
    const result = await gitAction('/api/git/revert', { hard: false }, setRevertLoading, repo);
    if (result.success) {
      show('Posledni commit vracen (zmeny zachovany)', 'success');
      setShowRevertConfirm(false);
    } else {
      show(String(result.error || 'Revert failed'), 'error');
    }
  }, [show, repo]);

  // Helper: count file types
  const files = git.files ?? [];
  const modified = files.filter((f) => f.index === 'M' || f.worktree === 'M').length;
  const added = files.filter(
    (f) => f.index === '?' || f.index === 'A' || f.worktree === '?',
  ).length;
  const deleted = files.filter((f) => f.index === 'D' || f.worktree === 'D').length;

  const labelSec = {
    fontSize: 'var(--font-size-footnote)',
    color: 'var(--color-label-secondary)',
    lineHeight: 1.5,
  };
  const labelPri = {
    fontSize: 'var(--font-size-body)',
    color: 'var(--color-label-primary)',
    fontWeight: 500 as const,
    lineHeight: 1.5,
  };
  const explainBox = {
    background: 'var(--color-fill-quaternary)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 14px',
    fontSize: 'var(--font-size-footnote)',
    color: 'var(--color-label-secondary)',
    lineHeight: 1.6,
  };

  return (
    <div>
      {/* ── DIRTY: uncommitted changes ── */}
      {isDirty && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* What happened */}
          <div style={labelPri}>
            {git.changedFiles} zmenen
            {(git.changedFiles ?? 0) === 1
              ? 'y soubor'
              : (git.changedFiles ?? 0) < 5
                ? 'e soubory'
                : 'ych souboru'}
            {modified > 0 || added > 0 || deleted > 0 ? (
              <span style={{ fontWeight: 400, color: 'var(--color-label-secondary)' }}>
                {' \u2014 '}
                {[
                  modified > 0
                    ? `${modified} upraven${modified === 1 ? '' : modified < 5 ? 'e' : 'ych'}`
                    : '',
                  added > 0 ? `${added} nov${added === 1 ? 'y' : added < 5 ? 'e' : 'ych'}` : '',
                  deleted > 0
                    ? `${deleted} smazan${deleted === 1 ? 'y' : deleted < 5 ? 'e' : 'ych'}`
                    : '',
                ]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            ) : null}
          </div>

          {/* AI explanation — what to do and why */}
          <div style={explainBox}>
            {aiLoading
              ? 'Analyzuji zmeny...'
              : aiSummary
                ? aiSummary
                : 'Zmeny jsou pripravene k ulozeni. Commit ulozi snapshot aktualniho stavu do historie.'}
          </div>

          {/* Commit message */}
          <div>
            <div style={{ ...labelSec, fontSize: 'var(--font-size-caption)', marginBottom: 4 }}>
              Commit message:
            </div>
            {editingMessage ? (
              <textarea
                value={aiMessage}
                onChange={(e) => setAiMessage(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'var(--color-bg-primary, #fff)',
                  border: '1px solid var(--color-border-opaque)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-footnote)',
                  fontFamily: 'var(--font-family-mono, monospace)',
                  color: 'var(--color-label-primary)',
                  resize: 'vertical',
                  minHeight: 36,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleCommit();
                  }
                }}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: 'var(--color-fill-quaternary)',
                  borderRadius: 'var(--radius-md)',
                  padding: '8px 12px',
                }}
              >
                <span
                  style={{
                    flex: 1,
                    fontSize: 'var(--font-size-footnote)',
                    fontFamily: 'var(--font-family-mono, monospace)',
                    color: 'var(--color-label-primary)',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    minWidth: 0,
                  }}
                >
                  {aiLoading ? '...' : aiMessage || 'chore: update files'}
                </span>
                <button
                  type="button"
                  onClick={fetchAiMessage}
                  disabled={aiLoading}
                  style={{
                    ...gitBtnGhost,
                    padding: '2px 8px',
                    minHeight: 24,
                    fontSize: 'var(--font-size-caption)',
                    opacity: aiLoading ? 0.5 : 1,
                  }}
                  title="Refresh AI commit message"
                >
                  {aiLoading ? '...' : 'AI'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingMessage(true)}
                  style={{
                    ...gitBtnGhost,
                    padding: '2px 8px',
                    minHeight: 24,
                    fontSize: 'var(--font-size-caption)',
                  }}
                >
                  Edit
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              onClick={handleCommit}
              disabled={commitLoading || aiLoading || !aiMessage.trim()}
              style={{
                ...gitBtnPrimary,
                padding: '6px 20px',
                minHeight: 32,
                opacity: commitLoading || aiLoading || !aiMessage.trim() ? 0.6 : 1,
                cursor: commitLoading ? 'wait' : 'pointer',
              }}
            >
              {commitLoading ? 'Commituji...' : 'Commit All'}
            </button>
          </div>

          {/* Warning: also behind */}
          {isBehind && (
            <div
              style={{
                padding: '6px 12px',
                background: 'var(--color-tint-light-orange)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-caption)',
                color: 'var(--color-tint-orange)',
                fontWeight: 500,
              }}
            >
              Remote ma {git.behind} nov
              {(git.behind ?? 0) === 1 ? 'y' : (git.behind ?? 0) < 5 ? 'e' : 'ych'} commit
              {(git.behind ?? 0) === 1 ? '' : (git.behind ?? 0) < 5 ? 'y' : 'u'}. Nejdriv commitni,
              pak udelej Pull.
            </div>
          )}
        </div>
      )}

      {/* ── BEHIND: remote has new commits ── */}
      {!isDirty && isBehind && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={labelPri}>
            Remote je {git.behind} commit
            {(git.behind ?? 0) === 1 ? '' : (git.behind ?? 0) < 5 ? 'y' : 'u'} napred
          </div>
          <div style={explainBox}>
            Nekdo pushnul zmeny na remote. Pull stahne tyto zmeny a aktualizuje tvuj lokalni kod.
            Bez toho muzes pracovat se starou verzi.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handlePull}
              disabled={pullLoading}
              style={{
                ...gitBtnPrimary,
                padding: '6px 20px',
                minHeight: 32,
                opacity: pullLoading ? 0.6 : 1,
                cursor: pullLoading ? 'wait' : 'pointer',
              }}
            >
              {pullLoading ? 'Pulluju...' : 'Pull'}
            </button>
          </div>
        </div>
      )}

      {/* ── AHEAD: local commits not pushed ── */}
      {!isDirty && !isBehind && isAhead && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={labelPri}>
            {git.ahead} commit{(git.ahead ?? 0) === 1 ? '' : (git.ahead ?? 0) < 5 ? 'y' : 'u'} ceka
            na Push
          </div>
          <div style={explainBox}>
            Mas lokalne ulozene commity ktere jeste nejsou na remote. Push je posle na server aby je
            videl zbytek teamu.
          </div>
          {git.commits && git.commits.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {git.commits.slice(0, git.ahead).map((c) => (
                <div
                  key={c.hash}
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'baseline',
                    fontSize: 'var(--font-size-caption)',
                  }}
                >
                  <code
                    style={{
                      fontFamily: 'var(--font-family-mono, monospace)',
                      color: 'var(--color-tint-blue)',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {c.hash}
                  </code>
                  <span
                    style={{
                      color: 'var(--color-label-primary)',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' as const,
                      minWidth: 0,
                    }}
                  >
                    {c.message}
                  </span>
                  <span style={{ color: 'var(--color-label-tertiary)', flexShrink: 0 }}>
                    {c.time}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {!showRevertConfirm ? (
              <button
                type="button"
                onClick={() => setShowRevertConfirm(true)}
                style={{
                  ...gitBtnGhost,
                  padding: '6px 14px',
                  minHeight: 32,
                  fontSize: 'var(--font-size-caption)',
                  color: 'var(--color-label-tertiary)',
                }}
              >
                Undo Last Commit
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-tint-orange)',
                  }}
                >
                  Vratit posledni commit?
                </span>
                <button
                  type="button"
                  onClick={handleRevert}
                  disabled={revertLoading}
                  style={{
                    ...gitBtnGhost,
                    padding: '4px 10px',
                    minHeight: 26,
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-tint-red)',
                    borderColor: 'var(--color-tint-red)',
                  }}
                >
                  {revertLoading ? '...' : 'Ano, vratit'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRevertConfirm(false)}
                  style={{
                    ...gitBtnGhost,
                    padding: '4px 10px',
                    minHeight: 26,
                    fontSize: 'var(--font-size-caption)',
                  }}
                >
                  Zrusit
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={handlePush}
              disabled={pushLoading}
              style={{
                ...gitBtnPrimary,
                padding: '6px 20px',
                minHeight: 32,
                opacity: pushLoading ? 0.6 : 1,
                cursor: pushLoading ? 'wait' : 'pointer',
              }}
            >
              {pushLoading ? 'Pushuju...' : 'Push'}
            </button>
          </div>
        </div>
      )}

      {/* ── CLEAN: all synced ── */}
      {!isDirty && !isBehind && !isAhead && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span
            style={{
              fontSize: 'var(--font-size-body)',
              color: 'var(--color-tint-green)',
              fontWeight: 500,
            }}
          >
            Vse synchronizovano
          </span>
          <button
            type="button"
            onClick={handleFetch}
            disabled={fetchLoading}
            style={{
              ...gitBtnGhost,
              opacity: fetchLoading ? 0.6 : 1,
              cursor: fetchLoading ? 'wait' : 'pointer',
            }}
          >
            {fetchLoading ? 'Fetchuji...' : 'Fetch'}
          </button>
        </div>
      )}

      {ToastEl}
    </div>
  );
}

// ─── Advanced Mode ───

function GitPanelAdvanced({
  git,
  onRefresh,
  repo,
}: { git: GitStatusData; onRefresh: () => void; repo: RepoTarget }) {
  const [expanded, setExpanded] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedDiff, setExpandedDiff] = useState<string | null>(null);
  const [diffContent, setDiffContent] = useState<Record<string, string>>({});
  const [diffLoading, setDiffLoading] = useState<string | null>(null);
  const [commitMsg, setCommitMsg] = useState('');
  const [aiMsgLoading, setAiMsgLoading] = useState(false);
  const [showBranches, setShowBranches] = useState(false);
  const [showStash, setShowStash] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [branches, setBranches] = useState<GitBranch[]>([]);
  const [stashList, setStashList] = useState<GitStashEntry[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [stashLoading, setStashLoading] = useState(false);
  const [newBranch, setNewBranch] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    destructive: boolean;
    onConfirm: () => void;
  } | null>(null);
  const [copiedHash, setCopiedHash] = useState<string | null>(null);
  const { show, ToastEl } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isDirty = !git.isClean && (git.changedFiles ?? 0) > 0;
  const isAhead = (git.ahead ?? 0) > 0;
  const isBehind = (git.behind ?? 0) > 0;
  const files = git.files ?? [];
  const stagedFiles = files.filter(isFileStaged);
  const unstagedFiles = files.filter(isFileUnstaged);
  const branchName = git.branch || 'unknown';
  const isMainish = branchName === 'main' || branchName === 'develop' || branchName === 'master';
  const branchBadgeBg = isMainish ? 'var(--color-tint-green)' : 'var(--color-tint-blue)';

  // Fetch branches when section opens
  useEffect(() => {
    if (!showBranches) return;
    setBranchesLoading(true);
    const controller = new AbortController();
    fetch(repoUrl('/api/git/branches', repo), { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.branches) setBranches(data.branches);
      })
      .catch(() => {})
      .finally(() => setBranchesLoading(false));
    return () => controller.abort();
  }, [showBranches, repo]);

  // Fetch stash when section opens
  useEffect(() => {
    if (!showStash) return;
    setStashLoading(true);
    const controller = new AbortController();
    fetch(repoUrl('/api/git/stash', repo), { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.stashes) setStashList(data.stashes);
      })
      .catch(() => {})
      .finally(() => setStashLoading(false));
    return () => controller.abort();
  }, [showStash, repo]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: callback uses refs
  const fetchDiff = useCallback(
    async (filePath: string) => {
      if (expandedDiff === filePath) {
        setExpandedDiff(null);
        return;
      }
      setExpandedDiff(filePath);
      if (diffContent[filePath]) return;
      setDiffLoading(filePath);
      try {
        const resp = await fetch(
          repoUrl(`/api/git/diff?file=${encodeURIComponent(filePath)}`, repo),
          { signal: AbortSignal.timeout(10000) },
        );
        const data = await resp.json();
        if (data.success) {
          setDiffContent((prev) => ({ ...prev, [filePath]: data.diff || '(empty diff)' }));
        } else {
          setDiffContent((prev) => ({
            ...prev,
            [filePath]: data.binary
              ? 'Binary file — diff not available'
              : data.error || 'Diff failed',
          }));
        }
      } catch {
        setDiffContent((prev) => ({ ...prev, [filePath]: 'Failed to load diff' }));
      } finally {
        setDiffLoading(null);
      }
    },
    [expandedDiff, diffContent],
  );

  const handleStage = useCallback(
    async (fileOrAll: string | true) => {
      const body = fileOrAll === true ? { all: true } : { files: [fileOrAll] };
      setActionLoading(typeof fileOrAll === 'string' ? `stage-${fileOrAll}` : 'stage-all');
      const result = await gitAction('/api/git/stage', body, undefined, repo);
      setActionLoading(null);
      if (!result.success) show(String(result.error || 'Stage failed'), 'error');
    },
    [show, repo],
  );

  const handleUnstage = useCallback(
    async (fileOrAll: string | true) => {
      const body = fileOrAll === true ? { all: true } : { files: [fileOrAll] };
      setActionLoading(typeof fileOrAll === 'string' ? `unstage-${fileOrAll}` : 'unstage-all');
      const result = await gitAction('/api/git/unstage', body, undefined, repo);
      setActionLoading(null);
      if (!result.success) show(String(result.error || 'Unstage failed'), 'error');
    },
    [show, repo],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: callback uses refs
  const handleDiscard = useCallback(
    (filePath: string) => {
      setConfirmDialog({
        title: 'Discard changes?',
        message: `This will permanently discard all changes in "${shortPath(filePath)}". This cannot be undone.`,
        confirmLabel: 'Discard',
        destructive: true,
        onConfirm: async () => {
          setConfirmDialog(null);
          setActionLoading(`discard-${filePath}`);
          const result = await gitAction(
            '/api/git/discard',
            { files: [filePath] },
            undefined,
            repo,
          );
          setActionLoading(null);
          if (result.success) {
            show(`Discarded: ${shortPath(filePath)}`, 'success');
            setDiffContent((prev) => {
              const n = { ...prev };
              delete n[filePath];
              return n;
            });
            if (expandedDiff === filePath) setExpandedDiff(null);
          } else {
            show(String(result.error || 'Discard failed'), 'error');
          }
        },
      });
    },
    [show, expandedDiff],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: callback uses refs
  const handleCommit = useCallback(async () => {
    if (!commitMsg.trim() || stagedFiles.length === 0) return;
    setActionLoading('commit');
    const result = await gitAction(
      '/api/git/commit',
      { message: commitMsg.trim() },
      undefined,
      repo,
    );
    setActionLoading(null);
    if (result.success) {
      const hash = typeof result.hash === 'string' ? result.hash.slice(0, 7) : '';
      show(`Committed ${hash}`, 'success');
      setCommitMsg('');
      setDiffContent({});
      setExpandedDiff(null);
    } else {
      show(String(result.error || 'Commit failed'), 'error');
    }
  }, [commitMsg, stagedFiles.length, show]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: callback uses refs
  const handleAiMessage = useCallback(async () => {
    setAiMsgLoading(true);
    try {
      const resp = await fetch(repoUrl('/api/git/ai-message', repo), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      setCommitMsg(data.success ? data.message : data.fallback || 'chore: update files');
    } catch {
      setCommitMsg('chore: update files');
    } finally {
      setAiMsgLoading(false);
    }
  }, []);

  const handlePush = useCallback(async () => {
    setActionLoading('push');
    const result = await gitAction('/api/git/push', undefined, undefined, repo);
    setActionLoading(null);
    if (result.success) show('Pushed', 'success');
    else show(String(result.error || 'Push failed'), 'error');
  }, [show, repo]);

  const handleFetch = useCallback(async () => {
    setActionLoading('fetch');
    const result = await gitAction('/api/git/fetch', undefined, undefined, repo);
    setActionLoading(null);
    if (result.success) show('Fetched', 'success');
    else show(String(result.error || 'Fetch failed'), 'error');
  }, [show, repo]);

  const handlePull = useCallback(async () => {
    setActionLoading('pull');
    const result = await gitAction('/api/git/pull', undefined, undefined, repo);
    setActionLoading(null);
    if (result.success) show(String(result.message || 'Pulled'), 'success');
    else show(String(result.error || 'Pull failed'), 'error');
  }, [show, repo]);

  const handleRevert = useCallback(async () => {
    setActionLoading('revert');
    const result = await gitAction('/api/git/revert', { hard: false }, undefined, repo);
    setActionLoading(null);
    if (result.success) show('Last commit undone (changes kept staged)', 'success');
    else show(String(result.error || 'Revert failed'), 'error');
    setConfirmDialog(null);
  }, [show, repo]);

  const handleMerge = useCallback(
    async (branchName: string) => {
      setActionLoading('merge');
      const result = await gitAction(
        '/api/git/branch/merge',
        { name: branchName },
        undefined,
        repo,
      );
      setActionLoading(null);
      if (result.success) show(`Merged ${branchName}`, 'success');
      else show(String(result.error || 'Merge failed'), 'error');
      setConfirmDialog(null);
    },
    [show, repo],
  );

  const handleSwitchBranch = useCallback(
    async (name: string) => {
      setActionLoading(`branch-switch-${name}`);
      const result = await gitAction('/api/git/branch/switch', { name }, undefined, repo);
      setActionLoading(null);
      if (result.success) {
        show(`Switched to ${name}`, 'success');
        setShowBranches(false);
      } else show(String(result.error || 'Switch failed'), 'error');
    },
    [show, repo],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: callback uses refs
  const handleDeleteBranch = useCallback(
    (name: string) => {
      setConfirmDialog({
        title: 'Delete branch?',
        message: `Delete branch "${name}"? This cannot be undone.`,
        confirmLabel: 'Delete',
        destructive: true,
        onConfirm: async () => {
          setConfirmDialog(null);
          setActionLoading(`branch-del-${name}`);
          const result = await gitAction('/api/git/branch/delete', { name }, undefined, repo);
          setActionLoading(null);
          if (result.success) {
            show(`Deleted: ${name}`, 'success');
            setBranches((prev) => prev.filter((b) => b.name !== name));
          } else show(String(result.error || 'Delete failed'), 'error');
        },
      });
    },
    [show],
  );

  const handleCreateBranch = useCallback(async () => {
    if (!newBranch.trim()) return;
    setActionLoading('branch-create');
    const result = await gitAction(
      '/api/git/branch/create',
      { name: newBranch.trim() },
      undefined,
      repo,
    );
    setActionLoading(null);
    if (result.success) {
      show(`Created: ${newBranch.trim()}`, 'success');
      setNewBranch('');
      setShowBranches(false);
      setTimeout(() => setShowBranches(true), 100);
    } else show(String(result.error || 'Create failed'), 'error');
  }, [newBranch, show, repo]);

  const handleStashSave = useCallback(async () => {
    setActionLoading('stash-save');
    const result = await gitAction('/api/git/stash/save', undefined, undefined, repo);
    setActionLoading(null);
    if (result.success) {
      show('Stashed', 'success');
      setShowStash(false);
      setTimeout(() => setShowStash(true), 100);
    } else show(String(result.error || 'Stash failed'), 'error');
  }, [show, repo]);

  const handleStashApply = useCallback(
    async (ref: string) => {
      setActionLoading(`stash-apply-${ref}`);
      const result = await gitAction('/api/git/stash/apply', { ref }, undefined, repo);
      setActionLoading(null);
      if (result.success) show('Applied', 'success');
      else show(String(result.error || 'Apply failed'), 'error');
    },
    [show, repo],
  );

  const handleStashDrop = useCallback(
    async (ref: string) => {
      setActionLoading(`stash-drop-${ref}`);
      const result = await gitAction('/api/git/stash/drop', { ref }, undefined, repo);
      setActionLoading(null);
      if (result.success) {
        show('Dropped', 'success');
        setStashList((prev) => prev.filter((s) => s.ref !== ref));
      } else show(String(result.error || 'Drop failed'), 'error');
    },
    [show, repo],
  );

  const copyHash = useCallback(async (hash: string) => {
    const ok = await copyToClipboard(hash);
    if (ok) {
      setCopiedHash(hash);
      setTimeout(() => setCopiedHash(null), 1500);
    }
  }, []);

  const renderDiffLines = (diff: string) => {
    const lines = diff.split('\n');
    return (
      <div
        style={{
          maxHeight: 300,
          overflow: 'auto',
          fontFamily: 'var(--font-family-mono, monospace)',
          fontSize: 'var(--font-size-caption)',
          lineHeight: 1.6,
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--color-border-opaque)',
          marginTop: 4,
          marginBottom: 4,
        }}
      >
        {lines.map((line, i) => {
          let bg = 'transparent';
          let color = 'var(--color-label-secondary)';
          if (line.startsWith('+') && !line.startsWith('+++')) {
            bg = 'rgba(52, 199, 89, 0.1)';
            color = 'var(--color-tint-green)';
          } else if (line.startsWith('-') && !line.startsWith('---')) {
            bg = 'rgba(255, 59, 48, 0.1)';
            color = 'var(--color-tint-red)';
          } else if (line.startsWith('@@')) {
            bg = 'rgba(0, 122, 255, 0.06)';
            color = 'var(--color-tint-blue)';
          }
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: steps have no stable unique ID
              key={i}
              style={{
                padding: '0 8px',
                background: bg,
                color,
                whiteSpace: 'pre' as const,
                minHeight: 18,
              }}
            >
              {line || ' '}
            </div>
          );
        })}
      </div>
    );
  };

  const renderFileRow = (f: GitFile, staged: boolean) => {
    const st = fileStatusCode(f);
    const isExpanded = expandedDiff === f.path;
    const isLoading = diffLoading === f.path;
    return (
      <div key={`${staged ? 's' : 'u'}-${f.path}`}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px 8px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-caption)',
          }}
        >
          <span
            style={{
              fontWeight: 700,
              color: st.color,
              width: 14,
              textAlign: 'center',
              flexShrink: 0,
              fontFamily: 'var(--font-family-mono, monospace)',
            }}
          >
            {st.code}
          </span>
          <button
            type="button"
            onClick={() => fetchDiff(f.path)}
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-mono, monospace)',
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-label-primary)',
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap' as const,
            }}
            title={f.path}
          >
            {f.path}
          </button>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {staged ? (
              <button
                type="button"
                onClick={() => handleUnstage(f.path)}
                disabled={actionLoading === `unstage-${f.path}`}
                style={{ ...gitBtnGhost, padding: '1px 6px', minHeight: 22, fontSize: 11 }}
              >
                Unstage
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleStage(f.path)}
                  disabled={actionLoading === `stage-${f.path}`}
                  style={{ ...gitBtnGhost, padding: '1px 6px', minHeight: 22, fontSize: 11 }}
                >
                  Stage
                </button>
                <button
                  type="button"
                  onClick={() => handleDiscard(f.path)}
                  disabled={actionLoading === `discard-${f.path}`}
                  style={{
                    ...gitBtnGhost,
                    padding: '1px 6px',
                    minHeight: 22,
                    fontSize: 11,
                    color: 'var(--color-tint-red)',
                  }}
                >
                  Discard
                </button>
              </>
            )}
          </div>
        </div>
        {isExpanded &&
          (isLoading ? (
            <div
              style={{
                padding: '8px 12px',
                fontSize: 'var(--font-size-caption)',
                color: 'var(--color-label-tertiary)',
              }}
            >
              Loading diff...
            </div>
          ) : diffContent[f.path] ? (
            renderDiffLines(diffContent[f.path])
          ) : null)}
      </div>
    );
  };

  const commitChips = ['feat', 'fix', 'chore', 'docs', 'refactor'];

  return (
    <div>
      {/* Compact header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span
          style={{
            ...badge(branchBadgeBg, 'var(--color-static-white)'),
            fontFamily: 'var(--font-family-mono, monospace)',
            fontSize: 'var(--font-size-caption)',
          }}
        >
          {branchName}
        </span>

        {git.isClean ? (
          <span
            style={{
              color: 'var(--color-tint-green)',
              fontWeight: 600,
              fontSize: 'var(--font-size-caption)',
            }}
          >
            Clean
          </span>
        ) : (
          <span style={badge('var(--color-tint-orange)', 'var(--color-static-white)')}>
            {git.changedFiles} changed
          </span>
        )}

        {isBehind && (
          <span style={badge('var(--color-tint-orange)', 'var(--color-static-white)')}>
            {git.behind} behind
          </span>
        )}
        {isAhead && (
          <span style={badge('var(--color-tint-blue)', 'var(--color-static-white)')}>
            {git.ahead} ahead
          </span>
        )}

        <span style={{ flex: 1 }} />

        <button
          type="button"
          onClick={handleFetch}
          disabled={actionLoading === 'fetch'}
          style={{ ...gitBtnGhost, opacity: actionLoading === 'fetch' ? 0.6 : 1 }}
        >
          {actionLoading === 'fetch' ? 'Fetching...' : 'Fetch'}
        </button>

        {isBehind && (
          <button
            type="button"
            onClick={handlePull}
            disabled={actionLoading === 'pull'}
            style={{
              ...gitBtnGhost,
              background:
                actionLoading === 'pull'
                  ? 'var(--color-fill-secondary)'
                  : 'var(--color-tint-green)',
              color:
                actionLoading === 'pull'
                  ? 'var(--color-label-tertiary)'
                  : 'var(--color-static-white)',
            }}
          >
            {actionLoading === 'pull' ? 'Pulling...' : 'Pull'}
          </button>
        )}

        {isDirty && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            style={{ ...gitBtnPrimary, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            Review Changes <span style={{ fontSize: 10 }}>{expanded ? '\u25B2' : '\u25BC'}</span>
          </button>
        )}

        {!isDirty && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            style={{ ...gitBtnGhost, fontSize: 10, padding: '4px 8px' }}
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '\u25B2' : '\u25BC'}
          </button>
        )}
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div
          style={{
            marginTop: 12,
            borderTop: '1px solid var(--color-border-opaque)',
            paddingTop: 12,
          }}
        >
          {/* CHANGES */}
          {unstagedFiles.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    fontWeight: 700,
                    color: 'var(--color-label-tertiary)',
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.5px',
                  }}
                >
                  Changes ({unstagedFiles.length})
                </span>
                <button
                  type="button"
                  onClick={() => handleStage(true)}
                  disabled={actionLoading === 'stage-all'}
                  style={{ ...gitBtnGhost, padding: '1px 8px', minHeight: 22, fontSize: 11 }}
                >
                  Stage All
                </button>
              </div>
              <div
                style={{
                  background: 'var(--color-fill-quaternary)',
                  borderRadius: 'var(--radius-md)',
                  padding: '4px 4px',
                }}
              >
                {unstagedFiles.map((f) => renderFileRow(f, false))}
              </div>
            </div>
          )}

          {/* STAGED */}
          {stagedFiles.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    fontWeight: 700,
                    color: 'var(--color-tint-green)',
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.5px',
                  }}
                >
                  Staged ({stagedFiles.length})
                </span>
                <button
                  type="button"
                  onClick={() => handleUnstage(true)}
                  disabled={actionLoading === 'unstage-all'}
                  style={{ ...gitBtnGhost, padding: '1px 8px', minHeight: 22, fontSize: 11 }}
                >
                  Unstage All
                </button>
              </div>
              <div
                style={{
                  background: 'rgba(52, 199, 89, 0.05)',
                  borderRadius: 'var(--radius-md)',
                  padding: '4px 4px',
                  border: '1px solid rgba(52, 199, 89, 0.15)',
                }}
              >
                {stagedFiles.map((f) => renderFileRow(f, true))}
              </div>
            </div>
          )}

          {/* COMMIT */}
          {stagedFiles.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <span
                style={{
                  fontSize: 'var(--font-size-caption)',
                  fontWeight: 700,
                  color: 'var(--color-label-tertiary)',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.5px',
                }}
              >
                Commit
              </span>
              <div style={{ position: 'relative', marginTop: 6 }}>
                <textarea
                  ref={textareaRef}
                  value={commitMsg}
                  onChange={(e) => setCommitMsg(e.target.value)}
                  placeholder="feat: "
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    paddingRight: 48,
                    background: 'var(--color-bg-primary, #fff)',
                    border: '1px solid var(--color-border-opaque)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-footnote)',
                    fontFamily: 'var(--font-family-mono, monospace)',
                    color: 'var(--color-label-primary)',
                    resize: 'vertical',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      handleCommit();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAiMessage}
                  disabled={aiMsgLoading}
                  title="Generate AI commit message"
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    background: 'none',
                    border: 'none',
                    cursor: aiMsgLoading ? 'wait' : 'pointer',
                    fontSize: 16,
                    opacity: aiMsgLoading ? 0.4 : 0.7,
                    padding: 2,
                    color: 'var(--color-label-secondary)',
                  }}
                >
                  {aiMsgLoading ? '...' : 'AI'}
                </button>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginTop: 6,
                  flexWrap: 'wrap',
                }}
              >
                {commitChips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => {
                      setCommitMsg(`${chip}: `);
                      textareaRef.current?.focus();
                    }}
                    style={{
                      ...gitBtnGhost,
                      padding: '1px 8px',
                      minHeight: 20,
                      fontSize: 11,
                      fontFamily: 'var(--font-family-mono, monospace)',
                    }}
                  >
                    {chip}
                  </button>
                ))}
                <span style={{ flex: 1 }} />
                <button
                  type="button"
                  onClick={handleCommit}
                  disabled={
                    actionLoading === 'commit' || !commitMsg.trim() || stagedFiles.length === 0
                  }
                  style={{
                    ...gitBtnPrimary,
                    padding: '4px 16px',
                    minHeight: 28,
                    opacity:
                      actionLoading === 'commit' || !commitMsg.trim() || stagedFiles.length === 0
                        ? 0.5
                        : 1,
                    cursor: actionLoading === 'commit' ? 'wait' : 'pointer',
                  }}
                >
                  {actionLoading === 'commit' ? 'Committing...' : 'Commit'}
                </button>
                <span
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-label-quaternary)',
                  }}
                >
                  Cmd+Enter
                </span>
              </div>
            </div>
          )}

          {/* Separator */}
          <div
            style={{ borderTop: '1px solid var(--color-border-transparent)', margin: '8px 0' }}
          />

          {/* More Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {isAhead && (
              <>
                <button
                  type="button"
                  onClick={handlePush}
                  disabled={actionLoading === 'push'}
                  style={{
                    ...gitBtnPrimary,
                    opacity: actionLoading === 'push' ? 0.6 : 1,
                    cursor: actionLoading === 'push' ? 'wait' : 'pointer',
                  }}
                >
                  {actionLoading === 'push'
                    ? 'Pushing...'
                    : `Push (${git.ahead} commit${(git.ahead ?? 0) > 1 ? 's' : ''})`}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setConfirmDialog({
                      title: 'Undo Last Commit',
                      message:
                        'Vrati posledni commit. Zmeny zustanou staged — muzete je upravit a commitnout znovu.',
                      confirmLabel: 'Undo',
                      destructive: true,
                      onConfirm: handleRevert,
                    })
                  }
                  disabled={actionLoading === 'revert'}
                  style={{
                    ...gitBtnGhost,
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-label-tertiary)',
                  }}
                >
                  {actionLoading === 'revert' ? '...' : 'Undo Last Commit'}
                </button>
              </>
            )}
            {['Branches', 'Stash', 'History'].map((tab) => {
              const key = tab.toLowerCase();
              const isActive =
                key === 'branches' ? showBranches : key === 'stash' ? showStash : showHistory;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    if (key === 'branches') {
                      setShowBranches(!showBranches);
                      setShowStash(false);
                      setShowHistory(false);
                    } else if (key === 'stash') {
                      setShowStash(!showStash);
                      setShowBranches(false);
                      setShowHistory(false);
                    } else {
                      setShowHistory(!showHistory);
                      setShowBranches(false);
                      setShowStash(false);
                    }
                  }}
                  style={{
                    ...gitBtnGhost,
                    fontWeight: isActive ? 700 : 500,
                    borderBottom: isActive
                      ? '2px solid var(--color-tint-blue)'
                      : '2px solid transparent',
                    borderRadius: 0,
                  }}
                >
                  {tab}
                </button>
              );
            })}
          </div>

          {/* Branches */}
          {showBranches && (
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: 'var(--color-fill-quaternary)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {branchesLoading ? (
                <div
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-label-tertiary)',
                  }}
                >
                  Loading branches...
                </div>
              ) : (
                <>
                  {branches.map((b) => (
                    <div
                      key={b.name}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0',
                        fontSize: 'var(--font-size-caption)',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'var(--font-family-mono, monospace)',
                          fontWeight: b.isCurrent ? 700 : 400,
                          color: b.isCurrent
                            ? 'var(--color-tint-green)'
                            : 'var(--color-label-primary)',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap' as const,
                        }}
                      >
                        {b.isCurrent && '\u2022 '}
                        {b.name}
                      </span>
                      {!b.isCurrent && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleSwitchBranch(b.name)}
                            disabled={actionLoading === `branch-switch-${b.name}`}
                            style={{
                              ...gitBtnGhost,
                              padding: '1px 6px',
                              minHeight: 20,
                              fontSize: 11,
                            }}
                          >
                            Switch
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmDialog({
                                title: `Merge ${b.name}`,
                                message: `Mergne branch "${b.name}" do aktualni branch "${git.branch}". Zmeny z te branch se pridaji sem.`,
                                confirmLabel: 'Merge',
                                destructive: false,
                                onConfirm: () => handleMerge(b.name),
                              })
                            }
                            disabled={actionLoading === 'merge'}
                            style={{
                              ...gitBtnGhost,
                              padding: '1px 6px',
                              minHeight: 20,
                              fontSize: 11,
                              color: 'var(--color-tint-green)',
                            }}
                          >
                            Merge
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteBranch(b.name)}
                            disabled={actionLoading === `branch-del-${b.name}`}
                            style={{
                              ...gitBtnGhost,
                              padding: '1px 6px',
                              minHeight: 20,
                              fontSize: 11,
                              color: 'var(--color-tint-red)',
                            }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
                    <input
                      value={newBranch}
                      onChange={(e) => setNewBranch(e.target.value)}
                      placeholder="New branch..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateBranch();
                      }}
                      style={{
                        flex: 1,
                        padding: '4px 8px',
                        border: '1px solid var(--color-border-opaque)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-caption)',
                        fontFamily: 'var(--font-family-mono, monospace)',
                        background: 'var(--color-bg-primary, #fff)',
                        color: 'var(--color-label-primary)',
                        outline: 'none',
                        minHeight: 24,
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleCreateBranch}
                      disabled={!newBranch.trim() || actionLoading === 'branch-create'}
                      style={{
                        ...gitBtnPrimary,
                        padding: '2px 10px',
                        minHeight: 24,
                        fontSize: 11,
                        opacity: !newBranch.trim() ? 0.5 : 1,
                      }}
                    >
                      Create
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Stash */}
          {showStash && (
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: 'var(--color-fill-quaternary)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {stashLoading ? (
                <div
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-label-tertiary)',
                  }}
                >
                  Loading stash...
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 8 }}>
                    <button
                      type="button"
                      onClick={handleStashSave}
                      disabled={git.isClean || actionLoading === 'stash-save'}
                      style={{
                        ...gitBtnPrimary,
                        padding: '4px 12px',
                        fontSize: 11,
                        opacity: git.isClean ? 0.5 : 1,
                      }}
                    >
                      Stash Changes
                    </button>
                  </div>
                  {stashList.length === 0 && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        color: 'var(--color-label-tertiary)',
                      }}
                    >
                      No stashes
                    </div>
                  )}
                  {stashList.map((s) => (
                    <div
                      key={s.ref}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 0',
                        fontSize: 'var(--font-size-caption)',
                      }}
                    >
                      <code
                        style={{
                          fontFamily: 'var(--font-family-mono, monospace)',
                          color: 'var(--color-tint-blue)',
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {s.ref}
                      </code>
                      <span
                        style={{
                          flex: 1,
                          color: 'var(--color-label-primary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap' as const,
                          minWidth: 0,
                        }}
                      >
                        {s.message}
                      </span>
                      <span style={{ color: 'var(--color-label-tertiary)', flexShrink: 0 }}>
                        {s.time}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleStashApply(s.ref)}
                        disabled={actionLoading === `stash-apply-${s.ref}`}
                        style={{ ...gitBtnGhost, padding: '1px 6px', minHeight: 20, fontSize: 11 }}
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStashDrop(s.ref)}
                        disabled={actionLoading === `stash-drop-${s.ref}`}
                        style={{
                          ...gitBtnGhost,
                          padding: '1px 6px',
                          minHeight: 20,
                          fontSize: 11,
                          color: 'var(--color-tint-red)',
                        }}
                      >
                        Drop
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* History */}
          {showHistory && (
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: 'var(--color-fill-quaternary)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {git.commits && git.commits.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {git.commits.map((c) => (
                    <div
                      key={c.hash}
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'baseline',
                        fontSize: 'var(--font-size-caption)',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => copyHash(c.hash)}
                        title={copiedHash === c.hash ? 'Copied!' : 'Copy hash'}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          fontFamily: 'var(--font-family-mono, monospace)',
                          color:
                            copiedHash === c.hash
                              ? 'var(--color-tint-green)'
                              : 'var(--color-tint-blue)',
                          fontWeight: 600,
                          flexShrink: 0,
                          fontSize: 'var(--font-size-caption)',
                        }}
                      >
                        {copiedHash === c.hash ? 'Copied!' : c.hash}
                      </button>
                      <span
                        style={{
                          color: 'var(--color-label-primary)',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap' as const,
                          minWidth: 0,
                        }}
                      >
                        {c.message}
                      </span>
                      <span style={{ color: 'var(--color-label-tertiary)', flexShrink: 0 }}>
                        {c.time}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-label-tertiary)',
                  }}
                >
                  No commits
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {ToastEl}
      {confirmDialog && (
        <GitConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          destructive={confirmDialog.destructive}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

// ─── Git Panel (Entry Point) ───

function GitPanel() {
  const [mode, setMode] = useState<'simple' | 'advanced'>(() => {
    try {
      return (localStorage.getItem('zbk-git-mode') as 'simple' | 'advanced') || 'simple';
    } catch {
      return 'simple';
    }
  });

  const [repo, setRepo] = useState<RepoTarget>(() => {
    try {
      return (localStorage.getItem('zbk-git-repo') as RepoTarget) || 'zoobook';
    } catch {
      return 'zoobook';
    }
  });

  const toggleRepo = useCallback((r: RepoTarget) => {
    setRepo(r);
    try {
      localStorage.setItem('zbk-git-repo', r);
    } catch {
      /* ignore */
    }
  }, []);

  const pollingUrl = repoUrl(
    mode === 'advanced' ? '/api/git/status?commitCount=20' : '/api/git/status',
    repo,
  );

  const [gitData, setGitData] = useState<GitStatusData>({});
  const [refreshCount, setRefreshCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const polled = usePolling<GitStatusData>(pollingUrl, 10000, {});

  // Merge polled data into state (polling updates automatically)
  useEffect(() => {
    if (polled.success) setGitData(polled);
  }, [polled]);

  const git = gitData;

  // Manual refresh — re-fetch git status NOW and signal child to reset AI message
  const refreshGit = useCallback(async () => {
    setRefreshing(true);
    try {
      const resp = await fetch(pollingUrl, { signal: AbortSignal.timeout(5000) });
      if (resp.ok) setGitData(await resp.json());
    } catch {
      /* ignore */
    }
    setRefreshCount((c) => c + 1);
    setRefreshing(false);
  }, [pollingUrl]);

  const toggleMode = useCallback(() => {
    const next = mode === 'simple' ? 'advanced' : 'simple';
    setMode(next);
    try {
      localStorage.setItem('zbk-git-mode', next);
    } catch {
      /* ignore */
    }
  }, [mode]);

  if (!git.success || git.error) {
    return (
      <div style={{ ...card, color: 'var(--color-label-tertiary)' }}>
        {git.error || 'Not a git repository'}
      </div>
    );
  }

  const branchName = git.branch || 'unknown';
  const isMainish = branchName === 'main' || branchName === 'develop' || branchName === 'master';
  const branchBadgeBg = isMainish ? 'var(--color-tint-green)' : 'var(--color-tint-blue)';

  return (
    <div style={card}>
      {/* Header with mode toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: mode === 'simple' ? 12 : 0,
        }}
      >
        {mode === 'simple' && (
          <span
            style={{
              ...badge(branchBadgeBg, 'var(--color-static-white)'),
              fontFamily: 'var(--font-family-mono, monospace)',
              fontSize: 'var(--font-size-caption)',
            }}
          >
            {branchName}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={refreshGit}
          disabled={refreshing}
          title="Refresh git status + regenerate AI commit message"
          style={{
            ...gitBtnGhost,
            padding: '3px 8px',
            minHeight: 22,
            fontSize: 'var(--font-size-caption)',
            color: refreshing ? 'var(--color-label-tertiary)' : 'var(--color-tint-blue)',
            cursor: refreshing ? 'wait' : 'pointer',
          }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
        {/* Repo selector */}
        <div
          style={{
            display: 'inline-flex',
            borderRadius: 'var(--radius-sm)',
            overflow: 'hidden',
            border: '1px solid var(--color-border-opaque)',
            fontSize: 'var(--font-size-caption)',
          }}
        >
          <button
            type="button"
            onClick={() => repo !== 'zoobook' && toggleRepo('zoobook')}
            style={{
              border: 'none',
              padding: '3px 10px',
              fontWeight: repo === 'zoobook' ? 600 : 400,
              background: repo === 'zoobook' ? 'var(--color-fill-tertiary)' : 'transparent',
              color:
                repo === 'zoobook' ? 'var(--color-label-primary)' : 'var(--color-label-tertiary)',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
          >
            zoobook
          </button>
          <button
            type="button"
            onClick={() => repo !== 'workspace' && toggleRepo('workspace')}
            style={{
              border: 'none',
              borderLeft: '1px solid var(--color-border-opaque)',
              padding: '3px 10px',
              fontWeight: repo === 'workspace' ? 600 : 400,
              background: repo === 'workspace' ? 'var(--color-fill-tertiary)' : 'transparent',
              color:
                repo === 'workspace' ? 'var(--color-label-primary)' : 'var(--color-label-tertiary)',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
          >
            workspace
          </button>
        </div>
        <div
          style={{
            display: 'inline-flex',
            borderRadius: 'var(--radius-sm)',
            overflow: 'hidden',
            border: '1px solid var(--color-border-opaque)',
            fontSize: 'var(--font-size-caption)',
          }}
        >
          <button
            type="button"
            onClick={() => mode !== 'simple' && toggleMode()}
            style={{
              border: 'none',
              padding: '3px 10px',
              fontWeight: mode === 'simple' ? 600 : 400,
              background: mode === 'simple' ? 'var(--color-fill-tertiary)' : 'transparent',
              color:
                mode === 'simple' ? 'var(--color-label-primary)' : 'var(--color-label-tertiary)',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
          >
            Simple
          </button>
          <button
            type="button"
            onClick={() => mode !== 'advanced' && toggleMode()}
            style={{
              border: 'none',
              borderLeft: '1px solid var(--color-border-opaque)',
              padding: '3px 10px',
              fontWeight: mode === 'advanced' ? 600 : 400,
              background: mode === 'advanced' ? 'var(--color-fill-tertiary)' : 'transparent',
              color:
                mode === 'advanced' ? 'var(--color-label-primary)' : 'var(--color-label-tertiary)',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
          >
            Git
          </button>
        </div>
      </div>

      {mode === 'simple' ? (
        <GitPanelSimplified
          git={git}
          onRefresh={refreshGit}
          repo={repo}
          refreshCount={refreshCount}
        />
      ) : (
        <GitPanelAdvanced git={git} onRefresh={refreshGit} repo={repo} />
      )}
    </div>
  );
}

// ─── Dependency Health Panel ──────────────────────────────────────────────

interface DepPackage {
  name: string;
  current: string;
  latest: string;
  wanted: string;
  updateType: 'major' | 'minor' | 'patch' | 'same';
  dependencyType: string;
  workspace?: string;
  vulnerability?: { severity: string; title: string; url?: string };
}

interface DepsStatus {
  success?: boolean;
  packages?: DepPackage[];
  audit?: { critical: number; high: number; moderate: number; low: number };
  cachedAt?: number;
  error?: string;
}

interface AICheckResult {
  safe: boolean | null;
  breakingChanges: string[];
  recommendation: string;
}

function DependencyPanel() {
  const data = usePolling<DepsStatus>(`${DEV_SERVER}/api/deps/status`, 60000, {});
  const [filter, setFilter] = useState<'outdated' | 'all' | 'security'>('outdated');
  const [checking, setChecking] = useState<string | null>(null);
  const [aiResults, setAiResults] = useState<Record<string, AICheckResult>>({});
  const [updating, setUpdating] = useState<string | null>(null);
  const [updateResult, setUpdateResult] = useState<
    Record<string, { success: boolean; message: string }>
  >({});
  const [confirmUpdate, setConfirmUpdate] = useState<{ pkg: string; version: string } | null>(null);
  const [showAll, setShowAll] = useState(false);

  const packages = data.packages ?? [];
  const audit = data.audit;

  const outdatedCount = packages.filter((p) => p.updateType !== 'same').length;
  const vulnCount = audit ? audit.critical + audit.high + audit.moderate + audit.low : 0;

  const filtered = useMemo(() => {
    if (filter === 'security') return packages.filter((p) => p.vulnerability);
    if (filter === 'outdated') return packages.filter((p) => p.updateType !== 'same');
    return packages;
  }, [packages, filter]);

  const visible = showAll ? filtered : filtered.slice(0, 20);

  const handleCheck = useCallback(async (pkg: DepPackage) => {
    setChecking(pkg.name);
    try {
      const resp = await fetch(`${DEV_SERVER}/api/deps/check-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package: pkg.name,
          currentVersion: pkg.current,
          latestVersion: pkg.latest,
        }),
      });
      const result = await resp.json();
      setAiResults((prev) => ({ ...prev, [pkg.name]: result }));
    } catch {
      /* ignore */
    }
    setChecking(null);
  }, []);

  const handleUpdate = useCallback(async (pkg: string, version: string) => {
    setConfirmUpdate(null);
    setUpdating(pkg);
    try {
      const resp = await fetch(`${DEV_SERVER}/api/deps/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package: pkg, version }),
      });
      const result = await resp.json();
      setUpdateResult((prev) => ({
        ...prev,
        [pkg]: {
          success: result.success,
          message: result.message || (result.success ? 'Updated successfully' : 'Update failed'),
        },
      }));
    } catch {
      setUpdateResult((prev) => ({
        ...prev,
        [pkg]: { success: false, message: 'Request failed' },
      }));
    }
    setUpdating(null);
  }, []);

  const updateTypeBadge = (type: string, hasVuln: boolean) => {
    if (hasVuln) return badge('var(--color-tint-red)', 'var(--color-static-white)');
    switch (type) {
      case 'major':
        return badge('var(--color-tint-red)', 'var(--color-static-white)');
      case 'minor':
        return badge('var(--color-tint-orange)', 'var(--color-static-white)');
      case 'patch':
        return badge('var(--color-tint-blue)', 'var(--color-static-white)');
      default:
        return badge('var(--color-tint-light-green)', 'var(--color-tint-green)');
    }
  };

  const updateTypeLabel = (type: string, hasVuln: boolean) => {
    if (hasVuln) return 'CVE';
    switch (type) {
      case 'major':
        return 'Major';
      case 'minor':
        return 'Minor';
      case 'patch':
        return 'Patch';
      default:
        return 'Up to date';
    }
  };

  if (data.error) return <div style={labelSecondary}>{data.error}</div>;
  if (!data.success && !data.packages)
    return <div style={labelSecondary}>Loading dependencies...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Summary bar */}
      <div
        style={{
          ...card,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-body)',
            fontWeight: 600,
            color: 'var(--color-label-primary)',
          }}
        >
          {packages.length} packages
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-caption)',
            color: outdatedCount > 0 ? 'var(--color-tint-orange)' : 'var(--color-tint-green)',
          }}
        >
          {outdatedCount} outdated
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-caption)',
            color: vulnCount > 0 ? 'var(--color-tint-red)' : 'var(--color-tint-green)',
          }}
        >
          {vulnCount} vulnerabilities
        </span>
        {data.cachedAt && (
          <span
            style={{
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-label-quaternary)',
              marginLeft: 'auto',
            }}
          >
            cached {new Date(data.cachedAt).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4 }}>
        {(['outdated', 'all', 'security'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            style={{
              ...gitBtnBase,
              background: filter === f ? 'var(--color-fill-tertiary)' : 'transparent',
              color: filter === f ? 'var(--color-label-primary)' : 'var(--color-label-tertiary)',
              fontWeight: filter === f ? 700 : 400,
              textTransform: 'capitalize' as const,
              borderBottom:
                filter === f ? '2px solid var(--color-tint-blue)' : '2px solid transparent',
              borderRadius: 0,
            }}
          >
            {f === 'outdated'
              ? `Outdated (${outdatedCount})`
              : f === 'security'
                ? `Security (${vulnCount})`
                : `All (${packages.length})`}
          </button>
        ))}
      </div>

      {/* Table */}
      {visible.length === 0 ? (
        <div
          style={{
            ...card,
            textAlign: 'center',
            color: 'var(--color-tint-green)',
            fontSize: 'var(--font-size-caption)',
          }}
        >
          {filter === 'security'
            ? 'No vulnerabilities found'
            : filter === 'outdated'
              ? 'All packages up to date'
              : 'No packages'}
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-opaque)' }}>
                {['Package', 'Current', 'Latest', 'Status', ''].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      fontSize: 'var(--font-size-caption)',
                      fontWeight: 600,
                      color: 'var(--color-label-tertiary)',
                      textTransform: 'uppercase' as const,
                      letterSpacing: '0.5px',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((pkg) => {
                const ai = aiResults[pkg.name];
                const isChecking = checking === pkg.name;
                const isUpdating = updating === pkg.name;
                const result = updateResult[pkg.name];
                const hasVuln = !!pkg.vulnerability;

                return (
                  <tr
                    key={pkg.name}
                    style={{ borderBottom: '1px solid var(--color-border-transparent)' }}
                  >
                    <td style={{ padding: '8px 12px' }}>
                      <code
                        style={{
                          fontSize: 'var(--font-size-caption)',
                          fontFamily: 'var(--font-family-mono, monospace)',
                          color: 'var(--color-label-primary)',
                        }}
                      >
                        {pkg.name}
                      </code>
                      {pkg.workspace && (
                        <span
                          style={{
                            fontSize: 10,
                            color: 'var(--color-label-quaternary)',
                            marginLeft: 6,
                          }}
                        >
                          {pkg.workspace}
                        </span>
                      )}
                      {/* AI recommendation row */}
                      {ai && (
                        <div
                          style={{
                            marginTop: 6,
                            padding: '6px 10px',
                            borderRadius: 'var(--radius-sm)',
                            background: ai.safe
                              ? 'var(--color-tint-light-green)'
                              : 'var(--color-tint-light-orange)',
                            fontSize: 'var(--font-size-caption)',
                            lineHeight: 1.4,
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              color: ai.safe
                                ? 'var(--color-tint-green)'
                                : 'var(--color-tint-orange)',
                              marginBottom: 2,
                            }}
                          >
                            {ai.safe ? 'Safe to update' : 'Breaking changes detected'}
                          </div>
                          <div style={{ color: 'var(--color-label-secondary)' }}>
                            {ai.recommendation}
                          </div>
                          {ai.breakingChanges.length > 0 && (
                            <ul
                              style={{
                                margin: '4px 0 0 16px',
                                padding: 0,
                                color: 'var(--color-tint-red)',
                                fontSize: 11,
                              }}
                            >
                              {ai.breakingChanges.map((bc, i) => (
                                // biome-ignore lint/suspicious/noArrayIndexKey: log lines have no stable unique ID
                                <li key={i}>{bc}</li>
                              ))}
                            </ul>
                          )}
                          <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                            {ai.safe ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setConfirmUpdate({ pkg: pkg.name, version: pkg.latest })
                                }
                                disabled={isUpdating}
                                style={{
                                  ...gitBtnBase,
                                  padding: '2px 10px',
                                  minHeight: 24,
                                  background: 'var(--color-tint-green)',
                                  color: 'var(--color-static-white)',
                                }}
                              >
                                {isUpdating ? 'Updating...' : 'Update'}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  setConfirmUpdate({ pkg: pkg.name, version: pkg.latest })
                                }
                                disabled={isUpdating}
                                style={{
                                  ...gitBtnBase,
                                  padding: '2px 10px',
                                  minHeight: 24,
                                  background: 'var(--color-tint-orange)',
                                  color: 'var(--color-static-white)',
                                }}
                              >
                                {isUpdating ? 'Updating...' : 'Update Anyway'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {result && (
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 'var(--font-size-caption)',
                            fontWeight: 600,
                            color: result.success
                              ? 'var(--color-tint-green)'
                              : 'var(--color-tint-red)',
                          }}
                        >
                          {result.message}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <code
                        style={{
                          fontSize: 'var(--font-size-caption)',
                          fontFamily: 'var(--font-family-mono, monospace)',
                          color: 'var(--color-label-secondary)',
                        }}
                      >
                        {pkg.current}
                      </code>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <code
                        style={{
                          fontSize: 'var(--font-size-caption)',
                          fontFamily: 'var(--font-family-mono, monospace)',
                          color: 'var(--color-label-primary)',
                        }}
                      >
                        {pkg.latest}
                      </code>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={updateTypeBadge(pkg.updateType, hasVuln)}>
                        {updateTypeLabel(pkg.updateType, hasVuln)}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {pkg.updateType !== 'same' && !ai && (
                        <button
                          type="button"
                          onClick={() => handleCheck(pkg)}
                          disabled={isChecking}
                          style={{
                            ...gitBtnGhost,
                            padding: '2px 10px',
                            minHeight: 24,
                            opacity: isChecking ? 0.6 : 1,
                            cursor: isChecking ? 'wait' : 'pointer',
                          }}
                        >
                          {isChecking ? '...' : 'Check'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Show all link */}
      {filtered.length > 20 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            ...gitBtnGhost,
            width: '100%',
            textAlign: 'center',
            color: 'var(--color-label-secondary)',
            fontSize: 'var(--font-size-caption)',
          }}
        >
          Show all {filtered.length} packages
        </button>
      )}
      {showAll && filtered.length > 20 && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          style={{
            ...gitBtnGhost,
            width: '100%',
            textAlign: 'center',
            color: 'var(--color-label-secondary)',
            fontSize: 'var(--font-size-caption)',
          }}
        >
          Show less
        </button>
      )}

      {/* Confirm dialog */}
      {confirmUpdate && (
        <GitConfirmDialog
          title={`Update ${confirmUpdate.pkg}`}
          message={`Update ${confirmUpdate.pkg} to version ${confirmUpdate.version}? This will run pnpm update and verify TypeScript compilation.`}
          confirmLabel="Update"
          onConfirm={() => handleUpdate(confirmUpdate.pkg, confirmUpdate.version)}
          onCancel={() => setConfirmUpdate(null)}
        />
      )}
    </div>
  );
}

// ─── Ops Health Panel ─────────────────────────────────────────────────────

interface OpsCheck {
  name: string;
  status: 'pass' | 'warn' | 'fail';
  detail: string;
}

interface OpsReport {
  success?: boolean;
  report?: {
    timestamp: string;
    status: 'PASS' | 'WARN' | 'FAIL';
    checks: OpsCheck[];
  };
  error?: string;
}

function OpsReportPanel() {
  const [report, setReport] = useState<OpsReport>({});
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      const resp = await fetch(`${DEV_SERVER}/api/ops/report`);
      if (resp.ok) {
        const data = await resp.json();
        setReport(data);
      }
    } catch {
      /* ignore */
    }
    setFetched(true);
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleRun = useCallback(async () => {
    setLoading(true);
    try {
      await fetch(`${DEV_SERVER}/api/ops/run`, { method: 'POST' });
      // After run completes, fetch the formatted report
      await fetchReport();
    } catch {
      setReport({ error: 'Request failed' });
    }
    setLoading(false);
  }, [fetchReport]);

  const statusDot = (s: string) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    background:
      s === 'pass'
        ? 'var(--color-tint-green)'
        : s === 'warn'
          ? 'var(--color-tint-orange)'
          : 'var(--color-tint-red)',
    flexShrink: 0 as const,
  });

  const overallColor = (s?: string) =>
    s === 'PASS'
      ? 'var(--color-tint-green)'
      : s === 'WARN'
        ? 'var(--color-tint-orange)'
        : 'var(--color-tint-red)';

  if (!fetched) return <div style={labelSecondary}>Loading ops report...</div>;

  const r = report.report;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header */}
      <div
        style={{
          ...card,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {r ? (
          <>
            <span
              style={{
                fontSize: 'var(--font-size-caption)',
                color: 'var(--color-label-secondary)',
              }}
            >
              Last run: {new Date(r.timestamp).toLocaleString()}
            </span>
            <span
              style={{
                ...badge(
                  overallColor(r.status) === 'var(--color-tint-green)'
                    ? 'var(--color-tint-light-green)'
                    : 'var(--color-tint-light-orange)',
                  overallColor(r.status),
                ),
                fontWeight: 700,
              }}
            >
              {r.status}
            </span>
          </>
        ) : (
          <span
            style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-tertiary)' }}
          >
            No ops report yet.
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={handleRun}
          disabled={loading}
          style={{
            ...gitBtnPrimary,
            padding: '4px 14px',
            minHeight: 28,
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          {loading ? 'Running...' : 'Run Now'}
        </button>
      </div>

      {/* Check results grid */}
      {r && r.checks.length > 0 && (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {r.checks.map((check) => (
                <tr
                  key={check.name}
                  style={{ borderBottom: '1px solid var(--color-border-transparent)' }}
                >
                  <td
                    style={{
                      padding: '8px 14px',
                      fontWeight: 500,
                      fontSize: 'var(--font-size-footnote)',
                      width: 140,
                      whiteSpace: 'nowrap' as const,
                    }}
                  >
                    {check.name}
                  </td>
                  <td
                    style={{
                      padding: '8px 14px',
                      fontSize: 'var(--font-size-caption)',
                      color: 'var(--color-label-secondary)',
                    }}
                  >
                    {check.detail}
                  </td>
                  <td style={{ padding: '8px 14px', width: 28 }}>
                    <span style={statusDot(check.status)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {report.error && (
        <div style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-tint-red)' }}>
          {report.error}
        </div>
      )}
    </div>
  );
}

function DevServerStatusBar() {
  const health = usePolling<{
    success?: boolean;
    checks?: Record<string, { name: string; status: string; latencyMs: number }>;
  }>(`${DEV_SERVER}/api/health-check`, 8000, {});
  const metro = usePolling<{
    success?: boolean;
    metro?: { running: boolean; url: string; status: string; latencyMs: number };
  }>(`${DEV_SERVER}/api/expo/status`, 8000, {});
  const isOnline = !!health.checks;
  const checks = health.checks ?? {};

  if (!isOnline) {
    return (
      <div
        style={{
          background: 'var(--color-tint-red)',
          color: 'var(--color-static-white)',
          padding: '12px 20px',
          borderRadius: 12,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontSize: 'var(--font-size-body)',
          fontWeight: 600,
        }}
      >
        <span style={{ fontSize: 20 }}>!</span>
        <span>Dev Tools Server is offline</span>
        <code
          style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '2px 8px',
            borderRadius: 6,
            fontSize: 'var(--font-size-caption)',
            fontFamily: 'monospace',
            fontWeight: 400,
          }}
        >
          node tools/dev-server/index.js
        </code>
      </div>
    );
  }

  const items = Object.values(checks);
  const metroData = metro.metro;

  // All services except dev_server (that's us)
  const barItems = Object.entries(checks)
    .filter(([k]) => k !== 'dev_server')
    .map(([, v]) => v);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 14px',
        marginBottom: 24,
        padding: '8px 14px',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border-opaque)',
        borderRadius: 12,
      }}
    >
      {barItems.map((check) => {
        const color =
          check.status === 'online'
            ? 'var(--color-tint-green)'
            : check.status === 'degraded'
              ? 'var(--color-tint-orange)'
              : 'var(--color-tint-red)';
        return (
          <div
            key={check.name}
            style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' as const }}
          >
            <span
              style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }}
            />
            <span
              style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-tertiary)' }}
            >
              {check.name}
            </span>
            <span style={{ fontSize: 'var(--font-size-caption)', fontWeight: 600, color }}>
              {check.latencyMs}ms
            </span>
          </div>
        );
      })}
      {metroData && (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' as const }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: metroData.running ? 'var(--color-tint-green)' : 'var(--color-tint-red)',
              flexShrink: 0,
            }}
          />
          <span
            style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-tertiary)' }}
          >
            Metro
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-caption)',
              fontWeight: 600,
              color: metroData.running ? 'var(--color-tint-green)' : 'var(--color-tint-red)',
            }}
          >
            {metroData.running ? `${metroData.latencyMs}ms` : 'Off'}
          </span>
        </div>
      )}
    </div>
  );
}

function ProcessLogs({ toolName, onClose }: { toolName: string; onClose: () => void }) {
  const data = usePolling<{ success?: boolean; lines?: string[]; label?: string }>(
    `${DEV_SERVER}/api/logs/${toolName}`,
    3000,
    {},
  );
  const lines = data.lines ?? [];
  const scrollRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: SSE connection lifecycle
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines.length]);

  const lineColor = (line: string) => {
    if (line.includes('[err]')) return 'var(--color-tint-red)';
    if (line.includes('[system]')) return 'var(--color-label-tertiary)';
    return 'var(--color-label-secondary)';
  };

  return (
    <div style={{ ...card, marginTop: 8, padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          borderBottom: '1px solid var(--color-border-opaque)',
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: 'var(--font-size-footnote)',
            color: 'var(--color-label-primary)',
          }}
        >
          Logs: {data.label || toolName}
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 16,
            color: 'var(--color-label-tertiary)',
          }}
        >
          x
        </button>
      </div>
      <div
        ref={scrollRef}
        style={{
          maxHeight: 300,
          overflow: 'auto',
          padding: '8px 16px',
          background: 'var(--color-fill-primary)',
          fontFamily: 'monospace',
          fontSize: 'var(--font-size-caption)',
          lineHeight: 1.6,
        }}
      >
        {lines.length === 0 && (
          <div style={{ color: 'var(--color-label-quaternary)' }}>No logs yet...</div>
        )}
        {lines.map((line, i) => (
          // Using index as key is intentional here: log lines are append-only and
          // never reordered, so index is a stable and correct key for this list.
          // eslint-disable-next-line react/no-array-index-key
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: log entries have no stable ID
            key={i}
            style={{
              color: lineColor(line),
              whiteSpace: 'pre-wrap' as const,
              wordBreak: 'break-all' as const,
            }}
          >
            {line}
          </div>
        ))}
      </div>
    </div>
  );
}

function SprintProgress() {
  const statusStyle = (s: string) => {
    if (s === 'done')
      return {
        border: 'var(--color-tint-green)',
        bg: 'var(--color-tint-light-green)',
        fg: 'var(--color-tint-green)',
        text: 'Complete',
      };
    if (s === 'active')
      return {
        border: 'var(--color-tint-blue)',
        bg: 'var(--color-tint-light-blue)',
        fg: 'var(--color-tint-blue)',
        text: 'In Progress',
      };
    if (s === 'next')
      return {
        border: 'var(--color-tint-orange)',
        bg: 'var(--color-tint-light-orange)',
        fg: 'var(--color-tint-orange)',
        text: 'Next',
      };
    return {
      border: 'var(--color-fill-primary)',
      bg: 'var(--color-tint-light-blue)',
      fg: 'var(--color-tint-blue)',
      text: 'Planned',
    };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {SPRINTS.map((sprint) => {
        const ss = statusStyle(sprint.status);
        return (
          <div
            key={sprint.name}
            style={{
              ...card,
              padding: '12px 16px',
              borderLeft: `3px solid ${ss.border}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span
                style={{
                  fontSize: 12,
                  ...badge(ss.bg, ss.fg),
                }}
              >
                {ss.text}
              </span>
              <div style={{ fontWeight: 600 }}>{sprint.name}</div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {sprint.items.map((item) => (
                <span
                  key={item}
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    padding: '2px 8px',
                    background: 'var(--color-fill-quaternary)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-label-secondary)',
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [text]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      style={{
        padding: '2px 8px',
        background: copied ? 'var(--color-tint-light-green)' : 'var(--color-fill-secondary)',
        color: copied ? 'var(--color-tint-green)' : 'var(--color-label-tertiary)',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-caption)',
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap' as const,
        transition: 'all 0.15s',
        minHeight: 24,
      }}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

function DevCommands() {
  const commands = [
    { cmd: 'pnpm --filter web dev', desc: 'Web dev server (port 3000)' },
    { cmd: 'pnpm --filter web storybook', desc: 'Storybook (port 6006)' },
    { cmd: 'pnpm --filter web build', desc: 'Production build' },
    { cmd: 'pnpm --filter web build-storybook', desc: 'Build Storybook static' },
    { cmd: 'pnpm --filter @zoobook/desktop dev', desc: 'Electron dev (requires web dev)' },
    { cmd: 'pnpm --filter @zoobook/desktop dist:mac', desc: 'Build macOS .dmg' },
    { cmd: 'pnpm --filter @zoobook/desktop dist:win', desc: 'Build Windows .exe' },
    { cmd: 'pnpm --filter @zoobook/web exec tsc --noEmit', desc: 'TypeScript check' },
    { cmd: 'pnpm --filter @zoobook/mobile start', desc: 'Mobile — Expo dev server' },
    { cmd: 'pnpm --filter @zoobook/mobile ios', desc: 'Mobile — run on iOS Simulator' },
    { cmd: 'pnpm --filter @zoobook/mobile android', desc: 'Mobile — run on Android Emulator' },
    {
      cmd: 'cd packages/mobile && eas build --platform ios --profile development',
      desc: 'Mobile — EAS dev build (iOS)',
    },
    {
      cmd: 'cd packages/mobile && eas build --platform android --profile development',
      desc: 'Mobile — EAS dev build (Android)',
    },
  ];

  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      {commands.map((row, i) => (
        <div
          key={row.cmd}
          style={{
            display: 'flex',
            gap: 12,
            padding: '8px 16px',
            alignItems: 'center',
            borderBottom:
              i < commands.length - 1 ? '1px solid var(--color-border-transparent)' : 'none',
          }}
        >
          <code
            style={{
              flex: 1,
              fontSize: 'var(--font-size-caption)',
              fontFamily: 'monospace',
              color: 'var(--color-tint-blue)',
            }}
          >
            {row.cmd}
          </code>
          <span
            style={{
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-label-tertiary)',
              whiteSpace: 'nowrap',
            }}
          >
            {row.desc}
          </span>
          <CopyButton text={row.cmd} />
        </div>
      ))}
    </div>
  );
}

// ─── Staging Import Panel ─────────────────────────────────────────────────────

interface StagingImportRun {
  id: number;
  status: string;
  conclusion: string | null;
  createdAt: string;
  updatedAt: string;
  htmlUrl: string;
  triggerEvent: string;
  actor: string;
  runNumber: number;
}

interface StagingImportStatus {
  success?: boolean;
  status?: string;
  latest?: StagingImportRun | null;
  runs?: StagingImportRun[];
  error?: string;
}

const STAGING_SOURCES = [
  { value: 'hoquwjhxocpejqppszwy', label: 'Zoo Dev' },
  { value: 'clear_data', label: 'Clear Data' },
] as const;

function StagingImportPanel() {
  const data = usePolling<StagingImportStatus>(
    `${DEV_SERVER}/api/staging-import/status`,
    30000,
    {},
  );
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState<string | null>(null);
  const [source, setSource] = useState<string>(STAGING_SOURCES[0].value);

  const isClearData = source === 'clear_data';

  const handleTrigger = useCallback(async () => {
    if (
      isClearData &&
      !window.confirm('Clear ALL staging data (Zoo DB + Platform DB)? This cannot be undone.')
    ) {
      return;
    }
    setTriggering(true);
    setTriggerResult(null);
    try {
      const resp = await fetch(`${DEV_SERVER}/api/staging-import/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isClearData ? { mode: 'clear_data' } : { sourceProject: source }),
      });
      const result = await resp.json();
      if (result.success) {
        setTriggerResult(
          isClearData
            ? 'Clear data workflow triggered — check GitHub Actions for progress.'
            : 'Import workflow triggered — check GitHub Actions for progress.',
        );
      } else {
        setTriggerResult(`Failed: ${result.error}`);
      }
    } catch {
      setTriggerResult('Request failed — is the dev server running?');
    }
    setTriggering(false);
  }, [source, isClearData]);

  const statusColor = (s?: string) => {
    switch (s) {
      case 'success':
        return 'var(--color-tint-green)';
      case 'running':
        return 'var(--color-tint-orange)';
      case 'failed':
        return 'var(--color-tint-red)';
      case 'cancelled':
        return 'var(--color-label-tertiary)';
      default:
        return 'var(--color-label-quaternary)';
    }
  };

  const statusLabel = (s?: string) => {
    switch (s) {
      case 'success':
        return 'Last import succeeded';
      case 'running':
        return 'Import in progress...';
      case 'failed':
        return 'Last import failed';
      case 'cancelled':
        return 'Last import cancelled';
      case 'not_configured':
        return 'Workflow not configured on remote';
      default:
        return 'No import runs yet';
    }
  };

  const formatRelativeTime = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const latest = data.latest;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Status card */}
      <div
        style={{
          ...card,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {/* Status dot */}
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: statusColor(data.status),
            flexShrink: 0,
            animation: data.status === 'running' ? 'pulse 2s infinite' : undefined,
          }}
        />

        {/* Status text */}
        <span style={{ fontWeight: 500, fontSize: 'var(--font-size-body)' }}>
          {statusLabel(data.status)}
        </span>

        {latest?.createdAt && (
          <span
            style={{
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-label-secondary)',
            }}
          >
            {formatRelativeTime(latest.updatedAt || latest.createdAt)}
            {latest.triggerEvent === 'schedule' ? ' (cron)' : ` (${latest.actor})`}
          </span>
        )}

        <span style={{ flex: 1 }} />

        {/* Trigger button */}
        <button
          type="button"
          onClick={handleTrigger}
          disabled={triggering || data.status === 'running'}
          style={{
            ...gitBtnPrimary,
            padding: '4px 14px',
            minHeight: 28,
            opacity: triggering || data.status === 'running' ? 0.6 : 1,
            cursor: triggering || data.status === 'running' ? 'wait' : 'pointer',
          }}
        >
          {triggering ? 'Triggering...' : data.status === 'running' ? 'Running...' : 'Import Now'}
        </button>
      </div>

      {/* Trigger result message */}
      {triggerResult && (
        <div
          style={{
            fontSize: 'var(--font-size-caption)',
            color: triggerResult.startsWith('Failed')
              ? 'var(--color-tint-red)'
              : 'var(--color-tint-green)',
            padding: '4px 0',
          }}
        >
          {triggerResult}
        </div>
      )}

      {/* Recent runs table */}
      {data.runs && data.runs.length > 0 && (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr
                style={{
                  borderBottom: '1px solid var(--color-border-opaque)',
                  background: 'var(--color-fill-quaternary)',
                }}
              >
                <th
                  style={{
                    padding: '6px 14px',
                    textAlign: 'left',
                    fontSize: 'var(--font-size-caption)',
                    fontWeight: 600,
                    color: 'var(--color-label-secondary)',
                  }}
                >
                  Run
                </th>
                <th
                  style={{
                    padding: '6px 14px',
                    textAlign: 'left',
                    fontSize: 'var(--font-size-caption)',
                    fontWeight: 600,
                    color: 'var(--color-label-secondary)',
                  }}
                >
                  Status
                </th>
                <th
                  style={{
                    padding: '6px 14px',
                    textAlign: 'left',
                    fontSize: 'var(--font-size-caption)',
                    fontWeight: 600,
                    color: 'var(--color-label-secondary)',
                  }}
                >
                  Trigger
                </th>
                <th
                  style={{
                    padding: '6px 14px',
                    textAlign: 'left',
                    fontSize: 'var(--font-size-caption)',
                    fontWeight: 600,
                    color: 'var(--color-label-secondary)',
                  }}
                >
                  When
                </th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((run) => (
                <tr
                  key={run.id}
                  style={{ borderBottom: '1px solid var(--color-border-transparent)' }}
                >
                  <td style={{ padding: '8px 14px', fontSize: 'var(--font-size-footnote)' }}>
                    <a
                      href={run.htmlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--color-tint-blue)', textDecoration: 'none' }}
                    >
                      #{run.runNumber}
                    </a>
                  </td>
                  <td style={{ padding: '8px 14px', fontSize: 'var(--font-size-footnote)' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: statusColor(
                            run.status === 'in_progress' || run.status === 'queued'
                              ? 'running'
                              : run.conclusion === 'success'
                                ? 'success'
                                : run.conclusion === 'failure'
                                  ? 'failed'
                                  : 'cancelled',
                          ),
                        }}
                      />
                      {run.conclusion || run.status}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '8px 14px',
                      fontSize: 'var(--font-size-footnote)',
                      color: 'var(--color-label-secondary)',
                    }}
                  >
                    {run.triggerEvent === 'schedule' ? 'cron' : run.actor}
                  </td>
                  <td
                    style={{
                      padding: '8px 14px',
                      fontSize: 'var(--font-size-footnote)',
                      color: 'var(--color-label-secondary)',
                    }}
                  >
                    {formatRelativeTime(run.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Info footer */}
      <div
        style={{
          fontSize: 'var(--font-size-caption)',
          color: 'var(--color-label-tertiary)',
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <span>Nightly at 3:00 CET (2:00 UTC)</span>
        <span>Zoo DB + Platform DB sync</span>
        <span>Source: Zoo Dev (hoquwjhxocpejqppszwy)</span>
        <a
          href="https://github.com/artusculik/zoobook/actions/workflows/staging-import.yml"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--color-tint-blue)', textDecoration: 'none' }}
        >
          View all runs
        </a>
      </div>

      {data.error && (
        <div style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-tint-red)' }}>
          {data.error}
        </div>
      )}
    </div>
  );
}

// ─── Deploy Pipeline ──────────────────────────────────────────────────────────

// Product targets — user picks WHAT to ship, not HOW.
// Backend (DB migrations, PostGraphile, Edge Functions) is always included
// when deploying Web. The pipeline handles the correct order automatically.
const PRODUCT_TARGETS = [
  {
    key: 'web',
    label: 'Web',
    sub: 'app.zoobook.online',
    pipeline: ['db-migrations', 'postgraphile', 'edge-functions', 'web'],
  },
  { key: 'desktop', label: 'Desktop', sub: 'Electron Build', pipeline: ['desktop'] },
  { key: 'ios', label: 'iOS', sub: 'App Store', pipeline: ['mobile-ios'] },
  { key: 'android', label: 'Android', sub: 'Play Store', pipeline: ['mobile-android'] },
] as const;

type ProductKey = (typeof PRODUCT_TARGETS)[number]['key'];

interface PipelineStep {
  id: string;
  label: string;
  target: string;
  name?: string;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  duration?: number;
  error?: string;
}

interface PipelineData {
  success?: boolean;
  state?: 'idle' | 'running' | 'done' | 'failed';
  steps?: PipelineStep[];
  startedAt?: number | string;
  environment?: string;
  targets?: string[];
  error?: string;
}

interface DeployAnalysis {
  success?: boolean;
  analysis?:
    | string
    | {
        summary?: string;
        affectedPlatforms?: string[];
        risk?: string;
        confidence?: number;
        recommendedTargets?: string[];
        reasoning?: string;
      };
  hasApiKey?: boolean;
}

interface DeployHistoryEntry {
  id: string;
  targets: string[];
  environment: string;
  state: string;
  startedAt: number;
  finishedAt?: number;
  duration?: number;
  steps?: PipelineStep[];
}

interface DeployLogEntry {
  target: string;
  line: string;
  level: string;
  timestamp: number;
}

function useDeployPipeline(): PipelineData {
  const [data, setData] = useState<PipelineData>({});
  const visibleRef = useRef(true);

  useEffect(() => {
    const onVis = () => {
      visibleRef.current = document.visibilityState === 'visible';
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    let active = true;
    async function poll() {
      if (!visibleRef.current) return;
      try {
        const resp = await fetch(`${DEV_SERVER}/api/deploy/pipeline`, {
          signal: AbortSignal.timeout(5000),
        });
        if (resp.ok && active) setData(await resp.json());
      } catch {
        /* dev server not running */
      }
    }
    poll();
    // Poll fast when running, slow when idle
    const id = setInterval(
      () => {
        poll();
      },
      data.state === 'running' ? 1000 : 5000,
    );
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [data.state]);

  return data;
}

function useDeployLogs() {
  const [logs, setLogs] = useState<DeployLogEntry[]>([]);

  useEffect(() => {
    let es: EventSource | null = null;
    try {
      es = new EventSource(`${DEV_SERVER}/api/deploy/logs`);
      es.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed.type === 'log') {
            setLogs((prev) => [...prev.slice(-500), parsed.data]);
          }
        } catch {
          /* ignore parse errors */
        }
      };
    } catch {
      /* SSE not available */
    }
    return () => {
      if (es) es.close();
    };
  }, []);

  const clearLogs = useCallback(() => setLogs([]), []);

  return { logs, clearLogs };
}

function useDeployAnalysis() {
  const [analysis, setAnalysis] = useState<DeployAnalysis>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const resp = await fetch(`${DEV_SERVER}/api/deploy-ai/analyze`, {
          signal: AbortSignal.timeout(40000),
        });
        if (resp.ok && active) setAnalysis(await resp.json());
      } catch {
        /* no AI available */
      }
      if (active) setLoading(false);
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  return { analysis, loading };
}

function getAnalysisText(a: DeployAnalysis['analysis']): string {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return a.summary || a.reasoning || '';
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function formatElapsed(startedAt: number | string): string {
  const ts = typeof startedAt === 'string' ? new Date(startedAt).getTime() : startedAt;
  return formatDuration(Date.now() - ts);
}

// ─── Release Management ──────────────────────────────────────────────────────

interface ReleaseChannelInfo {
  version: string;
  releasedAt: string;
  releaseNotes?: string;
}

interface ReleaseMetadata {
  latestStable: string | null;
  latestBeta: string | null;
  latestCanary: string | null;
  globalMinVersion: string | null;
  rollbackTo: string | null;
  channels: {
    stable: ReleaseChannelInfo | null;
    beta: ReleaseChannelInfo | null;
    canary: ReleaseChannelInfo | null;
    staging: ReleaseChannelInfo | null;
  };
  updatedAt?: string;
}

function useReleaseMetadata() {
  const [metadata, setMetadata] = useState<ReleaseMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${DEV_SERVER}/api/releases/metadata`);
      const data = await resp.json();
      if (data.success) {
        setMetadata(data.metadata);
      } else {
        setError(data.error || 'Failed to fetch metadata');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { metadata, loading, error, refresh };
}

/**
 * Release Management section for the Dev Dashboard.
 * Shows current versions per channel, rollback controls, and force-update input.
 */
function ReleaseManagementSection() {
  const { metadata, loading, error, refresh } = useReleaseMetadata();
  const [rollbackChannel, setRollbackChannel] = useState('stable');
  const [rollbackVersion, setRollbackVersion] = useState('');
  const [forceMinVersion, setForceMinVersion] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const clearResult = useCallback(() => {
    setTimeout(() => setActionResult(null), 5000);
  }, []);

  const handleRollback = useCallback(async () => {
    if (!rollbackVersion.trim()) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      const resp = await fetch(`${DEV_SERVER}/api/releases/rollback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel: rollbackChannel, version: rollbackVersion.trim() }),
      });
      const data = await resp.json();
      setActionResult({ ok: resp.ok, msg: data.message || data.error || 'Unknown result' });
      if (resp.ok) {
        setRollbackVersion('');
        refresh();
      }
    } catch (err: unknown) {
      setActionResult({ ok: false, msg: err instanceof Error ? err.message : 'Network error' });
    } finally {
      setActionLoading(false);
      clearResult();
    }
  }, [rollbackChannel, rollbackVersion, refresh, clearResult]);

  const handleForceUpdate = useCallback(async () => {
    if (!forceMinVersion.trim()) return;
    setActionLoading(true);
    setActionResult(null);
    try {
      const resp = await fetch(`${DEV_SERVER}/api/releases/force-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minVersion: forceMinVersion.trim() }),
      });
      const data = await resp.json();
      setActionResult({ ok: resp.ok, msg: data.message || data.error || 'Unknown result' });
      if (resp.ok) {
        setForceMinVersion('');
        refresh();
      }
    } catch (err: unknown) {
      setActionResult({ ok: false, msg: err instanceof Error ? err.message : 'Network error' });
    } finally {
      setActionLoading(false);
      clearResult();
    }
  }, [forceMinVersion, refresh, clearResult]);

  const handleClearRollback = useCallback(async () => {
    setActionLoading(true);
    setActionResult(null);
    try {
      const resp = await fetch(`${DEV_SERVER}/api/releases/clear-rollback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      setActionResult({ ok: resp.ok, msg: data.message || data.error || 'Unknown result' });
      if (resp.ok) refresh();
    } catch (err: unknown) {
      setActionResult({ ok: false, msg: err instanceof Error ? err.message : 'Network error' });
    } finally {
      setActionLoading(false);
      clearResult();
    }
  }, [refresh, clearResult]);

  if (loading && !metadata) {
    return <p style={labelSecondary}>Loading release metadata...</p>;
  }

  if (error && !metadata) {
    return (
      <div style={{ ...card, borderColor: 'var(--color-tint-red)' }}>
        <p style={{ color: 'var(--color-tint-red)', margin: 0 }}>
          Failed to load metadata: {error}
        </p>
        <button type="button" onClick={refresh} style={{ ...actionBtn, marginTop: 8 }}>
          Retry
        </button>
      </div>
    );
  }

  const channels: Array<{ key: string; label: string; color: string }> = [
    { key: 'stable', label: 'Stable', color: 'var(--color-tint-green)' },
    { key: 'beta', label: 'Beta', color: 'var(--color-tint-orange)' },
    { key: 'canary', label: 'Canary', color: 'var(--color-tint-purple)' },
    { key: 'staging', label: 'Staging', color: 'var(--color-label-tertiary)' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Channel versions grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {channels.map(({ key, label, color }) => {
          const ch = metadata?.channels?.[key as keyof typeof metadata.channels];
          return (
            <div key={key} style={{ ...card, padding: 16, textAlign: 'center' }}>
              <div
                style={{
                  ...badge(color, '#fff'),
                  marginBottom: 8,
                }}
              >
                {label}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-title3)',
                  fontWeight: 700,
                  color: ch ? 'var(--color-label-primary)' : 'var(--color-label-quaternary)',
                }}
              >
                {ch ? `v${ch.version}` : 'N/A'}
              </div>
              {ch?.releasedAt && (
                <div
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-label-tertiary)',
                    marginTop: 4,
                  }}
                >
                  {new Date(ch.releasedAt).toLocaleDateString('cs-CZ', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Active states: rollback and force-update indicators */}
      {(metadata?.rollbackTo || metadata?.globalMinVersion) && (
        <div
          style={{
            ...card,
            borderColor: 'var(--color-tint-orange)',
            background:
              'color-mix(in srgb, var(--color-tint-orange) 8%, var(--color-bg-secondary))',
          }}
        >
          <div style={{ ...sectionTitle, fontSize: 'var(--font-size-body)', marginBottom: 8 }}>
            Active Overrides
          </div>
          {metadata?.rollbackTo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={badge('var(--color-tint-orange)', '#fff')}>ROLLBACK</span>
              <span style={labelSecondary}>
                All clients will downgrade to v{metadata.rollbackTo}
              </span>
              <button
                type="button"
                onClick={handleClearRollback}
                disabled={actionLoading}
                style={actionBtnSmall}
              >
                Clear
              </button>
            </div>
          )}
          {metadata?.globalMinVersion && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={badge('var(--color-tint-red)', '#fff')}>FORCE UPDATE</span>
              <span style={labelSecondary}>
                Clients below v{metadata.globalMinVersion} are blocked
              </span>
            </div>
          )}
        </div>
      )}

      {/* Rollback controls */}
      <div style={card}>
        <div style={{ ...sectionTitle, fontSize: 'var(--font-size-body)', marginBottom: 12 }}>
          Emergency Rollback
        </div>
        <p
          style={{
            ...labelSecondary,
            fontSize: 'var(--font-size-caption)',
            marginTop: 0,
            marginBottom: 12,
          }}
        >
          Set rollbackTo to force all clients on a channel to downgrade. Versioned artifacts must
          already exist in R2 (uploaded during CI or local+R2 deploy).
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span
              style={{
                fontSize: 'var(--font-size-caption)',
                color: 'var(--color-label-secondary)',
              }}
            >
              Channel
            </span>
            <select
              value={rollbackChannel}
              onChange={(e) => setRollbackChannel(e.target.value)}
              style={selectInput}
            >
              <option value="stable">Stable</option>
              <option value="beta">Beta</option>
              <option value="canary">Canary</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <span
              style={{
                fontSize: 'var(--font-size-caption)',
                color: 'var(--color-label-secondary)',
              }}
            >
              Target version
            </span>
            <input
              type="text"
              value={rollbackVersion}
              onChange={(e) => setRollbackVersion(e.target.value)}
              placeholder="e.g. 0.0.1"
              style={textInput}
            />
          </label>
          <button
            type="button"
            onClick={handleRollback}
            disabled={actionLoading || !rollbackVersion.trim()}
            style={{
              ...actionBtn,
              background: 'var(--color-tint-orange)',
              color: '#fff',
              opacity: actionLoading || !rollbackVersion.trim() ? 0.5 : 1,
            }}
          >
            {actionLoading ? 'Setting...' : 'Rollback'}
          </button>
        </div>
      </div>

      {/* Force update controls */}
      <div style={card}>
        <div style={{ ...sectionTitle, fontSize: 'var(--font-size-body)', marginBottom: 12 }}>
          Force Update (globalMinVersion)
        </div>
        <p
          style={{
            ...labelSecondary,
            fontSize: 'var(--font-size-caption)',
            marginTop: 0,
            marginBottom: 12,
          }}
        >
          Set the minimum version required. Clients below this version will be blocked from using
          the app until they update. Use with caution.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <span
              style={{
                fontSize: 'var(--font-size-caption)',
                color: 'var(--color-label-secondary)',
              }}
            >
              Minimum version
            </span>
            <input
              type="text"
              value={forceMinVersion}
              onChange={(e) => setForceMinVersion(e.target.value)}
              placeholder="e.g. 1.0.0"
              style={textInput}
            />
          </label>
          <button
            type="button"
            onClick={handleForceUpdate}
            disabled={actionLoading || !forceMinVersion.trim()}
            style={{
              ...actionBtn,
              background: 'var(--color-tint-red)',
              color: '#fff',
              opacity: actionLoading || !forceMinVersion.trim() ? 0.5 : 1,
            }}
          >
            {actionLoading ? 'Setting...' : 'Set Min Version'}
          </button>
        </div>
      </div>

      {/* Action result feedback */}
      {actionResult && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 'var(--font-size-caption)',
            background: actionResult.ok
              ? 'color-mix(in srgb, var(--color-tint-green) 12%, var(--color-bg-secondary))'
              : 'color-mix(in srgb, var(--color-tint-red) 12%, var(--color-bg-secondary))',
            color: actionResult.ok ? 'var(--color-tint-green)' : 'var(--color-tint-red)',
          }}
        >
          {actionResult.msg}
        </div>
      )}

      {/* Refresh + last updated */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span
          style={{ fontSize: 'var(--font-size-caption)', color: 'var(--color-label-quaternary)' }}
        >
          {metadata?.updatedAt
            ? `Last updated: ${new Date(metadata.updatedAt).toLocaleString('cs-CZ')}`
            : ''}
        </span>
        <button type="button" onClick={refresh} disabled={loading} style={actionBtnSmall}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}

const actionBtn: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 8,
  border: 'none',
  fontSize: 'var(--font-size-caption)',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const actionBtnSmall: CSSProperties = {
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid var(--color-border-opaque)',
  background: 'transparent',
  fontSize: 'var(--font-size-caption)',
  color: 'var(--color-label-secondary)',
  cursor: 'pointer',
};

const textInput: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--color-border-opaque)',
  background: 'var(--color-bg-primary)',
  fontSize: 'var(--font-size-body)',
  color: 'var(--color-label-primary)',
  outline: 'none',
};

const selectInput: CSSProperties = {
  ...textInput,
  minWidth: 100,
};

// ─── Deploy Pipeline ──────────────────────────────────────────────────────────

function DeploySection() {
  const pipeline = useDeployPipeline();
  const { logs, clearLogs } = useDeployLogs();
  const { analysis } = useDeployAnalysis();
  const devToolsOnline = pipeline.state != null;
  const [selected, setSelected] = useState<Set<ProductKey>>(new Set(['web']));
  const [localTargets, setLocalTargets] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('zb-deploy-local-targets');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [environment, setEnvironment] = useState<'staging' | 'production'>('staging');
  const [deploying, setDeploying] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [desktopPublishMode, setDesktopPublishMode] = useState<'local-only' | 'local+r2' | 'cloud'>(
    'local-only',
  );
  const [history, setHistory] = useState<DeployHistoryEntry[]>([]);
  const [elapsed, setElapsed] = useState('');
  const [stepElapsed, setStepElapsed] = useState('');
  const logsRef = useRef<HTMLDivElement>(null);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stepStartRef = useRef<{ id: string; startedAt: number } | null>(null);

  const pipelineState = pipeline.state ?? 'idle';

  // Expand product keys to infrastructure pipeline targets (deduplicated)
  const infraTargets = useMemo(() => {
    const result = new Set<string>();
    for (const key of selected) {
      const product = PRODUCT_TARGETS.find((p) => p.key === key);
      // biome-ignore lint/complexity/noForEach: forEach on small array is readable here
      if (product) product.pipeline.forEach((t) => result.add(t));
    }
    return [...result];
  }, [selected]);

  // Step duration estimates: average from successful history entries (id → ms)
  const stepEstimates = useMemo((): Record<string, number> => {
    const sums: Record<string, { total: number; count: number }> = {};
    for (const entry of history) {
      if (!entry?.steps) continue;
      for (const s of entry.steps) {
        if (s.duration != null && s.duration > 0 && s.id && s.status === 'done') {
          if (!sums[s.id]) sums[s.id] = { total: 0, count: 0 };
          sums[s.id].total += s.duration;
          sums[s.id].count += 1;
        }
      }
    }
    const map: Record<string, number> = {};
    for (const [id, { total, count }] of Object.entries(sums)) {
      map[id] = Math.round(total / count);
    }
    return map;
  }, [history]);

  // Total estimated duration for currently selected targets (sum of step estimates)
  const totalEstimate = useMemo((): number | null => {
    if (Object.keys(stepEstimates).length === 0) return null;
    let total = 0;
    let hasMatch = false;
    // Find a successful history entry matching current targets to get step list
    for (const entry of history) {
      if (entry.state !== 'done' || !entry.steps) continue;
      const entryTargets = new Set(entry.targets);
      const match = infraTargets.every((t) => entryTargets.has(t));
      if (match) {
        for (const s of entry.steps) {
          if (stepEstimates[s.id]) {
            total += stepEstimates[s.id];
            hasMatch = true;
          }
        }
        break;
      }
    }
    return hasMatch ? total : null;
  }, [stepEstimates, history, infraTargets]);

  // Fetch deploy history on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: polling with stable refs
  useEffect(() => {
    async function loadHistory() {
      try {
        const resp = await fetch(`${DEV_SERVER}/api/deploy/history`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.history) setHistory(data.history);
        }
      } catch {
        /* ignore */
      }
    }
    loadHistory();
  }, [pipelineState]);

  // Elapsed timer
  useEffect(() => {
    if (pipelineState === 'running' && pipeline.startedAt) {
      const tick = () => setElapsed(formatElapsed(pipeline.startedAt ?? Date.now()));
      tick();
      elapsedRef.current = setInterval(tick, 1000);
      return () => {
        if (elapsedRef.current) clearInterval(elapsedRef.current);
      };
    }
    setElapsed('');
  }, [pipelineState, pipeline.startedAt]);

  // Per-step elapsed timer — uses backend startedAt for accuracy across refreshes
  const runningStep = pipeline.steps?.find((s) => s.status === 'running');
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional: polling with stable refs
  useEffect(() => {
    if (!runningStep) {
      setStepElapsed('');
      stepStartRef.current = null;
      return;
    }
    // Use backend startedAt if available, fall back to Date.now() for first render
    const stepStart = (runningStep as { startedAt?: number }).startedAt || Date.now();
    if (!stepStartRef.current || stepStartRef.current.id !== runningStep.id) {
      stepStartRef.current = { id: runningStep.id, startedAt: stepStart };
    }
    const ref = stepStartRef.current;
    const tick = () => setStepElapsed(formatDuration(Date.now() - ref.startedAt));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [runningStep?.id]);

  // Auto-scroll logs
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on new log entries
  useEffect(() => {
    if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight;
  }, [logs.length]);

  // Sync deploying state with pipeline
  useEffect(() => {
    if (pipelineState !== 'running') setDeploying(false);
  }, [pipelineState]);

  const toggleProduct = useCallback((key: ProductKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Cloud targets that support local builds (Desktop = macOS only, iOS/Android = EAS --local)
  const LOCALLY_BUILDABLE: Record<string, string> = {
    desktop: 'desktop',
    ios: 'mobile-ios',
    android: 'mobile-android',
  };

  const toggleLocalBuild = useCallback((productKey: string) => {
    setLocalTargets((prev) => {
      const next = new Set(prev);
      const infraKey = LOCALLY_BUILDABLE[productKey];
      if (!infraKey) return prev;
      if (next.has(infraKey)) next.delete(infraKey);
      else next.add(infraKey);
      try {
        localStorage.setItem('zb-deploy-local-targets', JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const handleDeploy = useCallback(async () => {
    if (infraTargets.length === 0) return;
    setDeploying(true);
    clearLogs();
    setShowLogs(true);
    const activeLocalTargets = infraTargets.filter((t) => localTargets.has(t));
    try {
      await fetch(`${DEV_SERVER}/api/deploy/all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targets: infraTargets,
          environment,
          ...(activeLocalTargets.length > 0 ? { localTargets: activeLocalTargets } : {}),
          publishMode: desktopPublishMode,
        }),
      });
    } catch {
      setDeploying(false);
    }
  }, [infraTargets, environment, clearLogs, localTargets, desktopPublishMode]);

  const handleOta = useCallback(async () => {
    setDeploying(true);
    clearLogs();
    setShowLogs(true);
    try {
      await fetch(`${DEV_SERVER}/api/deploy/all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targets: ['ota-update'], environment }),
      });
    } catch {
      setDeploying(false);
    }
  }, [environment, clearLogs]);

  const handleRebuildDesktop = useCallback(async () => {
    setDeploying(true);
    clearLogs();
    setShowLogs(true);
    try {
      await fetch(`${DEV_SERVER}/api/deploy/all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targets: ['desktop'],
          environment,
          localTargets: ['desktop'],
          publishMode: desktopPublishMode,
        }),
      });
    } catch {
      setDeploying(false);
    }
  }, [environment, clearLogs, desktopPublishMode]);

  const handleCancel = useCallback(async () => {
    try {
      await fetch(`${DEV_SERVER}/api/deploy/stop`, { method: 'POST' });
    } catch {
      /* ignore */
    }
  }, []);

  const logLineColor = (level: string) => {
    if (level === 'error') return 'var(--color-tint-red)';
    if (level === 'warn') return 'var(--color-tint-orange)';
    if (level === 'system') return 'var(--color-label-quaternary)';
    return 'var(--color-label-secondary)';
  };

  const stepIcon = (status: PipelineStep['status']) => {
    if (status === 'pending') return { symbol: '\u25CB', color: 'var(--color-label-quaternary)' };
    if (status === 'running') return { symbol: '\u25CF', color: 'var(--color-tint-green)' };
    if (status === 'done') return { symbol: '\u2713', color: 'var(--color-tint-green)' };
    if (status === 'failed') return { symbol: '\u2715', color: 'var(--color-tint-red)' };
    return { symbol: '\u2192', color: 'var(--color-label-quaternary)' };
  };

  // ---- RUNNING VIEW ----
  if (pipelineState === 'running') {
    const steps = pipeline.steps ?? [];
    const doneCount = steps.filter((s) => s.status === 'done').length;
    const progress = steps.length > 0 ? (doneCount / steps.length) * 100 : 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Progress header */}
        <div style={{ ...card, padding: 16 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: 'var(--color-tint-green)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
              <span style={{ fontWeight: 600, color: 'var(--color-label-primary)' }}>
                Deploying to {environment}
              </span>
              {elapsed && (
                <span
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-label-tertiary)',
                    fontFamily: 'monospace',
                  }}
                >
                  {elapsed}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: '6px 14px',
                background: 'var(--color-tint-red)',
                color: 'var(--color-static-white)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                fontSize: 'var(--font-size-footnote)',
                cursor: 'pointer',
                minHeight: 32,
              }}
            >
              Cancel
            </button>
          </div>

          {/* Progress bar */}
          <div
            style={{
              height: 4,
              background: 'var(--color-fill-quaternary)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background: 'var(--color-tint-green)',
                borderRadius: 2,
                transition: 'width 0.5s ease',
              }}
            />
          </div>
        </div>

        {/* Steps */}
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          {steps.map((step, i) => {
            const icon = stepIcon(step.status);
            const isRunning = step.status === 'running';
            const isPending = step.status === 'pending';
            const estimate = stepEstimates[step.id];
            const stepLogs = isRunning
              ? logs.filter((l) => l.target === step.target || l.target === step.id).slice(-5)
              : [];
            return (
              <div
                key={step.label || step.name}
                style={{
                  borderBottom:
                    i < steps.length - 1 ? '1px solid var(--color-border-transparent)' : 'none',
                  background: isRunning ? 'var(--color-fill-quaternary)' : 'transparent',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px' }}
                >
                  <span
                    style={{
                      fontSize: 16,
                      color: icon.color,
                      fontWeight: 700,
                      width: 20,
                      textAlign: 'center',
                      flexShrink: 0,
                      ...(isRunning ? { animation: 'pulse 1.5s ease-in-out infinite' } : {}),
                    }}
                  >
                    {icon.symbol}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontWeight: isRunning ? 600 : 400,
                      fontSize: 'var(--font-size-body)',
                      color: isPending
                        ? 'var(--color-label-tertiary)'
                        : 'var(--color-label-primary)',
                    }}
                  >
                    {step.label || step.name}
                  </span>
                  {/* Running: elapsed / ~estimate */}
                  {isRunning && (
                    <span
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        fontFamily: 'monospace',
                        color: 'var(--color-tint-blue)',
                      }}
                    >
                      {stepElapsed || '0s'}
                      {estimate != null && (
                        <span style={{ color: 'var(--color-label-tertiary)' }}>
                          {' '}
                          / ~{formatDuration(estimate)}
                        </span>
                      )}
                    </span>
                  )}
                  {/* Done/failed: actual duration */}
                  {!isRunning && step.duration != null && (
                    <span
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        color: 'var(--color-label-tertiary)',
                        fontFamily: 'monospace',
                      }}
                    >
                      {formatDuration(step.duration)}
                    </span>
                  )}
                  {/* Pending: estimated duration from last deploy */}
                  {isPending && estimate != null && step.duration == null && (
                    <span
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        color: 'var(--color-label-quaternary)',
                        fontFamily: 'monospace',
                        fontStyle: 'italic',
                      }}
                    >
                      ~{formatDuration(estimate)}
                    </span>
                  )}
                  {step.error && (
                    <span
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        color: 'var(--color-tint-red)',
                      }}
                    >
                      {step.error}
                    </span>
                  )}
                </div>
                {isRunning && stepLogs.length > 0 && (
                  <div
                    style={{
                      margin: '0 16px 10px 48px',
                      padding: '6px 10px',
                      background: 'var(--color-fill-tertiary)',
                      borderRadius: 'var(--radius-sm)',
                      fontFamily: 'monospace',
                      fontSize: 11,
                      color: 'var(--color-label-secondary)',
                      lineHeight: 1.5,
                      overflow: 'hidden',
                    }}
                  >
                    {stepLogs.map((l, li) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: log lines have no stable ID
                        key={li}
                        style={{
                          color: l.level === 'error' ? 'var(--color-tint-red)' : undefined,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {l.line}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Live logs */}
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px',
              borderBottom: '1px solid var(--color-border-opaque)',
            }}
          >
            <span
              style={{
                fontWeight: 600,
                fontSize: 'var(--font-size-footnote)',
                color: 'var(--color-label-primary)',
              }}
            >
              Live Logs ({logs.length})
            </span>
            <button
              type="button"
              onClick={() => setShowLogs((v) => !v)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 'var(--font-size-caption)',
                color: 'var(--color-tint-blue)',
                fontWeight: 600,
              }}
            >
              {showLogs ? 'Collapse' : 'Expand'}
            </button>
          </div>
          {showLogs && (
            <div
              ref={logsRef}
              style={{
                maxHeight: 280,
                overflow: 'auto',
                padding: '8px 16px',
                background: 'var(--color-bg-tertiary)',
                fontFamily: 'monospace',
                fontSize: 'var(--font-size-caption)',
                lineHeight: 1.7,
              }}
            >
              {logs.length === 0 && (
                <div style={{ color: 'var(--color-label-quaternary)' }}>Waiting for logs...</div>
              )}
              {logs.map((entry, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: log entries have no stable ID
                  key={i}
                  style={{
                    color: logLineColor(entry.level),
                    whiteSpace: 'pre-wrap' as const,
                    wordBreak: 'break-all' as const,
                  }}
                >
                  <span style={{ color: 'var(--color-label-quaternary)', marginRight: 8 }}>
                    [{entry.target}]
                  </span>
                  {entry.line}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- DONE / FAILED VIEW ----
  if (pipelineState === 'done' || pipelineState === 'failed') {
    const steps = pipeline.steps ?? [];
    const allDone = steps.every((s) => s.status === 'done' || s.status === 'skipped');
    const totalDuration = steps.reduce((sum, s) => sum + (s.duration ?? 0), 0);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Result header */}
        <div
          style={{
            ...card,
            borderColor: allDone ? 'var(--color-tint-green)' : 'var(--color-tint-red)',
            padding: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span
              style={{
                fontSize: 20,
                color: allDone ? 'var(--color-tint-green)' : 'var(--color-tint-red)',
              }}
            >
              {allDone ? '\u2713' : '\u2715'}
            </span>
            <span
              style={{
                fontWeight: 600,
                color: 'var(--color-label-primary)',
                fontSize: 'var(--font-size-title4)',
              }}
            >
              {allDone ? 'Deploy Complete' : 'Deploy Failed'}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-caption)',
                color: 'var(--color-label-tertiary)',
                fontFamily: 'monospace',
              }}
            >
              {formatDuration(totalDuration)}
            </span>
          </div>

          {/* Step results — same layout as running view */}
          <div style={{ ...card, padding: 0, overflow: 'hidden', marginTop: 8 }}>
            {steps.map((step, i) => {
              const icon = stepIcon(step.status);
              return (
                <div
                  key={step.label || step.name || step.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 16px',
                    borderBottom:
                      i < steps.length - 1 ? '1px solid var(--color-border-transparent)' : 'none',
                  }}
                >
                  <span
                    style={{
                      fontSize: 16,
                      color: icon.color,
                      fontWeight: 700,
                      width: 20,
                      textAlign: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {icon.symbol}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontWeight: 400,
                      fontSize: 'var(--font-size-body)',
                      color:
                        step.status === 'skipped'
                          ? 'var(--color-label-tertiary)'
                          : 'var(--color-label-primary)',
                    }}
                  >
                    {step.label || step.name}
                  </span>
                  {step.duration != null && (
                    <span
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        color: 'var(--color-label-tertiary)',
                        fontFamily: 'monospace',
                      }}
                    >
                      {formatDuration(step.duration)}
                    </span>
                  )}
                  {step.error && (
                    <span
                      style={{
                        fontSize: 'var(--font-size-caption)',
                        color: 'var(--color-tint-red)',
                      }}
                    >
                      {step.error}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Summary (if available) */}
        {analysis.analysis && (
          <div
            style={{
              ...card,
              background:
                'linear-gradient(135deg, var(--color-bg-secondary) 0%, var(--color-fill-quaternary) 100%)',
              border: '1px solid var(--color-border-opaque)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 'var(--font-size-body)',
                  color: 'var(--color-label-primary)',
                }}
              >
                AI Summary
              </span>
              <span
                style={{
                  ...badge('var(--color-tint-purple)', 'var(--color-static-white)'),
                  fontSize: 10,
                }}
              >
                Sonnet
              </span>
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-footnote)',
                color: 'var(--color-label-secondary)',
                lineHeight: 1.6,
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
              }}
            >
              {getAnalysisText(analysis.analysis)}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {pipeline.targets?.includes('web') &&
            (() => {
              const isStaging = pipeline.environment === 'staging';
              const webUrl = isStaging
                ? 'https://develop.zoobook-web.pages.dev'
                : 'https://app.zoobook.online';
              const graphiqlUrl = isStaging
                ? 'https://zoobook-staging.fly.dev/graphiql'
                : 'https://zoobook.fly.dev/graphiql';
              const linkStyle = {
                padding: '8px 16px',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600 as const,
                fontSize: 'var(--font-size-footnote)',
                textDecoration: 'none',
                minHeight: 36,
                display: 'inline-flex' as const,
                alignItems: 'center' as const,
              };
              return (
                <>
                  <a
                    href={webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...linkStyle,
                      background: 'var(--color-tint-blue)',
                      color: 'var(--color-static-white)',
                    }}
                  >
                    Open {isStaging ? 'Staging' : 'Production'}
                  </a>
                  <a
                    href={graphiqlUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      ...linkStyle,
                      background: 'var(--color-fill-secondary)',
                      color: 'var(--color-label-primary)',
                    }}
                  >
                    GraphiQL {isStaging ? '(staging)' : ''}
                  </a>
                </>
              );
            })()}
          <a
            href="https://zoobook-am.sentry.io"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '8px 16px',
              background: 'var(--color-fill-secondary)',
              color: 'var(--color-label-primary)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: 'var(--font-size-footnote)',
              textDecoration: 'none',
              minHeight: 36,
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            View Sentry
          </a>
          <button
            type="button"
            onClick={() => {
              fetch(`${DEV_SERVER}/api/deploy/reset`, { method: 'POST' }).catch(() => {});
              clearLogs();
            }}
            style={{
              padding: '8px 16px',
              background: 'var(--color-fill-secondary)',
              color: 'var(--color-label-primary)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: 'var(--font-size-footnote)',
              cursor: 'pointer',
              minHeight: 36,
            }}
          >
            Done
          </button>
        </div>

        {/* Expandable logs from the run */}
        {logs.length > 0 && (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => setShowLogs((v) => !v)}
              style={{
                width: '100%',
                padding: '8px 16px',
                background: 'none',
                border: 'none',
                borderBottom: showLogs ? '1px solid var(--color-border-opaque)' : 'none',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 'var(--font-size-footnote)',
                  color: 'var(--color-label-primary)',
                }}
              >
                Deploy Logs ({logs.length} lines)
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-caption)',
                  color: 'var(--color-tint-blue)',
                  fontWeight: 600,
                }}
              >
                {showLogs ? 'Collapse' : 'Expand'}
              </span>
            </button>
            {showLogs && (
              <div
                ref={logsRef}
                style={{
                  maxHeight: 280,
                  overflow: 'auto',
                  padding: '8px 16px',
                  background: 'var(--color-bg-tertiary)',
                  fontFamily: 'monospace',
                  fontSize: 'var(--font-size-caption)',
                  lineHeight: 1.7,
                }}
              >
                {logs.map((entry, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: log entries have no stable ID
                    key={i}
                    style={{
                      color: logLineColor(entry.level),
                      whiteSpace: 'pre-wrap' as const,
                      wordBreak: 'break-all' as const,
                    }}
                  >
                    <span style={{ color: 'var(--color-label-quaternary)', marginRight: 8 }}>
                      [{entry.target}]
                    </span>
                    {entry.line}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ---- IDLE VIEW ----
  const hasMobile = selected.has('ios') || selected.has('android');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Sonnet analysis — single guidance card, replaces warning list */}
      <div style={{ ...card, padding: '14px 16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: analysis.analysis ? 8 : 0,
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 'var(--font-size-body)',
              color: 'var(--color-label-primary)',
            }}
          >
            Analysis
          </span>
          <span
            style={{
              ...badge('var(--color-tint-purple)', 'var(--color-static-white)'),
              fontSize: 10,
            }}
          >
            Sonnet
          </span>
        </div>
        {analysis.analysis ? (
          <div
            style={{
              fontSize: 'var(--font-size-footnote)',
              color: 'var(--color-label-secondary)',
              lineHeight: 1.6,
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            {getAnalysisText(analysis.analysis)}
          </div>
        ) : (
          <span
            style={{ fontSize: 'var(--font-size-footnote)', color: 'var(--color-label-tertiary)' }}
          >
            Analysing git diff...
          </span>
        )}
      </div>

      {/* Product toggle buttons — 2x2 grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {PRODUCT_TARGETS.map((product) => {
          const isOn = selected.has(product.key);
          return (
            // biome-ignore lint/a11y/useSemanticElements: role="tablist" is intentional for custom tab UI
            <div
              role="button"
              tabIndex={0}
              key={product.key}
              onClick={() => toggleProduct(product.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleProduct(product.key);
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 2,
                padding: '14px 16px',
                border: isOn
                  ? '2px solid var(--color-tint-green)'
                  : '2px solid var(--color-border-opaque)',
                borderRadius: 12,
                background: isOn
                  ? 'color-mix(in srgb, var(--color-tint-green) 8%, var(--color-bg-secondary))'
                  : 'var(--color-bg-secondary)',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 'var(--font-size-body)',
                  color: isOn ? 'var(--color-tint-green)' : 'var(--color-label-primary)',
                }}
              >
                {product.label}
              </span>
              <span
                style={{
                  fontSize: 'var(--font-size-caption)',
                  color: 'var(--color-label-tertiary)',
                  fontFamily: 'monospace',
                }}
              >
                {product.key === 'web'
                  ? environment === 'staging'
                    ? 'develop.zoobook-web.pages.dev'
                    : 'app.zoobook.online'
                  : product.key === 'desktop'
                    ? environment === 'staging'
                      ? 'Staging Build'
                      : 'Electron Build'
                    : product.sub}
              </span>
              {product.key in LOCALLY_BUILDABLE &&
                (() => {
                  const isLocal = localTargets.has(LOCALLY_BUILDABLE[product.key]);
                  const isDesktop = product.key === 'desktop';

                  // Desktop uses 3-mode publish selector; iOS/Android keep simple local/cloud toggle
                  if (isDesktop) {
                    const modes = [
                      {
                        key: 'local-only' as const,
                        label: 'Local',
                        title: 'Build DMG only, no upload',
                      },
                      {
                        key: 'local+r2' as const,
                        label: 'Local + R2',
                        title: 'Build locally + upload to R2',
                      },
                      {
                        key: 'cloud' as const,
                        label: 'Cloud',
                        title: 'GitHub Actions CI build + upload',
                      },
                    ];
                    return (
                      // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard nav handled by parent tablist
                      <div
                        role="radiogroup"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: 'flex',
                          marginTop: 6,
                          borderRadius: 'var(--radius-full)',
                          border: '1px solid var(--color-border-opaque)',
                          overflow: 'hidden',
                          flexShrink: 0,
                        }}
                      >
                        {modes.map((mode) => {
                          const active = desktopPublishMode === mode.key;
                          return (
                            <button
                              key={mode.key}
                              type="button"
                              onClick={() => {
                                setDesktopPublishMode(mode.key);
                                // Sync local/cloud build target accordingly
                                const infraKey = LOCALLY_BUILDABLE[product.key];
                                if (mode.key === 'cloud') {
                                  setLocalTargets((prev) => {
                                    const next = new Set(prev);
                                    next.delete(infraKey);
                                    return next;
                                  });
                                } else {
                                  setLocalTargets((prev) => {
                                    const next = new Set(prev);
                                    next.add(infraKey);
                                    return next;
                                  });
                                }
                              }}
                              title={mode.title}
                              style={{
                                padding: '4px 10px',
                                border: 'none',
                                background: active
                                  ? mode.key === 'cloud'
                                    ? 'var(--color-tint-green)'
                                    : mode.key === 'local+r2'
                                      ? 'var(--color-tint-purple)'
                                      : 'var(--color-tint-blue)'
                                  : 'transparent',
                                color: active
                                  ? 'var(--color-static-white)'
                                  : 'var(--color-label-secondary)',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                              }}
                            >
                              {mode.label}
                            </button>
                          );
                        })}
                      </div>
                    );
                  }

                  return (
                    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard nav handled by parent tablist
                    <div
                      role="radiogroup"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        marginTop: 6,
                        borderRadius: 'var(--radius-full)',
                        border: '1px solid var(--color-border-opaque)',
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      {(['local', 'cloud'] as const).map((mode) => {
                        const active = mode === 'local' ? isLocal : !isLocal;
                        return (
                          <button
                            key={mode}
                            type="button"
                            onClick={() => {
                              if ((mode === 'local') !== isLocal) toggleLocalBuild(product.key);
                            }}
                            title={mode === 'local' ? 'EAS build --local' : 'EAS cloud build'}
                            style={{
                              padding: '4px 10px',
                              border: 'none',
                              background: active
                                ? mode === 'cloud'
                                  ? 'var(--color-tint-green)'
                                  : 'var(--color-tint-blue)'
                                : 'transparent',
                              color: active
                                ? 'var(--color-static-white)'
                                : 'var(--color-label-secondary)',
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'background 0.15s',
                              textTransform: 'capitalize' as const,
                            }}
                          >
                            {mode}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
            </div>
          );
        })}
      </div>

      {/* Preset chips */}
      <div style={{ display: 'flex', gap: 8 }}>
        {(
          [
            ['All Platforms', ['web', 'desktop', 'ios', 'android']],
            ['Web + Desktop', ['web', 'desktop']],
          ] as [string, ProductKey[]][]
        ).map(([label, keys]) => (
          <button
            type="button"
            key={label}
            onClick={() => setSelected(new Set(keys))}
            style={{
              padding: '6px 14px',
              border: '1px solid var(--color-border-opaque)',
              borderRadius: 'var(--radius-full)',
              background: 'transparent',
              fontSize: 'var(--font-size-footnote)',
              color: 'var(--color-label-secondary)',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Environment pill + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div
          style={{
            display: 'flex',
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--color-border-opaque)',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {(['staging', 'production'] as const).map((env) => (
            <button
              type="button"
              key={env}
              onClick={() => setEnvironment(env)}
              style={{
                padding: '7px 14px',
                border: 'none',
                background:
                  environment === env
                    ? env === 'production'
                      ? 'var(--color-tint-green)'
                      : 'var(--color-tint-blue)'
                    : 'transparent',
                color:
                  environment === env
                    ? 'var(--color-static-white)'
                    : 'var(--color-label-secondary)',
                fontSize: 'var(--font-size-footnote)',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.15s',
                textTransform: 'capitalize' as const,
              }}
            >
              {env}
            </button>
          ))}
        </div>
        {/* Show R2 channel indicator for local+r2 publish mode */}
        {selected.has('desktop') && desktopPublishMode === 'local+r2' && (
          <span
            style={{
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-tint-purple)',
              fontWeight: 600,
            }}
          >
            R2 channel: {environment === 'staging' ? 'staging' : 'canary'}
          </span>
        )}

        <span style={{ flex: 1 }} />

        {selected.has('desktop') && localTargets.has(LOCALLY_BUILDABLE.desktop) && (
          <button
            type="button"
            onClick={handleRebuildDesktop}
            disabled={deploying}
            title="Rebuild Electron main + pack only (skip web build)"
            style={{
              padding: '10px 16px',
              border: '1px solid var(--color-tint-orange)',
              borderRadius: 'var(--radius-md)',
              background: 'transparent',
              color: deploying ? 'var(--color-label-tertiary)' : 'var(--color-tint-orange)',
              fontSize: 'var(--font-size-footnote)',
              fontWeight: 600,
              cursor: deploying ? 'not-allowed' : 'pointer',
              minHeight: 40,
              whiteSpace: 'nowrap' as const,
            }}
          >
            Rebuild Desktop
          </button>
        )}

        {hasMobile && (
          <button
            type="button"
            onClick={handleOta}
            disabled={deploying}
            title="JS-only patch via EAS Update — instant, no App Store review"
            style={{
              padding: '10px 16px',
              border: '1px solid var(--color-tint-blue)',
              borderRadius: 'var(--radius-md)',
              background: 'transparent',
              color: deploying ? 'var(--color-label-tertiary)' : 'var(--color-tint-blue)',
              fontSize: 'var(--font-size-footnote)',
              fontWeight: 600,
              cursor: deploying ? 'not-allowed' : 'pointer',
              minHeight: 40,
              whiteSpace: 'nowrap' as const,
            }}
          >
            OTA Update
          </button>
        )}

        <button
          type="button"
          onClick={handleDeploy}
          disabled={selected.size === 0 || deploying}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            background:
              selected.size === 0 || deploying
                ? 'var(--color-fill-secondary)'
                : 'var(--color-tint-green)',
            color:
              selected.size === 0 || deploying
                ? 'var(--color-label-tertiary)'
                : 'var(--color-static-white)',
            fontWeight: 700,
            fontSize: 'var(--font-size-body)',
            cursor: selected.size === 0 || deploying ? 'not-allowed' : 'pointer',
            minHeight: 40,
            whiteSpace: 'nowrap' as const,
          }}
        >
          {deploying ? 'Starting...' : selected.size > 0 ? `Deploy (${selected.size})` : 'Deploy'}
        </button>
      </div>

      {/* Environment links */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          fontSize: 'var(--font-size-caption)',
          color: 'var(--color-label-tertiary)',
        }}
      >
        <span>{environment === 'staging' ? 'Staging' : 'Production'}:</span>
        <a
          href={
            environment === 'staging'
              ? 'https://develop.zoobook-web.pages.dev'
              : 'https://app.zoobook.online'
          }
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--color-tint-blue)' }}
        >
          {environment === 'staging' ? 'develop.zoobook-web.pages.dev' : 'app.zoobook.online'}
        </a>
        <span style={{ color: 'var(--color-label-quaternary)' }}>|</span>
        <a
          href={
            environment === 'staging'
              ? 'https://zoobook-staging.fly.dev/graphiql'
              : 'https://zoobook.fly.dev/graphiql'
          }
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--color-tint-blue)' }}
        >
          GraphiQL
        </a>
        <span style={{ color: 'var(--color-label-quaternary)' }}>|</span>
        <a
          href={
            environment === 'staging'
              ? 'https://supabase.com/dashboard/project/wuanjbemryupwovbwnoo'
              : 'https://supabase.com/dashboard/project/hoquwjhxocpejqppszwy'
          }
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--color-tint-blue)' }}
        >
          Supabase
        </a>
        {totalEstimate != null && !deploying && selected.size > 0 && (
          <>
            <span style={{ color: 'var(--color-label-quaternary)' }}>|</span>
            <span style={{ fontStyle: 'italic' }}>~{formatDuration(totalEstimate)}</span>
          </>
        )}
      </div>

      {/* Recent deploys */}
      {history.length > 0 && (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div
            style={{
              padding: '8px 16px',
              borderBottom: '1px solid var(--color-border-opaque)',
              fontWeight: 600,
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-label-tertiary)',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.5px',
            }}
          >
            Recent
          </div>
          {history.slice(0, 4).map((entry) => (
            <div
              key={entry.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 16px',
                borderBottom: '1px solid var(--color-border-transparent)',
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background:
                    entry.state === 'done'
                      ? 'var(--color-tint-green)'
                      : entry.state === 'failed'
                        ? 'var(--color-tint-red)'
                        : 'var(--color-tint-orange)',
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 'var(--font-size-footnote)',
                  color: 'var(--color-label-primary)',
                  flex: 1,
                }}
              >
                {entry.targets.join(', ')}
              </span>
              <span
                style={{
                  ...badge(
                    entry.environment === 'production'
                      ? 'var(--color-tint-light-green)'
                      : 'var(--color-tint-light-blue)',
                    entry.environment === 'production'
                      ? 'var(--color-tint-green)'
                      : 'var(--color-tint-blue)',
                  ),
                  fontSize: 10,
                }}
              >
                {entry.environment}
              </span>
              {entry.duration != null && (
                <span
                  style={{
                    fontSize: 'var(--font-size-caption)',
                    color: 'var(--color-label-tertiary)',
                    fontFamily: 'monospace',
                  }}
                >
                  {formatDuration(entry.duration)}
                </span>
              )}
              <span
                style={{
                  fontSize: 'var(--font-size-caption)',
                  color: 'var(--color-label-quaternary)',
                }}
              >
                {new Date(entry.startedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function useActiveSection(sectionIds: string[]): string {
  const [active, setActive] = useState(sectionIds[0] ?? '');

  useEffect(() => {
    const scrollRoot = document.getElementById('dev-dashboard-scroll') ?? undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost visible section
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActive(visible[0].target.id);
        }
      },
      { root: scrollRoot, rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );

    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sectionIds]);

  return active;
}

export function DevDashboardPage() {
  const buildDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const allSectionIds = useMemo(
    () => SIDEBAR_CATEGORIES.flatMap((c) => c.items.map((i) => i.id)),
    [],
  );
  const activeSection = useActiveSection(allSectionIds);

  const [isWide, setIsWide] = useState(() => window.innerWidth >= 900);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 900px)');
    const handler = (e: MediaQueryListEvent) => setIsWide(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <div
      id="dev-dashboard-scroll"
      style={{ height: '100vh', overflowY: 'auto', overflowX: 'hidden' }}
    >
      <div
        style={{
          maxWidth: isWide ? 1340 : 1100,
          margin: '0 auto',
          padding: '32px 24px 64px',
          display: isWide ? 'grid' : 'block',
          gridTemplateColumns: isWide ? '180px 1fr' : undefined,
          gap: isWide ? 40 : undefined,
        }}
      >
        {/* Sidebar */}
        {isWide && (
          <div style={{ minWidth: 0 }}>
            <SidebarNav activeSection={activeSection} />
          </div>
        )}

        {/* Main content */}
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          {/* Header */}
          <header style={{ marginBottom: 32 }}>
            <h1
              style={{
                fontSize: 'var(--font-size-title1)',
                lineHeight: 'var(--line-height-title1)',
                letterSpacing: 'var(--letter-spacing-title1)',
                fontWeight: 700,
                color: 'var(--color-label-primary)',
                marginBottom: 4,
              }}
            >
              Zoobook 3 — Developer Dashboard
            </h1>
            <p
              style={{
                fontSize: 'var(--font-size-title4)',
                color: 'var(--color-label-secondary)',
                marginBottom: 4,
              }}
            >
              Infrastructure Complete — Core + Cloud + Email System + Email-First Onboarding + Dev
              Tools
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-footnote)',
                color: 'var(--color-label-tertiary)',
              }}
            >
              Build: {buildDate} | Web bundle: {BUILD_METRICS.webBundle} gzip (
              {BUILD_METRICS.webBundleRaw} raw)
            </p>
          </header>

          <DevServerStatusBar />

          {/* 0. INFRASTRUCTURE MONITORING */}
          <Section title="Infrastructure Monitoring" id="section-monitoring" defaultOpen={true}>
            <MonitoringSection />
          </Section>

          {/* 1. OPERATIONS (actionable) */}
          <Section title="Deploy Pipeline" id="section-deploy" defaultOpen={true}>
            <DeploySection />
          </Section>

          <Section title="Release Management" id="section-releases" defaultOpen={true}>
            <ReleaseManagementSection />
          </Section>

          <Section title="Staging Import" id="section-staging-import" defaultOpen={true}>
            <StagingImportPanel />
          </Section>

          <Section title="Feature Flags" id="section-feature-flags" defaultOpen={true}>
            <FeatureFlagsSection />
          </Section>

          <Section title="Servers & Tools" id="section-servers-tools" defaultOpen={true}>
            {/* intentionally empty — Production/Staging cards are in the grid below */}
            <DevTools />
          </Section>

          <Section title="Simulators & Emulators" id="section-simulators" defaultOpen={true}>
            <SimulatorLauncher />
          </Section>

          {/* 3. MONITORING (observability) */}

          <Section title="Sentry Issues" id="section-sentry" defaultOpen={true}>
            <SentryWidget />
          </Section>

          <Section title="Git" id="section-git" defaultOpen={true}>
            <GitPanel />
          </Section>

          <Section title="Dependencies" id="section-deps" defaultOpen={true}>
            <DependencyPanel />
          </Section>

          <Section title="Ops Health" id="section-ops" defaultOpen={true}>
            <OpsReportPanel />
          </Section>

          {/* 4. PROJECT INFO (reference) */}
          <Section title="Key Metrics" id="section-key-metrics" defaultOpen={false}>
            <KeyMetrics />
          </Section>

          <Section title="Infrastructure Status" id="section-infrastructure" defaultOpen={false}>
            <InfraStatus />
          </Section>

          <Section title="Database Overview" id="section-database" defaultOpen={false}>
            <DatabaseOverview />
          </Section>

          <Section title="Module Categories (36 modules)" id="section-modules" defaultOpen={false}>
            <ModuleCategories />
          </Section>

          <Section title="Desktop App (Electron)" id="section-desktop" defaultOpen={false}>
            <DesktopApp />
          </Section>

          <Section title="Tech Stack" id="section-tech-stack" defaultOpen={false}>
            <TechStack />
          </Section>

          <Section title="Pending Setup" id="section-pending" defaultOpen={false}>
            <PendingSetupSection />
          </Section>

          {/* 5. DEV REFERENCE */}
          <Section title="Quick Links" id="section-quick-links" defaultOpen={true}>
            <QuickLinks />
          </Section>

          <Section title="GraphQL Quick Test" id="section-graphql-test" defaultOpen={true}>
            <GraphQLTest />
          </Section>

          <Section title="Sprint Progress" id="section-sprints" defaultOpen={true}>
            <SprintProgress />
          </Section>

          <Section title="Dev Commands" id="section-commands" defaultOpen={true}>
            <DevCommands />
          </Section>

          {/* Footer */}
          <footer
            style={{
              marginTop: 48,
              paddingTop: 16,
              borderTop: '1px solid var(--color-border-opaque)',
              fontSize: 'var(--font-size-caption)',
              color: 'var(--color-label-tertiary)',
              textAlign: 'center',
            }}
          >
            Zoobook 3 — Internal Developer Portal — Not for production use
          </footer>
        </div>
      </div>
    </div>
  );
}
